import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useVoiceEngine } from '../hooks/useVoiceEngine';
import { useNavigate, useLocation } from 'react-router-dom';
import { NeuralOrb } from '../components/NeuralOrb';
import axios from 'axios';
import { ThinkingIndicator } from '../components/ThinkingIndicator';
import { AthenaPresentationEngine } from '../components/AthenaPresentationEngine';
import { AdaptiveResponseRenderer, resolveAdaptiveResponsePresentation } from '../components/response-design/AdaptiveResponseRenderer';
import { ResponseActions } from '../components/ResponseActions';
import { ImageGenerationCard } from '../components/ImageGenerationCard';
import { SmartComponentRenderer } from '../components/smart/SmartComponentRenderer';
import { detectSmartComponent } from '../utils/athenaSmartComponents';
import { AthenaMiniAvatar } from '../components/AthenaMiniAvatar';
import WorkspacePresentationRenderer from '../components/presentation/WorkspacePresentationRenderer';
import PresentationRenderer from '../components/presentation/PresentationRenderer';
import { hasPremiumRenderer } from '../components/presentation/registry';
import { detectThinkingType, getThinkingMessages } from '../thinkingStates';
import { AthenaLogo } from '../components/AthenaLogo';
import { AthenaWorkspaceBg } from '../components/AthenaWorkspaceBg';
import { PanelPersonalizar, applyPreferences } from '../components/PanelPersonalizar';
import { parseGifFromResponse, pickGifPath, loadSocialPrefs, shouldRenderGif } from '../utils/socialBehaviorManager';
import { extractNotificationCommand, startContextualScheduler, getCurrentGreeting, useNotifLog, markNotifClicked, markAllNotifRead } from '../utils/athenaNotifications';
import { FocusMissionCard, FocusWinToast, FocusResumePrompt } from '../components/FocusMode';
import { detectFocusMission, advanceMission, parseFocusUpdate, saveMission, loadMission } from '../utils/focusMission';
import { FocusTotalLanding } from '../components/FocusTotalLanding';
import { getActiveProfile, detectProfileSwitch, setActiveProfile, updateProfileLastAccess } from '../utils/athenaCognitiveCore';
import { VisualAidCard } from '../components/VisualAidCard';
import { buildWorkspaceProfile } from '../utils/athenaWorkspaceProfile';
import { trackMessageSignals, trackImageOpened, checkDriftSuggestion, applyDriftUpdate } from '../utils/athenaProfileLearning';
import { CognitiveProfileButton, CognitiveProfilePanel, DriftSuggestionToast } from '../components/CognitiveProfilePanel';
import { buildResponseProfile } from '../utils/athenaResponseProfile';
import { buildCognitivePrompt } from '../utils/athenaPromptContext';
import { trackPreferenceSignal, checkInsightSuggestion, isInsightDismissed, dismissInsight, setPreferenceOverride, getPreferenceOverrides } from '../utils/athenaPreferenceLearning';
import { AdaptiveInsightToast } from '../components/AdaptiveInsightToast';
import { API_BASE as WORKER_API_BASE, IS_NATIVE_APP } from '../config/api';
import { defaultReminderMinutesFor } from '../utils/calendarChat';
import { reminderMinutesForTask } from '../utils/taskChat';
import { playAthenaAudio, requestAthenaTts } from '../voice/athenaTts';
import {
  captureAthenaPhoto,
  discardAthenaContextItem,
  getNativeCalendars,
  pickAthenaDocuments,
  pickAthenaImage,
  requestNativeCalendarAccess,
  shareAthenaText,
  uploadAthenaContextItem,
} from '../capabilities/AthenaCapabilities';
import { usePendingDeviceContext } from '../capabilities/PendingDeviceContext';
import { useAthenaDeviceContext } from '../context/AthenaDeviceContextProvider';
import { ATHENA_ACTION_TYPES, executeAthenaAction, prepareAthenaAction } from '../capabilities/AthenaActionRouter';
import { detectAthenaOperationalIntent, executeAthenaOperationalAction } from '../capabilities/AthenaActionOrchestrator';
import { signalAthenaAuthExpired } from '../context/AuthContext';
import { isAthenaSessionFailure, normalizeAthenaApiFailure } from '../utils/athenaApiFailure';
import { registerAthenaQuickOverlayMessageHandler } from '../platform/AthenaQuickOverlayRuntime';
import { LifeOSProvider, TodayPanel, InboxPanel, VaultPanel, RoutinePanel, useOptionalLifeOS } from '../life-os';

const API_BASE = WORKER_API_BASE;
const ACTIVE_SESSION_STORAGE_KEY = 'athena_active_session_id';

function stripEmojis(t) {
  return t.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2702}-\u{27B0}\u{24C2}-\u{1F251}\u{1f926}-\u{1f937}\u{10000}-\u{10ffff}\u{2600}-\u{2B55}\u{200d}\u{23cf}\u{23e9}\u{231a}\u{fe0f}\u{3030}]+/gu, '').trim();
}

function stripMarkdown(text) {
  return text
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/\*(.+?)\*/gs, '$1')
    .replace(/`{3}[\s\S]*?`{3}/g, '')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/^>\s+/gm, '')
    .replace(/\|[^|\n]*\|/g, '')
    .replace(/^[-|:\s]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ─── Tokens ───────────────────────────────────────────────────────────────────
const C = {
  bg:'#030712', base:'#07111F', card:'#0c1220', bar:'#050d1a',
  border:'#1a2240', accent:'#3B82F6', accentD:'#1e3a5f', accentB:'#0a1628',
  text:'#e8eaf2', muted:'#6b7590', soft:'#4a5070', dim:'#2e3560', dimmer:'#3a4070',
};

// Athena Blue Palette — sidebar
const BLUE = {
  bg:       '#08111F',    // deepest background
  bgCard:   '#0D1B2F',    // card backgrounds
  bgHover:  '#0F2035',    // hover state
  accent:   '#1D7CFF',    // primary blue (buttons, active)
  accentSoft: 'rgba(29,124,255,0.12)',  // soft accent bg
  accentBdr:  'rgba(29,124,255,0.25)',  // accent borders
  neon:     '#3AA8FF',    // secondary blue / neon
  glow:     '#5CC9FF',    // glow highlights
  glowSoft: 'rgba(92,201,255,0.08)',   // very soft glow
  text:     '#E8F0FF',    // primary text
  textSec:  'rgba(180,210,255,0.75)',  // secondary text
  textMut:  'rgba(120,160,220,0.5)',   // muted / labels
  border:   'rgba(29,124,255,0.12)',   // card borders
  borderSub: 'rgba(255,255,255,0.05)', // subtle structural borders
  divider:  'rgba(255,255,255,0.04)',  // section dividers
  scrollbar: 'rgba(29,124,255,0.3)',   // scrollbar thumb
};

// SB — Sidebar redesign color system (used exclusively in the sidebar)
const SB = {
  bg:          '#07101D',   // sidebar background (deepest)
  zone:        '#0A1525',   // zone backgrounds (slight lift)
  card:        '#0D1B2F',   // widget cards
  cardHover:   '#112040',   // widget hover
  glass:       'rgba(255,255,255,0.025)', // glassmorphism

  accent:      '#1B6FE8',   // primary blue (active states)
  accentSoft:  'rgba(27,111,232,0.1)',
  accentBdr:   'rgba(27,111,232,0.22)',
  accentGlow:  'rgba(27,111,232,0.18)',

  neon:        '#38A8FF',   // secondary highlight
  neonSoft:    'rgba(56,168,255,0.1)',

  text:        '#DCE8F8',   // primary text
  textSec:     'rgba(180,210,248,0.7)',
  textMut:     'rgba(120,160,220,0.45)',

  border:      'rgba(255,255,255,0.055)',  // structural borders
  borderAccent:'rgba(27,111,232,0.18)',

  divider:     'rgba(255,255,255,0.04)',   // zone separators

  activeRail:  '#1B6FE8',   // left border on active item
  activeBg:    'rgba(27,111,232,0.1)',     // active item bg
  hoverBg:     'rgba(255,255,255,0.04)',   // hover bg

  scrollbar:   'rgba(27,111,232,0.25)',

  onlineGreen: '#22c55e',
  onlineGlow:  'rgba(34,197,94,0.5)',
};

// User-scoped localStorage key for pinned sessions — prevents cross-user pin contamination
const _PINNED_KEY = (() => {
  try {
    const tok = localStorage.getItem('athena_token');
    if (!tok) return 'athena_pinned';
    const p = JSON.parse(atob(tok.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
    return p.sub ? `athena_pinned_${p.sub}` : 'athena_pinned';
  } catch { return 'athena_pinned'; }
})();

// fmtTime — sessionList entries already carry a pre-formatted relative string
// ("agora", "há 5 min", "ontem", "12/05"); pass those through untouched.
// Raw timestamps (ISO/number/Date) are formatted here.
function fmtTime(ts) {
  if (!ts) return '';
  const isRaw = typeof ts === 'number' || ts instanceof Date ||
    (typeof ts === 'string' && /^\d{4}-\d{2}-\d{2}/.test(ts));
  if (!isRaw) return String(ts);
  const diff = Date.now() - new Date(ts).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 2) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  if (h < 48) return 'ontem';
  return new Date(ts).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit' });
}

const KF = `
@keyframes blink  {0%,100%{opacity:1}50%{opacity:.2}}
@keyframes wave   {0%,100%{height:4px}50%{height:18px}}
@keyframes fadeIn {from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
@keyframes slideR {from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:none}}
@keyframes toast  {0%{opacity:0;transform:translateY(8px)}10%{opacity:1;transform:none}80%{opacity:1}100%{opacity:0}}
.no-scrollbar::-webkit-scrollbar{display:none}
.athena-sidebar-scroll::-webkit-scrollbar{width:3px}
.athena-sidebar-scroll::-webkit-scrollbar-track{background:transparent}
.athena-sidebar-scroll::-webkit-scrollbar-thumb{background:rgba(29,124,255,0.3);border-radius:3px}
.athena-sidebar-scroll::-webkit-scrollbar-thumb:hover{background:rgba(29,124,255,0.5)}
.side-pin{opacity:.4;transition:opacity .15s ease}
button:hover>.side-pin{opacity:1}
.recent-row .pin-btn{opacity:0;transition:opacity .15s ease}
.recent-row:hover .pin-btn{opacity:1}
.recent-row .pin-btn.is-pinned{opacity:1}
.memory-vortex-stage{height:100%;min-height:420px;display:flex;align-items:center;justify-content:center;background:#030712;overflow:hidden}
.memory-vortex-frame{width:min(680px,100%);height:min(760px,100%);display:flex;align-items:center;justify-content:center;overflow:hidden}
.memory-vortex-video{display:block;width:100%;height:100%;object-fit:contain}
/* Workspace responsivo: preserva área útil e alvos de toque no celular. */
@media (max-width:767px){
  .workspace-top-actions{gap:2px!important}
  .workspace-top-divider{display:none!important}
  .workspace-panel{padding:16px 14px!important}
  .workspace-calendar-layout{grid-template-columns:minmax(0,1fr)!important;gap:12px!important}
  .workspace-calendar-day{min-height:58px!important;padding:6px 4px!important}
  .workspace-calendar-event{display:none!important}
  .workspace-calendar-weekday{font-size:9px!important;letter-spacing:0!important}
  .workspace-project-header{align-items:flex-start!important}
  .workspace-project-header h1{font-size:19px!important}
  .workspace-project-action{padding:8px 9px!important;font-size:11px!important;white-space:nowrap}
  .workspace-decision-row{flex-direction:column!important}
  .workspace-decision-row button{width:100%!important}
  .workspace-task-controls>*{min-width:100%!important;width:100%!important}
  .workspace-task-controls button{min-height:40px!important}
  .workspace-tasks-layout{grid-template-columns:minmax(0,1fr)!important}
  .workspace-composer{padding:11px 12px!important;border-radius:18px!important}
  .workspace-composer-shell{padding-left:10px!important;padding-right:10px!important;padding-bottom:max(10px, env(safe-area-inset-bottom))!important}
  .workspace-message-list{padding-left:12px!important;padding-right:12px!important;padding-top:16px!important;padding-bottom:calc(var(--keyboard-h,0px) + 178px)!important}
  .memory-vortex-frame{width:min(100%,420px);height:min(640px,100%)}
}
`;

const UNIVERSOS = [
  {
    id:'anexos',
    label:'Anexos',
    desc:'Arquivos e documentos anexados',
    color:'#00c8a0',
    icon:'M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48',
  },
  {
    id:'conversas-fixadas',
    label:'Conversas anexadas',
    desc:'Conversas fixadas e importantes',
    color:'#8b8fa8',
    icon:'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71',
  },
];

const COG_MODES = [
  {id:'athena2',    label:'Athena 2.0', desc:'Inteligência adaptativa — responde no nível certo para cada mensagem.'},
  {id:'foco_total', label:'Foco Total',  desc:'Modo focado para trabalho, estudo e programação.'},
];

const TOOLS_DATA = [
  {id:'web',   name:'Pesquisa Web',    icon:'🔍', status:'Ativo',   desc:'Busca em tempo real'},
  {id:'ocr',   name:'OCR',             icon:'👁️', status:'Ativo',   desc:'Extração de texto de imagens'},
  {id:'pdf',   name:'PDF',             icon:'📄', status:'Ativo',   desc:'Leitura e geração de PDFs'},
  {id:'sheet', name:'Planilhas',       icon:'📊', status:'Standby', desc:'Análise de dados tabulares'},
  {id:'code',  name:'Código',          icon:'💻', status:'Ativo',   desc:'Execução e análise de código'},
  {id:'img',   name:'Imagens',         icon:'🖼️', status:'Ativo',   desc:'Geração e análise visual'},
  {id:'audio', name:'Áudio',           icon:'🎵', status:'Standby', desc:'Transcrição e análise'},
  {id:'video', name:'Vídeo',           icon:'🎬', status:'Standby', desc:'Extração e edição'},
];

// ─── SVG helper ───────────────────────────────────────────────────────────────
function Ico({d, size=15}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={d}/>
    </svg>
  );
}
const D = {
  mic:    'M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8',
  send:   'M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z',
  plus:   'M12 5v14M5 12h14',
  x:      'M18 6L6 18M6 6l12 12',
  copy:   'M8 4H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V8l-4-4H8zm0 0v4h8M8 12h8M8 16h4',
  brain:  'M9.5 2A2.5 2.5 0 007 4.5v.1a4 4 0 00-4 4 3.5 3.5 0 002 3.4V17.5a2.5 2.5 0 005 0V12a3.5 3.5 0 002-3.4 4 4 0 00-4-4v-.1A2.5 2.5 0 009.5 2zm5 0A2.5 2.5 0 0117 4.5v.1a4 4 0 014 4A3.5 3.5 0 0119 12v5.5a2.5 2.5 0 01-5 0V12a3.5 3.5 0 01-2-3.4 4 4 0 014-4v-.1A2.5 2.5 0 0014.5 2z',
  pin:    'M15 3l2 2-6 6 1 5-5-3-4 4-1-1 4-4-3-5 5 1 6-6zm-6 6L3 15',
  share:  'M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13',
  trash:  'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6',
  edit:   'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  search: 'M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z',
  check:  'M20 6L9 17l-5-5',
  star:   'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  folder: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z',
  upload: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12',
  wave:   'M22 12h-4l-3 9L9 3l-3 9H2',
  vol:    'M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07',
  bell:   'M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0',
  gear:   'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z',
  logOut: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9',
  user:   'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',
  help:   'M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3M12 17h.01',
  cam:    'M15 10l4.553-2.277A1 1 0 0121 8.677V15.32a1 1 0 01-1.447.894L15 14M3 8h9a3 3 0 013 3v4a3 3 0 01-3 3H3a3 3 0 01-3-3v-4a3 3 0 013-3z',
  book:   'M12 6.5C10.5 5 8 4 4 4v13c4 0 6.5 1 8 2.5M12 6.5C13.5 5 16 4 20 4v13c-4 0-6.5 1-8 2.5M12 6.5V19',
  chevronR:'M9 6l6 6-6 6',
};

// ─── ScaledOrb ────────────────────────────────────────────────────────────────
function ScaledOrb({size, ...props}) {
  const N=320, h=N/2;
  return (
    <div style={{width:size,height:size,overflow:'hidden',position:'relative',flexShrink:0}}>
      <div style={{position:'absolute',width:N,height:N,top:'50%',left:'50%',marginTop:-h,marginLeft:-h,
        transform:`scale(${size/N})`,transformOrigin:'center',pointerEvents:'none'}}>
        <NeuralOrb {...props}/>
      </div>
    </div>
  );
}

// ─── Sidebar section helpers ──────────────────────────────────────────────────
function SectionHeader({label, first=false}) {
  return (
    <div style={{
      fontSize:9.5, fontWeight:700,
      color:'rgba(120,160,220,0.38)',
      letterSpacing:'0.14em', textTransform:'lowercase',
      padding:'0 6px', marginTop:first?4:18, marginBottom:6,
      userSelect:'none',
    }}>{label}</div>
  );
}
function SectionDivider() {
  return (
    <div style={{height:1, background:'rgba(255,255,255,0.03)', margin:'6px 8px'}}/>
  );
}

// ─── Toast notification ───────────────────────────────────────────────────────
function Toast({msg}) {
  return (
    <div style={{
      position:'fixed',bottom:80,left:'50%',transform:'translateX(-50%)',
      background:C.card,border:`1px solid ${C.accentD}`,borderRadius:8,
      padding:'8px 16px',fontSize:12,color:C.accent,
      display:'flex',alignItems:'center',gap:6,
      boxShadow:'0 8px 24px rgba(0,0,0,.6)',zIndex:999,
      animation:'toast 2.5s ease forwards',pointerEvents:'none',
    }}>
      <span style={{color:C.accent}}>✓</span> {msg}
    </div>
  );
}

// ─── Modal base ───────────────────────────────────────────────────────────────
function Modal({onClose, children, width=440}) {
  return (
    <div style={{position:'fixed',inset:0,zIndex:600,background:'rgba(0,0,0,.85)',
      backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',
      padding:'0 16px',boxSizing:'border-box'}}
      onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{
        width:'100%',maxWidth:width,background:C.card,border:`1px solid ${C.border}`,borderRadius:16,
        boxShadow:'0 24px 64px rgba(0,0,0,.9)',animation:'fadeIn .2s ease',overflow:'hidden',
      }}>
        {children}
      </div>
    </div>
  );
}

// ─── Editar Prompt — regenera o mesmo card com um prompt novo ─────────────────
function EditPromptModal({initialPrompt, onClose, onRegenerate}) {
  const [text, setText] = useState(initialPrompt || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  async function handleRegenerate() {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(false);
    try {
      await onRegenerate(trimmed);
      onClose();
    } catch {
      setError(true);
      setBusy(false);
    }
  }

  return (
    <Modal onClose={busy ? ()=>{} : onClose} width={480}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>Editar Prompt</span>
        <button onClick={onClose} disabled={busy} style={{background:'none',border:'none',cursor:busy?'default':'pointer',color:C.muted,fontSize:18,opacity:busy?0.4:1}}>×</button>
      </div>
      <div style={{padding:'16px 20px'}}>
        <textarea
          autoFocus
          value={text}
          onChange={e=>setText(e.target.value)}
          disabled={busy}
          rows={5}
          style={{
            width:'100%',resize:'vertical',boxSizing:'border-box',
            background:C.base,border:`1px solid ${C.border}`,borderRadius:10,
            padding:'10px 12px',color:C.text,fontSize:13,lineHeight:1.5,
            fontFamily:'inherit',outline:'none',
          }}
        />
        {error && (
          <div style={{marginTop:8,fontSize:11.5,color:'rgba(239,68,68,0.85)'}}>
            Não consegui regenerar o card. Tenta de novo.
          </div>
        )}
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:16}}>
          <button onClick={onClose} disabled={busy} style={{
            padding:'8px 16px',borderRadius:9,fontSize:13,cursor:busy?'default':'pointer',
            background:'transparent',border:`1px solid ${C.border}`,color:C.muted,fontFamily:'inherit',
            opacity:busy?0.5:1,
          }}>Cancelar</button>
          <button onClick={handleRegenerate} disabled={busy || !text.trim()} style={{
            padding:'8px 16px',borderRadius:9,fontSize:13,fontWeight:600,cursor:(busy||!text.trim())?'default':'pointer',
            background:C.accent,border:'none',color:'#fff',fontFamily:'inherit',
            opacity:(busy||!text.trim())?0.6:1,
          }}>{busy ? 'Regenerando…' : 'Regenerar Card'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── New Item Modal ────────────────────────────────────────────────────────────
function NewItemModal({onClose, onNewConversation, onNewProject, token}) {
  const [view,    setView]    = useState('menu');
  const [projName,setProjName]= useState('');
  const [projDesc,setProjDesc]= useState('');
  const [projGoal,setProjGoal]= useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const createConversation = async () => {
    setLoading(true);
    try {
      const {data} = await axios.post(`${API_BASE}/memory/sessions/new`, {},
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      onNewConversation(data.session_id);
    } catch { onNewConversation(null); }
    onClose();
  };

  const createProject = async () => {
    if(!projName.trim()) return;
    setLoading(true); setError('');
    try {
      const {data} = await axios.post(`${API_BASE}/projects`,
        {name:projName, description:projDesc, goal:projGoal},
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      if (!data?.project?.project_id && !data?.project?.id) throw new Error('PROJECT_NOT_CONFIRMED');
      onNewProject(data.project);
      onClose();
    } catch { setError('Não foi possível criar o projeto. Tente novamente.'); }
    setLoading(false);
  };

  return (
    <Modal onClose={onClose} width={400}>
      <div style={{padding:'20px 22px'}}>
        {view==='menu' && (
          <>
            <div style={{fontSize:15,fontWeight:600,color:C.text,marginBottom:16}}>Criar novo</div>
            {[
              {id:'conv',   icon:'💬', label:'Nova Conversa',  desc:'Chat limpo com contexto e memória ativos'},
              {id:'proj',   icon:'📁', label:'Novo Projeto',   desc:'Pasta com memória, arquivos e histórico separados'},
              {id:'univ',   icon:'🌐', label:'Novo Universo',  desc:'Ambientes isolados — ainda não disponível nesta versão'},
            ].map(item=>(
              <button key={item.id} onClick={()=>item.id==='conv'?createConversation():setView(item.id)}
                style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'12px',
                  borderRadius:10,cursor:'pointer',marginBottom:8,
                  background:C.base,border:`1px solid ${C.border}`,textAlign:'left',
                  transition:'border-color .15s',}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=C.accentD}
                onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}
              >
                <span style={{fontSize:22,flexShrink:0}}>{item.icon}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:500,color:C.text}}>{item.label}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>{item.desc}</div>
                </div>
              </button>
            ))}
          </>
        )}
        {view==='proj' && (
          <>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
              <button onClick={()=>setView('menu')} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18,lineHeight:1}}>←</button>
              <span style={{fontSize:15,fontWeight:600,color:C.text}}>Novo Projeto</span>
            </div>
            {[
              {label:'Nome', val:projName, set:setProjName, ph:'Ex: Campanha FERMAQ Q3'},
              {label:'Descrição', val:projDesc, set:setProjDesc, ph:'O que este projeto envolve?'},
              {label:'Objetivo', val:projGoal, set:setProjGoal, ph:'Qual é o resultado esperado?'},
            ].map(f=>(
              <div key={f.label} style={{marginBottom:12}}>
                <div style={{fontSize:11,color:C.muted,marginBottom:5}}>{f.label}</div>
                <input value={f.val} onChange={e=>f.set(e.target.value)} placeholder={f.ph}
                  style={{width:'100%',padding:'8px 12px',background:C.base,border:`1px solid ${C.border}`,
                    borderRadius:8,color:C.text,fontSize:13,outline:'none',boxSizing:'border-box'}}
                  onFocus={e=>e.target.style.borderColor=`${C.accent}60`}
                  onBlur={e=>e.target.style.borderColor=C.border}/>
              </div>
            ))}
            <button onClick={createProject} disabled={!projName.trim()||loading}
              style={{width:'100%',padding:'10px',marginTop:4,
                background:projName.trim()?C.accentB:C.base,
                border:`1px solid ${projName.trim()?C.accentD:C.border}`,
                borderRadius:8,color:projName.trim()?C.accent:C.muted,
                fontSize:13,fontWeight:500,cursor:projName.trim()?'pointer':'default'}}>
              {loading?'Criando..':'Criar Projeto'}
            </button>
            {error&&<div style={{fontSize:11,color:'#f87171',marginTop:9,textAlign:'center'}}>{error}</div>}
          </>
        )}
        {view==='univ' && (
          <>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
              <button onClick={()=>setView('menu')} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18,lineHeight:1}}>←</button>
              <span style={{fontSize:15,fontWeight:600,color:C.text}}>Novo Universo</span>
            </div>
            <p style={{fontSize:12,color:C.muted,lineHeight:1.6,marginBottom:16}}>
              Universos isolados ainda não estão disponíveis nesta versão. Nenhuma memória, skill ou projeto novo será criado por esta tela.
            </p>
            <button onClick={()=>setView('menu')} style={{width:'100%',padding:'10px',
              background:C.base,border:`1px solid ${C.border}`,borderRadius:8,
              color:C.text,fontSize:13,fontWeight:500,cursor:'pointer'}}>Voltar</button>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Attach Modal ─────────────────────────────────────────────────────────────
const ATTACH_SHORTCUTS = [
  {id:'fotos',     label:'Fotos',     accept:'image/*',              icon:'M21 19V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2zM8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM21 15l-5-5L5 21'},
  {id:'camera',    label:'Câmera',    accept:'image/*', capture:true, icon:'M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2zM12 17a4 4 0 100-8 4 4 0 000 8z'},
  {id:'arquivos',  label:'Arquivos',  accept:'*',                    icon:'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z'},
  {id:'planilhas', label:'Planilhas', accept:'.xlsx,.xls,.csv,.ods', icon:'M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18'},
  {id:'audios',    label:'Áudios',    accept:'audio/*',              icon:'M9 18V5l12-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zM21 16a3 3 0 11-6 0 3 3 0 016 0z'},
  {id:'videos',    label:'Vídeos',    accept:'video/*',              icon:'M23 7l-7 5 7 5V7zM14 5H3a2 2 0 00-2 2v10a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2z'},
];

const ATTACH_TOOLS = [
  {id:'visual-call',   label:'Chamada Visual',             desc:'Converse com Athena vendo você',
    icon:'M15 10l4.553-2.277A1 1 0 0121 8.677V15.32a1 1 0 01-1.447.894L15 14M3 8h9a3 3 0 013 3v4a3 3 0 01-3 3H3a3 3 0 01-3-3v-4a3 3 0 013-3z'},
  {id:'imagens',       label:'Imagens',                    desc:'Crie e edite imagens',
    icon:'M21 19V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2zM8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM21 15l-5-5L5 21',
    prompt:'Quero criar uma imagem: '},
  {id:'video-gen',     label:'Vídeos',                     desc:'Crie vídeos por IA',
    icon:'M23 7l-7 5 7 5V7zM14 5H3a2 2 0 00-2 2v10a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2z',
    prompt:'Quero criar um vídeo: '},
  {id:'musicas',       label:'Músicas',                    desc:'Crie faixas de áudio',
    icon:'M9 18V5l12-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zM21 16a3 3 0 11-6 0 3 3 0 016 0z',
    prompt:'Quero criar uma faixa de áudio: '},
  {id:'canvas',        label:'Canvas',                     desc:'Programar, escrever e criar apresentações',
    icon:'M16 18l6-6-6-6M8 6l-6 6 6 6',
    prompt:'Vamos abrir o Canvas para: '},
  {id:'habilidades',   label:'Habilidades',                desc:'Gerencie habilidades da Athena',
    icon:D.brain, badge:'Novo'},
  {id:'guiado',        label:'Aprendizado Guiado',         desc:'Respostas mais estruturadas e organizadas',
    icon:'M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z', toggle:true},
  {id:'personalizada', label:'Inteligência Personalizada', desc:'A Athena aprende e se adapta a você',
    icon:'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8zM18 8l1.5 1.5M21 5l-1 1', badge:'Labs', toggle:true},
];

function AttachModal({onClose, onAttach, onDevicePick, onInsertPrompt, onOpenSkills, onOpenVisualCall, settings, onToggleSetting}) {
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const openPicker = async (shortcut) => {
    // Android selection is routed through the capability facade. It returns
    // opaque private-cache tokens rather than content:// URIs or paths.
    if (onDevicePick && ['fotos', 'camera', 'arquivos', 'planilhas'].includes(shortcut.id)) {
      const handled = await onDevicePick(shortcut);
      if (handled) {
        onClose();
        return;
      }
    }
    const { accept, capture } = shortcut;
    fileRef.current.accept = accept;
    if(capture) fileRef.current.setAttribute('capture','environment');
    else fileRef.current.removeAttribute('capture');
    fileRef.current.click();
  };

  return (
    <Modal onClose={onClose} width={420}>
      <div style={{padding:'18px 18px 16px',background:'rgba(9,16,32,0.6)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <span style={{fontSize:14,fontWeight:600,color:C.text}}>Adicionar</span>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
        </div>

        {/* Faixa de atalhos — scroll horizontal */}
        <div className="no-scrollbar" style={{display:'flex',gap:8,overflowX:'auto',
          paddingBottom:2,marginBottom:16,scrollbarWidth:'none',msOverflowStyle:'none'}}>
          {ATTACH_SHORTCUTS.map(s=>(
            <button key={s.id} onClick={()=>{ void openPicker(s); }} style={{
              flexShrink:0,display:'flex',flexDirection:'column',alignItems:'center',gap:6,
              padding:'12px 14px',minWidth:72,background:C.base,border:`1px solid ${C.border}`,
              borderRadius:12,cursor:'pointer',transition:'border-color .15s',
            }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=C.accentD}
              onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}
            >
              <Ico d={s.icon} size={16}/>
              <span style={{fontSize:10.5,color:C.muted,whiteSpace:'nowrap'}}>{s.label}</span>
            </button>
          ))}
        </div>

        {/* Lista de ferramentas — estilo Gemini */}
        <div style={{display:'flex',flexDirection:'column',maxHeight:220,overflowY:'auto'}}>
          {ATTACH_TOOLS.map((t,i)=>{
            const isOn = t.toggle ? !!settings?.[t.id] : false;
            return (
              <button key={t.id} onClick={()=>{
                if(t.id==='visual-call'){ onOpenVisualCall(); onClose(); }
                else if(t.id==='habilidades'){ onOpenSkills(); onClose(); }
                else if(t.toggle){ onToggleSetting(t.id); }
                else if(t.prompt){ onInsertPrompt(t.prompt); onClose(); }
              }} style={{
                display:'flex',alignItems:'center',gap:12,width:'100%',
                padding:'10px 4px',background:'none',border:'none',
                borderTop:i===0?'none':`1px solid ${C.border}80`,
                cursor:'pointer',textAlign:'left',transition:'background .12s',
              }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.025)'}
                onMouseLeave={e=>e.currentTarget.style.background='none'}
              >
                <div style={{width:30,height:30,borderRadius:8,flexShrink:0,background:C.card,
                  border:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'center',color:C.soft}}>
                  <Ico d={t.icon} size={14}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:13,fontWeight:500,color:C.text}}>{t.label}</span>
                    {t.badge && (
                      <span style={{fontSize:9,padding:'1px 6px',borderRadius:20,
                        background:`${C.accent}14`,border:`1px solid ${C.accentD}`,color:C.accent}}>{t.badge}</span>
                    )}
                  </div>
                  <div style={{fontSize:11,color:C.dim,marginTop:1}}>{t.desc}</div>
                </div>
                {t.toggle ? (
                  <div style={{width:34,height:19,borderRadius:20,flexShrink:0,position:'relative',
                    background:isOn?C.accentD:C.border,border:`1px solid ${isOn?C.accent:C.border}`,transition:'all .2s'}}>
                    <div style={{position:'absolute',top:1,left:isOn?16:1,width:15,height:15,borderRadius:'50%',
                      background:isOn?C.accent:C.soft,transition:'all .2s'}}/>
                  </div>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.dim} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                )}
              </button>
            );
          })}
        </div>

        <div onDragOver={e=>{e.preventDefault();setDragging(true)}}
          onDragLeave={()=>setDragging(false)}
          onDrop={e=>{e.preventDefault();setDragging(false);
            const f=e.dataTransfer.files[0]; if(f) onAttach(f); onClose();}}
          style={{
            marginTop:14,height:56,border:`1.5px dashed ${dragging?C.accent:C.border}`,
            borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',
            color:dragging?C.accent:C.dim,fontSize:11,transition:'all .2s',
            background:dragging?`${C.accent}06`:'transparent',
          }}>
          Ou arraste aqui qualquer arquivo
        </div>
        <input ref={fileRef} type="file" style={{display:'none'}}
          onChange={e=>{const f=e.target.files[0];if(f)onAttach(f);onClose();}}/>
      </div>
    </Modal>
  );
}

// ─── Profile Modal ────────────────────────────────────────────────────────────
function ProfileModal({user, onClose, onSave}) {
  const fields = [
    {label:'Nome',            key:'name',       ph:'Seu nome'},
    {label:'Empresa',         key:'company',    ph:'Ex: Outframe'},
    {label:'Cargo',           key:'role',       ph:'Ex: CEO'},
    {label:'Objetivos',       key:'goals',      ph:'O que você quer alcançar?'},
    {label:'Interesses',      key:'interests',  ph:'Ex: IA, design, automação'},
    {label:'Como você trabalha', key:'style',   ph:'Ex: prefiro respostas objetivas'},
  ];
  const [vals, setVals] = useState(fields.reduce((a,f)=>({...a,[f.key]:''}),{}));

  return (
    <Modal onClose={onClose} width={460}>
      <div style={{padding:'20px 22px',maxHeight:'80vh',overflowY:'auto'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
          <span style={{fontSize:15,fontWeight:600,color:C.text}}>Perfil</span>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
        </div>
        <div style={{background:`${C.accent}0a`,border:`1px solid ${C.accentD}`,borderRadius:8,padding:'10px 12px',marginBottom:16,fontSize:11,color:C.accent}}>
          Athena aprende com estas informações e personaliza todas as respostas para você.
        </div>
        {fields.map(f=>(
          <div key={f.key} style={{marginBottom:12}}>
            <div style={{fontSize:11,color:C.muted,marginBottom:5}}>{f.label}</div>
            <input value={vals[f.key]} onChange={e=>setVals(p=>({...p,[f.key]:e.target.value}))}
              placeholder={f.ph} style={{width:'100%',padding:'8px 12px',
                background:C.base,border:`1px solid ${C.border}`,borderRadius:8,
                color:C.text,fontSize:13,outline:'none',boxSizing:'border-box'}}
              onFocus={e=>e.target.style.borderColor=`${C.accent}60`}
              onBlur={e=>e.target.style.borderColor=C.border}/>
          </div>
        ))}
        <button onClick={()=>{onSave(vals);onClose();}} style={{width:'100%',padding:'10px',
          background:C.accentB,border:`1px solid ${C.accentD}`,borderRadius:8,
          color:C.accent,fontSize:13,fontWeight:500,cursor:'pointer',marginTop:4}}>
          Salvar e ensinar Athena
        </button>
      </div>
    </Modal>
  );
}

// ─── Skills Floating Panel ────────────────────────────────────────────────────
function SkillsPanel({onClose, token, showToast: showToastParent}) {
  const [mode, setMode] = useState('list');
  const [text, setText] = useState('');
  const [dragging, setDragging] = useState(false);
  const [toast, setToast] = useState('');
  const [skills, setSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(true);
  const [teaching, setTeaching] = useState(false);
  const fileRef = useRef(null);

  const showToast = msg => {
    if (showToastParent) showToastParent(msg);
    else { setToast(msg); setTimeout(()=>setToast(''),2500); }
  };

  const loadSkills = async () => {
    try {
      const {data} = await axios.get(`${API_BASE}/skills/list`,
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setSkills(data.skills || []);
    } catch(e) {/* silencioso */}
    finally { setLoadingSkills(false); }
  };

  useEffect(()=>{ loadSkills(); /* eslint-disable-next-line */ }, []);

  const removeSkill = async skill_id => {
    try {
      await axios.delete(`${API_BASE}/skills/${skill_id}`,
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setSkills(prev=>prev.filter(s=>s.skill_id!==skill_id));
      showToast('Skill removida');
    } catch(e) { showToast('Não foi possível remover a skill'); }
  };

  const teachAthena = async () => {
    const topic = text.trim();
    if(!topic || teaching) return;
    setTeaching(true);
    try {
      const {data} = await axios.post(`${API_BASE}/skills/create`, {topic},
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setSkills(prev=>[data.skill, ...prev]);
      showToast(`Skill criada: ${data.skill.name}`);
      setText('');
    } catch(e) {
      showToast('Não foi possível criar a skill agora');
    } finally { setTeaching(false); }
  };

  const handleFile = f => {
    if (!f?.name) return;
    showToast(`Importar “${f.name}” para criar uma skill ainda não está disponível; o arquivo não foi enviado e nenhuma skill foi criada.`);
  };

  return (
    <div style={{
      position:'fixed',top:0,right:0,bottom:0,width:320,zIndex:150,
      background:'#0c0d1c',borderLeft:`1px solid ${C.border}`,
      display:'flex',flexDirection:'column',
      animation:'slideR .2s ease',boxShadow:'-8px 0 32px rgba(0,0,0,.6)',
    }}>
      {toast && <Toast msg={toast}/>}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'16px 18px',borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:18}}>🧠</span>
          <span style={{fontSize:14,fontWeight:600,color:C.text}}>Skills</span>
        </div>
        <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:20,lineHeight:1}}>×</button>
      </div>

      <div style={{flex:1,overflowY:'auto',padding:'14px 16px'}}>
        <div style={{fontSize:10,color:C.dim,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:10}}>Minhas Skills</div>
        {loadingSkills ? (
          <div style={{fontSize:11.5,color:C.dim,padding:'8px 0'}}>Carregando...</div>
        ) : skills.length===0 ? (
          <div style={{fontSize:11.5,color:C.dim,padding:'8px 0',lineHeight:1.5}}>
            Nenhuma skill ainda. Ensine a Athena algo abaixo.
          </div>
        ) : skills.map(s=>(
          <div key={s.skill_id} style={{
            display:'flex',alignItems:'center',justifyContent:'space-between',
            padding:'8px 10px',borderRadius:8,marginBottom:6,
            background:C.card,border:`0.5px solid ${C.border}`,
          }}>
            <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
              <span style={{fontSize:13,color:s.active?C.accent:C.dim}}>⚡</span>
              <span style={{fontSize:12,color:s.active?'#c8ccd8':C.muted,overflow:'hidden',
                textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.name}</span>
            </div>
            <button onClick={()=>removeSkill(s.skill_id)}
              style={{background:'none',border:'none',cursor:'pointer',color:C.dim,fontSize:12,flexShrink:0}}
              onMouseEnter={e=>e.currentTarget.style.color='#ef4444'}
              onMouseLeave={e=>e.currentTarget.style.color=C.dim}>✕</button>
          </div>
        ))}

        <div style={{marginTop:20}}>
          <div style={{fontSize:10,color:C.dim,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:10}}>Adicionar Skill</div>
          <div style={{display:'flex',gap:6,marginBottom:12}}>
            {[{id:'text',label:'Digitar'},{id:'file',label:'Upload'},{id:'drag',label:'Arrastar'}].map(m=>(
              <button key={m.id} onClick={()=>setMode(m.id)} style={{
                flex:1,padding:'6px 0',borderRadius:7,fontSize:11,cursor:'pointer',
                background:mode===m.id?C.accentB:C.base,
                border:`1px solid ${mode===m.id?C.accentD:C.border}`,
                color:mode===m.id?C.accent:C.muted,
              }}>{m.label}</button>
            ))}
          </div>

          {mode==='text' && (
            <>
              <textarea value={text} onChange={e=>setText(e.target.value)}
                placeholder="Ensine a Athena a criar campanhas para Instagram com análise de tendências..."
                style={{width:'100%',height:100,padding:'10px',borderRadius:8,
                  background:C.base,border:`1px solid ${C.border}`,color:C.text,
                  fontSize:12,resize:'none',outline:'none',boxSizing:'border-box',lineHeight:1.6}}
                onFocus={e=>e.target.style.borderColor=`${C.accent}60`}
                onBlur={e=>e.target.style.borderColor=C.border}/>
              <button onClick={teachAthena} disabled={!text.trim()||teaching}
                style={{width:'100%',marginTop:8,padding:'9px',
                  background:text.trim()&&!teaching?C.accentB:C.base,
                  border:`1px solid ${text.trim()&&!teaching?C.accentD:C.border}`,
                  borderRadius:8,color:text.trim()&&!teaching?C.accent:C.muted,
                  fontSize:12,cursor:text.trim()&&!teaching?'pointer':'default'}}>
                {teaching ? 'Estruturando conhecimento...' : 'Ensinar Athena'}
              </button>
            </>
          )}

          {mode==='file' && (
            <div onClick={()=>fileRef.current.click()} style={{
              height:90,border:`1px dashed ${C.border}`,borderRadius:10,
              display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
              gap:6,cursor:'pointer',background:C.base,transition:'border-color .15s',
            }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=C.accentD}
              onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}
            >
              <Ico d={D.upload} size={20}/>
              <span style={{fontSize:11,color:C.muted}}>PDF • TXT • DOCX • CSV • MD</span>
              <input ref={fileRef} type="file" accept=".pdf,.txt,.docx,.csv,.md" style={{display:'none'}}
                onChange={e=>{const f=e.target.files[0];if(f)handleFile(f);}}/>
            </div>
          )}

          {mode==='drag' && (
            <div onDragOver={e=>{e.preventDefault();setDragging(true)}}
              onDragLeave={()=>setDragging(false)}
              onDrop={e=>{e.preventDefault();setDragging(false);const f=e.dataTransfer.files[0];if(f)handleFile(f);}}
              style={{
                height:90,border:`1.5px dashed ${dragging?C.accent:C.border}`,borderRadius:10,
                display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:6,
                background:dragging?`${C.accent}06`:C.base,transition:'all .2s',
                color:dragging?C.accent:C.dim,fontSize:12,
              }}>
              <span style={{fontSize:20}}>📂</span>
              Arraste qualquer arquivo
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Habilidades modal ────────────────────────────────────────────────────────
function SkillRow({skill, onToggle, onDelete}) {
  const active = !!skill.active;
  return (
    <div style={{display:'flex',alignItems:'center',gap:10,padding:'9px 4px',
      borderBottom:`1px solid ${C.border}80`}}>
      <div style={{width:16,height:16,borderRadius:'50%',flexShrink:0,
        border:`1.5px solid ${active?C.accent:C.soft}`,
        display:'flex',alignItems:'center',justifyContent:'center'}}>
        {active && <Ico d={D.check} size={9}/>}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12.5,fontWeight:500,color:active?C.text:C.muted}}>{skill.name}</div>
        {skill.description && <div style={{fontSize:10.5,color:C.dim,marginTop:1,
          overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{skill.description}</div>}
      </div>
      <button onClick={()=>onToggle(skill.skill_id)} style={{
        fontSize:10.5,padding:'4px 9px',borderRadius:7,whiteSpace:'nowrap',
        background:active?C.base:C.accentB,border:`1px solid ${active?C.border:C.accentD}`,
        color:active?C.muted:C.accent,cursor:'pointer'}}>
        {active ? 'Desativar' : 'Ativar'}
      </button>
      <button onClick={()=>onDelete(skill.skill_id)} style={{
        background:'none',border:'none',cursor:'pointer',color:C.dim,padding:4}}
        onMouseEnter={e=>e.currentTarget.style.color='#ef4444'}
        onMouseLeave={e=>e.currentTarget.style.color=C.dim}>
        <Ico d={D.trash} size={13}/>
      </button>
    </div>
  );
}

function SkillsModal({onClose, token, showToast, onOpenLegacy}) {
  const [skills,   setSkills]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [query,    setQuery]    = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const {data} = await axios.get(`${API_BASE}/skills/list`,
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setSkills(data.skills || []);
    } catch(e) {/* silencioso */}
    finally { setLoading(false); }
  };

  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, []);

  const toggle = async skill_id => {
    try {
      const {data} = await axios.patch(`${API_BASE}/skills/${skill_id}/toggle`, {},
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setSkills(prev=>prev.map(s=>s.skill_id===skill_id ? {...s, active:data.active} : s));
    } catch(e) { showToast('Não foi possível atualizar a skill'); }
  };

  const remove = async skill_id => {
    try {
      await axios.delete(`${API_BASE}/skills/${skill_id}`,
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setSkills(prev=>prev.filter(s=>s.skill_id!==skill_id));
      showToast('Skill removida');
    } catch(e) { showToast('Não foi possível remover a skill'); }
  };

  const createSkill = async () => {
    const topic = query.trim();
    if(!topic || creating) return;
    setCreating(true);
    try {
      const {data} = await axios.post(`${API_BASE}/skills/create`, {topic},
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setSkills(prev=>[data.skill, ...prev]);
      showToast(`Skill criada: ${data.skill.name}`);
      setQuery('');
    } catch(e) {
      showToast('Não foi possível criar a skill agora');
    } finally { setCreating(false); }
  };

  const active   = skills.filter(s=>s.active);
  const inactive = skills.filter(s=>!s.active);
  const canCreate = query.trim() && !creating;

  return (
    <Modal onClose={onClose} width={420}>
      <div style={{padding:'18px 20px',maxHeight:'82vh',display:'flex',flexDirection:'column'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <Ico d={D.brain} size={16}/>
            <span style={{fontSize:13,fontWeight:600,color:C.text,letterSpacing:'.06em'}}>HABILIDADES ATIVAS</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <button onClick={onOpenLegacy} title="Gerenciar skills"
              style={{width:26,height:26,borderRadius:8,background:C.base,border:`1px solid ${C.border}`,
                color:C.muted,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
              <Ico d={D.plus} size={13}/>
            </button>
            <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
          </div>
        </div>
        <div style={{fontSize:11,color:C.dim,marginBottom:14}}>Gerencie as habilidades que a Athena usa nas conversas.</div>

        <div style={{flex:1,overflowY:'auto',marginBottom:14,minHeight:60}}>
          {loading ? (
            <div style={{fontSize:12,color:C.dim,padding:'20px 0',textAlign:'center'}}>Carregando habilidades...</div>
          ) : skills.length===0 ? (
            <div style={{fontSize:12,color:C.dim,padding:'20px 0',textAlign:'center'}}>
              Nenhuma habilidade ainda. Pesquise um tema abaixo para criar a primeira.
            </div>
          ) : (
            <>
              {active.map(s=><SkillRow key={s.skill_id} skill={s} onToggle={toggle} onDelete={remove}/>)}
              {inactive.map(s=><SkillRow key={s.skill_id} skill={s} onToggle={toggle} onDelete={remove}/>)}
            </>
          )}
        </div>

        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14}}>
          <div style={{fontSize:10,color:C.dim,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:8}}>Sistema de Aprendizado de Skill</div>
          <div style={{display:'flex',gap:8}}>
            <input value={query} onChange={e=>setQuery(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter') createSkill();}}
              placeholder="Pesquisar habilidade..." disabled={creating}
              style={{flex:1,padding:'9px 12px',background:C.base,border:`1px solid ${C.border}`,
                borderRadius:9,color:C.text,fontSize:12,outline:'none'}}
              onFocus={e=>e.target.style.borderColor=`${C.accent}60`}
              onBlur={e=>e.target.style.borderColor=C.border}/>
            <button onClick={createSkill} disabled={!canCreate}
              style={{padding:'0 16px',borderRadius:9,fontSize:12,whiteSpace:'nowrap',
                background:canCreate?C.accentB:C.base,
                border:`1px solid ${canCreate?C.accentD:C.border}`,
                color:canCreate?C.accent:C.muted,
                cursor:canCreate?'pointer':'default'}}>
              {creating ? 'Criando...' : 'Criar skill'}
            </button>
          </div>
          <div style={{fontSize:10.5,color:C.dim,marginTop:7,lineHeight:1.5}}>
            A Athena pesquisa o tema, estrutura o conhecimento e cria uma habilidade nova — ex.: "Fermentação natural" → Skill criada: Especialista em fermentação natural.
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Panel: Conversas ─────────────────────────────────────────────────────────
function PanelConversas({recents, onSelect, onClose}) {
  const [filter, setFilter] = useState('recentes');
  const [search, setSearch] = useState('');
  const [ctx, setCtx] = useState(null); // {id, x, y}

  const filtered = recents.filter(r => r.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',animation:'fadeIn .15s ease'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>Chats</span>
        <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
      </div>

      <div style={{padding:'10px 16px 6px'}}>
        <div style={{position:'relative',marginBottom:10}}>
          <Ico d={D.search} size={13}/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Buscar conversas..."
            style={{width:'100%',padding:'7px 10px 7px 30px',background:C.base,
              border:`1px solid ${C.border}`,borderRadius:8,color:C.text,
              fontSize:12,outline:'none',boxSizing:'border-box',position:'relative'}}/>
          <div style={{position:'absolute',top:'50%',left:10,transform:'translateY(-50%)',color:C.dim,pointerEvents:'none'}}>
            <Ico d={D.search} size={13}/>
          </div>
        </div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {['Recentes','Favoritas','Arquivadas'].map(f=>(
            <button key={f} onClick={()=>setFilter(f.toLowerCase())} style={{
              padding:'3px 10px',borderRadius:20,fontSize:11,cursor:'pointer',
              background:filter===f.toLowerCase()?C.accentB:C.base,
              border:`1px solid ${filter===f.toLowerCase()?C.accentD:C.border}`,
              color:filter===f.toLowerCase()?C.accent:C.muted,
            }}>{f}</button>
          ))}
        </div>
      </div>

      <div style={{flex:1,overflowY:'auto',padding:'4px 10px'}}>
        {filtered.map(r=>(
          <div key={r.id}
            onContextMenu={e=>{e.preventDefault();setCtx({id:r.id,x:e.clientX,y:e.clientY});}}
            onClick={()=>{onSelect(r.id);onClose();}}
            style={{
              display:'flex',alignItems:'center',justifyContent:'space-between',
              padding:'8px 10px',borderRadius:8,cursor:'pointer',marginBottom:2,
              transition:'background .1s',
            }}
            onMouseEnter={e=>e.currentTarget.style.background=C.card}
            onMouseLeave={e=>e.currentTarget.style.background='none'}
          >
            <span style={{fontSize:12,color:'#c0c4e0',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.label}</span>
          </div>
        ))}
      </div>

      {/* Context menu */}
      {ctx && (
        <>
          <div style={{position:'fixed',inset:0,zIndex:300}} onClick={()=>setCtx(null)}/>
          <div style={{position:'fixed',left:ctx.x,top:ctx.y,zIndex:301,
            background:C.card,border:`1px solid ${C.border}`,borderRadius:9,
            padding:'6px 0',minWidth:160,boxShadow:'0 8px 24px rgba(0,0,0,.7)',
            animation:'fadeIn .1s ease'}}>
            {[
              {icon:'✏️',label:'Renomear'},
              {icon:'📌',label:'Fixar'},
              {icon:'📋',label:'Duplicar'},
              {icon:'📁',label:'Mover para projeto'},
              {icon:'🗑️',label:'Excluir',danger:true},
            ].map(item=>(
              <button key={item.label} onClick={()=>setCtx(null)} style={{
                display:'flex',alignItems:'center',gap:8,width:'100%',
                padding:'7px 14px',background:'none',border:'none',cursor:'pointer',
                color:item.danger?'#ef4444':C.muted,fontSize:12,textAlign:'left',
              }}
                onMouseEnter={e=>e.currentTarget.style.background=C.border}
                onMouseLeave={e=>e.currentTarget.style.background='none'}
              >{item.icon} {item.label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Panel: Projetos ──────────────────────────────────────────────────────────
function PanelProjetos({projects, onClose, onOpenProject}) {
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',animation:'fadeIn .15s ease'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>Projetos</span>
        <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'14px 16px'}}>
        {projects.length===0 ? (
          <div style={{textAlign:'center',padding:'40px 20px',color:C.dim}}>
            <div style={{fontSize:28,marginBottom:8}}>📁</div>
            <div style={{fontSize:13,marginBottom:4}}>Nenhum projeto ainda</div>
            <div style={{fontSize:11}}>Use + para criar um projeto</div>
          </div>
        ) : projects.map((p,i)=>(
          <div key={p.project_id||p.session_id||i} style={{padding:'12px 14px',borderRadius:10,marginBottom:8,
            background:C.base,border:`1px solid ${C.border}`,cursor:'pointer'}}
            onClick={()=>onOpenProject?.(p)}
            onMouseEnter={e=>e.currentTarget.style.borderColor=C.accentD}
            onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
            <div style={{fontSize:13,fontWeight:500,color:C.text,marginBottom:4}}>{p.name}</div>
            {p.desc&&<div style={{fontSize:11,color:C.muted,marginBottom:6}}>{p.desc}</div>}
            {p.goal&&<div style={{fontSize:11,color:C.accent,opacity:.7}}>🎯 {p.goal}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Um projeto é um ambiente vivo: este painel mostra o estado que a Athena
// realmente consegue recuperar, em vez de simular progresso ou inventar dados.
function PanelProjetoAmbiente({token, projectId, onBack, onOpenConversation}) {
  const lifeOS=useOptionalLifeOS(); const projectOpenedRef=useRef('');
  const [snapshot,setSnapshot]=useState(null); const [loading,setLoading]=useState(true);
  const [decision,setDecision]=useState(''); const [saving,setSaving]=useState(false);
  const load=useCallback(async()=>{setLoading(true);try{const {data}=await axios.get(`${API_BASE}/projects/${projectId}/snapshot`,{headers:operationalHeaders(token)});setSnapshot(data.snapshot||null);}catch{setSnapshot(null);}finally{setLoading(false);}},[token,projectId]);
  useEffect(()=>{load();},[load]);
  useEffect(()=>{if(!snapshot||projectOpenedRef.current===projectId)return;projectOpenedRef.current=projectId;void lifeOS?.trackTechnicalEvent?.('project_open','workspace');},[lifeOS,projectId,snapshot]);
  const addDecision=async()=>{if(!decision.trim()||saving)return;setSaving(true);try{await axios.post(`${API_BASE}/projects/${projectId}/decisions`,{title:decision.trim()},{headers:operationalHeaders(token)});setDecision('');await load();}finally{setSaving(false);}};
  if(loading)return <div style={{padding:24,color:C.muted,fontSize:12}}>Carregando ambiente do projeto…</div>;
  if(!snapshot)return <WorkspaceEmptyPanel title="Projeto não encontrado" body="Não foi possível recuperar o contexto deste projeto." onClose={onBack}/>;
  const {project}=snapshot; const stateColor=snapshot.state==='atenção'?'#fb7185':snapshot.state==='ativo'?C.accent:'#22c55e';
  const nextStepReason={overdue_task:'Prazo vencido',next_due_task:'Próximo prazo',next_open_task:'Tarefa em aberto',next_action:'Próxima ação registrada',next_event:'Próximo compromisso'}[snapshot.next_step?.reason]||'Próxima ação baseada no contexto do projeto';
  const Section=({title,children,empty})=><section style={{padding:14,border:`1px solid ${C.border}`,borderRadius:12,background:C.base}}><div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:10}}>{title}</div>{children||<div style={{fontSize:11,color:C.muted}}>{empty||'Nada registrado por enquanto.'}</div>}</section>;
  const Row=({title,meta,onClick})=><button onClick={onClick} disabled={!onClick} style={{display:'block',textAlign:'left',width:'100%',padding:'8px 0',border:0,borderTop:`1px solid ${C.border}`,background:'transparent',color:C.text,cursor:onClick?'pointer':'default'}}><div style={{fontSize:12,fontWeight:500}}>{title}</div>{meta&&<div style={{fontSize:10,color:C.muted,marginTop:3}}>{meta}</div>}</button>;
  return <div className="workspace-panel" style={{height:'100%',overflowY:'auto',padding:'24px',animation:'fadeIn .15s ease'}}>
    <div className="workspace-project-header" style={{display:'flex',justifyContent:'space-between',gap:12,alignItems:'start',marginBottom:18}}><div><button onClick={onBack} style={{border:0,background:'none',padding:0,color:C.accent,cursor:'pointer',fontSize:11}}>← Projetos</button><h1 style={{margin:'8px 0 5px',fontSize:21,color:C.text}}>{project.name}</h1><div style={{fontSize:12,color:C.muted}}>{project.type||'general'} · <span style={{color:stateColor,fontWeight:600,textTransform:'capitalize'}}>{snapshot.state}</span></div></div><button className="workspace-project-action" onClick={onOpenConversation} style={{padding:'9px 12px',border:0,borderRadius:8,background:C.accent,color:'#fff',cursor:'pointer'}}>Conversar aqui</button></div>
    {(project.objective||project.goal||project.summary||project.desc)&&<section style={{padding:15,border:`1px solid ${C.accentD}`,borderRadius:12,background:C.card,marginBottom:13}}>{project.objective&&<div style={{fontSize:13,color:C.text,fontWeight:600}}>Objetivo: {project.objective}</div>}{!project.objective&&project.goal&&<div style={{fontSize:13,color:C.text,fontWeight:600}}>Objetivo: {project.goal}</div>}{project.summary&&<div style={{fontSize:12,color:C.soft,lineHeight:1.55,marginTop:8}}>{project.summary}</div>}{project.desc&&!project.summary&&<div style={{fontSize:12,color:C.soft,lineHeight:1.55,marginTop:8}}>{project.desc}</div>}</section>}
    {snapshot.next_step&&<section style={{padding:14,border:`1px solid ${C.accentD}`,borderRadius:12,background:'rgba(91,185,255,.07)',marginBottom:13}}><div style={{fontSize:10,fontWeight:700,color:C.accent,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>Próximo passo</div><div style={{fontSize:13,fontWeight:600,color:C.text}}>{snapshot.next_step.title}</div><div style={{fontSize:11,color:C.muted,marginTop:4}}>{nextStepReason}</div></section>}
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:12}}>
      <Section title={`Pendências · ${snapshot.pending_tasks.length}`} empty="Nenhuma pendência ativa.">{snapshot.pending_tasks.map(task=><Row key={task.id} title={task.title} meta={`${task.priority||'normal'} · ${dateLabel(task.due_at)}`}/>)}</Section>
      <Section title={`Pendências abertas · ${(snapshot.open_loops||[]).length}`} empty="Nenhuma pendência aberta vinculada ao projeto.">{(snapshot.open_loops||[]).map(item=><Row key={item.id} title={item.topic} meta={item.next_action||item.summary||item.context}/>)}</Section>
      <Section title="Próxima agenda" empty="Sem eventos futuros.">{snapshot.upcoming_events.map(event=><Row key={event.id} title={event.title} meta={dateLabel(event.start_at)}/>)}</Section>
      <Section title="Planejamento ativo" empty="Ainda não há planejamento ativo.">{snapshot.active_plannings.map(item=><Row key={item.id} title={item.title} meta={item.description}/>)}</Section>
      <Section title="Decisões atuais" empty="Registre decisões importantes para a Athena manter continuidade.">{snapshot.current_decisions.map(item=><Row key={item.id} title={item.title} meta={item.content}/>)}</Section>
      <Section title={`Arquivos recentes · ${(snapshot.files||[]).length}`} empty="Nenhum arquivo vinculado ao projeto.">{(snapshot.files||[]).map(item=><Row key={item.id} title={item.original_name||item.name||'Arquivo'} meta={item.mime_type||item.source}/>)}</Section>
      <Section title={`Itens salvos · ${(snapshot.vault_items||[]).length}`} empty="Nenhum item salvo neste projeto.">{(snapshot.vault_items||[]).map(item=><Row key={item.id} title={item.title||'Item salvo'} meta={item.summary||item.category||item.content}/>)}</Section>
      <Section title="Conversas recentes" empty="Nenhuma conversa vinculada ao projeto.">{snapshot.recent_conversations.map(item=><Row key={item.session_id} title={item.title||'Conversa'} meta={item.last_message||dateLabel(item.updated_at)} onClick={()=>onOpenConversation(item.session_id)}/>)}</Section>
      <Section title="Pessoas" empty="Nenhuma pessoa registrada.">{snapshot.people.map(item=><Row key={item.id} title={item.name} meta={item.role||item.notes}/>)}</Section>
    </div>
    <section style={{marginTop:12,padding:14,border:`1px solid ${C.border}`,borderRadius:12,background:C.base}}><div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:8}}>Registrar decisão</div><div className="workspace-decision-row" style={{display:'flex',gap:8}}><input value={decision} onChange={e=>setDecision(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')addDecision();}} placeholder="Ex.: aprovar a primeira versão" style={{flex:1,minWidth:0,padding:9,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}/><button onClick={addDecision} disabled={saving||!decision.trim()} style={{padding:'8px 11px',border:0,borderRadius:8,background:C.accent,color:'#fff',cursor:'pointer'}}>Salvar</button></div></section>
  </div>;
}

function WorkspaceEmptyPanel({title, body, onClose}) {
  return <div style={{display:'flex',height:'100%',alignItems:'center',justifyContent:'center',padding:28,textAlign:'center'}}>
    <div style={{maxWidth:330}}>
      <div style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:9}}>{title}</div>
      <div style={{fontSize:12,lineHeight:1.6,color:C.muted}}>{body}</div>
      <button onClick={onClose} style={{marginTop:18,padding:'8px 12px',borderRadius:8,border:`1px solid ${C.border}`,background:C.card,color:C.text,cursor:'pointer'}}>Voltar ao Workspace</button>
    </div>
  </div>;
}

function PanelPlanejamento({token, projects, onClose}) {
  const [items,setItems]=useState([]); const [title,setTitle]=useState(''); const [description,setDescription]=useState(''); const [projectId,setProjectId]=useState(''); const [busy,setBusy]=useState(false);
  const load=useCallback(()=>axios.get(`${API_BASE}/planning`,{headers:{Authorization:`Bearer ${token}`}}).then(({data})=>setItems(data.plannings||[])).catch(()=>setItems([])),[token]);
  useEffect(()=>{load();},[load]);
  const create=async()=>{if(!title.trim()||busy)return;setBusy(true);try{await axios.post(`${API_BASE}/planning`,{title,description,project_id:projectId||null},{headers:{Authorization:`Bearer ${token}`}});setTitle('');setDescription('');setProjectId('');load();}finally{setBusy(false);}};
  return <div style={{height:'100%',overflowY:'auto',padding:'20px',animation:'fadeIn .15s ease'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}><span style={{fontSize:16,fontWeight:600,color:C.text}}>Planejamento</span><button onClick={onClose} style={{background:'none',border:'none',color:C.muted,cursor:'pointer'}}>×</button></div>
    <div style={{padding:14,border:`1px solid ${C.border}`,borderRadius:12,background:C.base,marginBottom:14}}>
      <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Nome do planejamento" style={{width:'100%',boxSizing:'border-box',padding:9,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,marginBottom:8}}/>
      <textarea value={description} onChange={e=>setDescription(e.target.value)} placeholder="Contexto ou objetivo (opcional)" style={{width:'100%',minHeight:62,boxSizing:'border-box',padding:9,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,resize:'vertical'}}/>
      <select value={projectId} onChange={e=>setProjectId(e.target.value)} style={{marginTop:8,width:'100%',padding:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}><option value="">Sem projeto associado</option>{projects.map(p=><option key={p.project_id} value={p.project_id}>{p.name}</option>)}</select>
      <button onClick={create} disabled={!title.trim()||busy} style={{marginTop:10,padding:'8px 12px',border:0,borderRadius:8,background:C.accent,color:'#fff',cursor:'pointer',opacity:busy?0.6:1}}>{busy?'Criando…':'Criar planejamento'}</button>
    </div>
    {items.length===0 ? <div style={{color:C.dim,fontSize:12,padding:'18px 0'}}>Ainda não existe planejamento aqui.</div> : items.map(item=><div key={item.id} style={{padding:12,marginBottom:8,border:`1px solid ${C.border}`,borderRadius:10,background:C.base}}><div style={{color:C.text,fontWeight:600}}>{item.title}</div>{item.description&&<div style={{color:C.muted,fontSize:12,marginTop:5}}>{item.description}</div>}</div>)}
  </div>;
}

const operationalHeaders = token => ({Authorization:`Bearer ${token}`});
const localInputToIso = value => value ? new Date(value).toISOString() : null;
const dateLabel = value => value ? new Intl.DateTimeFormat('pt-BR',{dateStyle:'medium',timeStyle:'short'}).format(new Date(value)) : 'Sem prazo';

function DeviceCalendarSyncControl({
  enabled, calendars, selectedCalendarId, endWhen, busy,
  onRequestAccess, onEnabledChange, onCalendarChange, onEndWhenChange,
}) {
  const writableCalendars = (calendars || []).filter((calendar) => calendar.canWrite);
  return (
    <section style={{padding:12,border:`1px solid ${enabled ? C.accentD : C.border}`,borderRadius:12,
      background:enabled ? 'rgba(11,27,53,.74)' : C.base,marginBottom:12}}>
      <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
        <input type="checkbox" checked={enabled} disabled={busy}
          onChange={(event)=>{
            if (event.target.checked && !writableCalendars.length) {
              void onRequestAccess();
              return;
            }
            onEnabledChange(event.target.checked);
          }} style={{marginTop:2,accentColor:C.accent}}/>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontSize:12,fontWeight:700,color:C.text}}>Também adicionar ao calendário do aparelho</div>
          <div style={{fontSize:10.5,color:C.muted,marginTop:3,lineHeight:1.45}}>
            A Athena pede permissão só quando você ativar esta opção e cria o vínculo após sua confirmação.
          </div>
        </div>
      </div>
      {!writableCalendars.length && (
        <button type="button" onClick={()=>{ void onRequestAccess(); }} disabled={busy} style={{marginTop:9,padding:'6px 9px',borderRadius:7,
          border:`1px solid ${C.accentD}`,background:C.accentB,color:C.accent,cursor:busy?'wait':'pointer',fontSize:10.5}}>
          Conectar calendário do aparelho
        </button>
      )}
      {enabled && writableCalendars.length > 0 && (
        <div style={{display:'grid',gap:7,marginTop:10}}>
          <select value={selectedCalendarId} onChange={(event)=>onCalendarChange(event.target.value)} disabled={busy}
            style={{width:'100%',boxSizing:'border-box',padding:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:11}}>
            <option value="">Escolha o calendário do aparelho</option>
            {writableCalendars.map((calendar)=><option key={calendar.deviceCalendarId} value={String(calendar.deviceCalendarId)}>{calendar.displayName || calendar.name || 'Calendário'}</option>)}
          </select>
          <label style={{fontSize:10.5,color:C.muted}}>Término no aparelho (obrigatório para sincronizar)
            <input type="datetime-local" value={endWhen} onChange={(event)=>onEndWhenChange(event.target.value)} disabled={busy}
              style={{marginTop:4,width:'100%',boxSizing:'border-box',padding:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontSize:11}}/>
          </label>
        </div>
      )}
    </section>
  );
}

function CalendarMobile({monthName,month,setMonth,activeDate,setActiveDate,days,events,calendars,selectedEvents,calendarColor,eventDay,isoDay,title,setTitle,selected,setSelected,when,setWhen,busy,createEvent,notice,deviceSyncControl}) {
  const upcoming=[...events].filter(event=>new Date(event.start_at)>=new Date()).sort((a,b)=>new Date(a.start_at)-new Date(b.start_at)).slice(0,10);
  const selectedLabel=new Intl.DateTimeFormat('pt-BR',{weekday:'long',day:'numeric',month:'long'}).format(activeDate);
  return <div className="workspace-panel" style={{height:'100%',overflowY:'auto',padding:'16px 14px 28px',animation:'fadeIn .15s ease'}}>
    <header style={{marginBottom:14}}><div style={{fontSize:19,fontWeight:700,color:C.text,textTransform:'capitalize'}}>Agenda · {monthName}</div><div style={{fontSize:12,color:C.muted,marginTop:4,lineHeight:1.45}}>Toque em um dia para ver compromissos ou agendar.</div></header>
    {notice&&<div style={{fontSize:12,color:'#ffb36a',marginBottom:10}}>{notice}</div>}
    {deviceSyncControl}
    <section style={{border:`1px solid ${C.border}`,borderRadius:16,background:'linear-gradient(145deg,#0c1728,#08101e)',overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 12px',borderBottom:`1px solid ${C.border}`}}><button onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()-1,1))} style={{width:34,height:34,borderRadius:9,border:`1px solid ${C.border}`,background:C.base,color:C.text,cursor:'pointer'}}>‹</button><button onClick={()=>{const today=new Date();setMonth(today);setActiveDate(today);}} style={{border:0,background:'transparent',color:C.accent,fontSize:12,fontWeight:700,cursor:'pointer'}}>Hoje</button><button onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()+1,1))} style={{width:34,height:34,borderRadius:9,border:`1px solid ${C.border}`,background:C.base,color:C.text,cursor:'pointer'}}>›</button></div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,minmax(0,1fr))',padding:'7px 5px 0'}}>{['D','S','T','Q','Q','S','S'].map((day,index)=><div key={`${day}-${index}`} style={{textAlign:'center',fontSize:10,fontWeight:700,color:C.dim,padding:'5px 0 8px'}}>{day}</div>)}{days.map(day=>{const sameMonth=day.getMonth()===month.getMonth();const active=isoDay(day)===isoDay(activeDate);const today=isoDay(day)===isoDay(new Date());const count=events.filter(event=>eventDay(event.start_at)===isoDay(day)).length;return <button key={day.toISOString()} onClick={()=>{setActiveDate(day);setWhen(`${isoDay(day)}T09:00`);}} style={{position:'relative',minHeight:52,padding:4,border:0,borderTop:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,background:active?'rgba(59,130,246,.16)':'transparent',color:sameMonth?C.text:C.dim,cursor:'pointer',textAlign:'center'}}><span style={{display:'inline-flex',width:26,height:26,borderRadius:9,alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:today?700:500,background:today?C.accent:'transparent',color:today?'#fff':'inherit',outline:active&&!today?`1px solid ${C.accent}`:'none'}}>{day.getDate()}</span>{count>0&&<span style={{position:'absolute',bottom:5,left:'50%',transform:'translateX(-50%)',width:5,height:5,borderRadius:'50%',background:calendarColor(events.find(event=>eventDay(event.start_at)===isoDay(day))?.calendar_id)}}/>}</button>})}</div>
    </section>
    <section style={{marginTop:12,padding:14,border:`1px solid ${C.border}`,borderRadius:14,background:C.base}}><div style={{fontSize:13,fontWeight:700,color:C.text,textTransform:'capitalize'}}>{selectedLabel}</div><div style={{marginTop:9,display:'grid',gap:7}}>{selectedEvents.length===0?<div style={{fontSize:12,color:C.muted}}>Sem compromissos neste dia.</div>:selectedEvents.map(event=><div key={event.id} style={{borderLeft:`3px solid ${calendarColor(event.calendar_id)}`,paddingLeft:8}}><div style={{fontSize:12,fontWeight:600,color:C.text}}>{event.title}</div><div style={{fontSize:10,color:C.muted,marginTop:2}}>{dateLabel(event.start_at)}</div></div>)}</div><div style={{marginTop:13,borderTop:`1px solid ${C.border}`,paddingTop:12}}><div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:8}}>Adicionar a este dia</div><input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Ex.: Reunião com cliente" style={{width:'100%',boxSizing:'border-box',padding:10,background:C.card,border:`1px solid ${C.border}`,borderRadius:9,color:C.text,marginBottom:8}}/><select value={selected} onChange={e=>setSelected(e.target.value)} style={{width:'100%',boxSizing:'border-box',padding:10,background:C.card,border:`1px solid ${C.border}`,borderRadius:9,color:C.text,marginBottom:8}}><option value="">Calendário</option>{calendars.map(calendar=><option key={calendar.id} value={calendar.id}>{calendar.name}</option>)}</select><input type="datetime-local" value={when} onChange={e=>setWhen(e.target.value)} style={{width:'100%',boxSizing:'border-box',padding:10,background:C.card,border:`1px solid ${C.border}`,borderRadius:9,color:C.text}}/><button onClick={createEvent} disabled={busy||!calendars.length} style={{marginTop:9,width:'100%',minHeight:43,padding:10,border:0,borderRadius:9,background:C.accent,color:'#fff',cursor:'pointer',opacity:busy||!calendars.length?.6:1}}>{busy?'Agendando…':'Agendar com lembrete'}</button></div></section>
    <section style={{marginTop:12,padding:14,border:`1px solid ${C.border}`,borderRadius:14,background:C.base}}><div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:8}}>Próximos eventos</div>{upcoming.length===0?<div style={{fontSize:12,color:C.muted}}>Nenhum evento futuro agendado.</div>:upcoming.map(event=><div key={event.id} style={{padding:'9px 0',borderTop:`1px solid ${C.border}`,borderLeft:`3px solid ${calendarColor(event.calendar_id)}`,paddingLeft:8}}><div style={{fontSize:12,color:C.text,fontWeight:600}}>{event.title}</div><div style={{fontSize:10,color:C.muted,marginTop:3}}>{dateLabel(event.start_at)}</div></div>)}</section>
  </div>;
}

function PanelCalendario({token, projects, onClose}) {
  const palette=['#3B82F6','#16C7B9','#B78BFA','#F59E0B','#F43F5E'];
  const [calendars,setCalendars]=useState([]); const [events,setEvents]=useState([]); const [month,setMonth]=useState(()=>new Date()); const [activeDate,setActiveDate]=useState(()=>new Date()); const [name,setName]=useState(''); const [title,setTitle]=useState(''); const [when,setWhen]=useState(''); const [selected,setSelected]=useState(''); const [busy,setBusy]=useState(false); const [notice,setNotice]=useState('');
  const [deviceCalendars,setDeviceCalendars]=useState([]); const [syncToDevice,setSyncToDevice]=useState(false); const [deviceCalendarId,setDeviceCalendarId]=useState(''); const [deviceEndWhen,setDeviceEndWhen]=useState(''); const [deviceBusy,setDeviceBusy]=useState(false);
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<768);
  useEffect(()=>{const update=()=>setIsMobile(window.innerWidth<768);window.addEventListener('resize',update);return()=>window.removeEventListener('resize',update);},[]);
  const monthStart=useMemo(()=>new Date(month.getFullYear(),month.getMonth(),1),[month]); const rangeStart=useMemo(()=>new Date(monthStart.getFullYear(),monthStart.getMonth(),1-monthStart.getDay()),[monthStart]); const rangeEnd=useMemo(()=>new Date(rangeStart.getFullYear(),rangeStart.getMonth(),rangeStart.getDate()+42),[rangeStart]);
  const load=useCallback(async()=>{try{const [c,e]=await Promise.all([axios.get(`${API_BASE}/calendars`,{headers:operationalHeaders(token)}),axios.get(`${API_BASE}/events?from=${encodeURIComponent(rangeStart.toISOString())}&to=${encodeURIComponent(rangeEnd.toISOString())}`,{headers:operationalHeaders(token)})]);setCalendars(c.data.calendars||[]);setEvents(e.data.events||[]);setSelected(current=>current||c.data.calendars?.[0]?.id||'');}catch{setNotice('Não foi possível carregar o calendário agora.');}},[token,rangeStart,rangeEnd]);
  useEffect(()=>{load();},[load]);
  const isoDay=date=>`${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`; const eventDay=value=>isoDay(new Date(value)); const days=Array.from({length:42},(_,i)=>new Date(rangeStart.getFullYear(),rangeStart.getMonth(),rangeStart.getDate()+i)); const selectedEvents=events.filter(event=>eventDay(event.start_at)===isoDay(activeDate)); const calendarColor=id=>calendars.find(calendar=>calendar.id===id)?.color||palette[Math.max(0,calendars.findIndex(calendar=>calendar.id===id))%palette.length];
  const createCalendar=async()=>{if(!name.trim()||busy)return;setBusy(true);try{await axios.post(`${API_BASE}/calendars`,{name:name.trim(),color:palette[calendars.length%palette.length],is_default:calendars.length===0},{headers:operationalHeaders(token)});setName('');await load();}catch{setNotice('Não consegui criar este calendário.');}finally{setBusy(false);}};
  const requestDeviceCalendar=useCallback(async()=>{if(deviceBusy)return;setDeviceBusy(true);try{const permission=await requestNativeCalendarAccess({write:true});if(!permission?.calendarWrite){setNotice(permission?.writePermission==='permanently_denied'?'A permissão do calendário foi bloqueada nas configurações do aparelho.':'Permita acesso ao calendário para escolher onde sincronizar.');return;}const result=await getNativeCalendars();const writable=(result?.calendars||[]).filter(calendar=>calendar.canWrite);setDeviceCalendars(writable);setDeviceCalendarId(current=>current||String(writable[0]?.deviceCalendarId||''));if(!writable.length){setNotice('Nenhum calendário gravável foi encontrado neste aparelho.');return;}setSyncToDevice(true);setNotice('Calendário do aparelho conectado. Escolha o destino e informe o horário de término.');}catch{setNotice('Não consegui acessar o calendário deste aparelho agora.');}finally{setDeviceBusy(false);}},[deviceBusy]);
  const createEvent=async()=>{if(!title.trim()||!when||!selected||busy)return;if(syncToDevice&&(!deviceCalendarId||!deviceEndWhen)){setNotice('Para sincronizar no aparelho, escolha o calendário e informe o horário de término.');return;}const startAt=localInputToIso(when);const endAt=syncToDevice?localInputToIso(deviceEndWhen):null;if(syncToDevice&&(!endAt||new Date(endAt)<=new Date(startAt))){setNotice('O término no calendário do aparelho precisa ser depois do início.');return;}setBusy(true);try{const {data}=await axios.post(`${API_BASE}/events`,{calendar_id:selected,title:title.trim(),start_at:startAt,end_at:endAt,timezone:Intl.DateTimeFormat().resolvedOptions().timeZone,reminder_minutes:defaultReminderMinutesFor(startAt)},{headers:operationalHeaders(token)});if(syncToDevice){let mappingAvailable=false;try{await axios.get(`${API_BASE}/events/${data.event?.id}/device-links`,{headers:operationalHeaders(token)});mappingAvailable=true;}catch{setNotice('Evento criado na Athena. A sincronização com o aparelho será liberada depois da ativação segura do servidor.');}if(mappingAvailable&&data.event?.id){const action=prepareAthenaAction({type:ATHENA_ACTION_TYPES.CREATE_CALENDAR_EVENT,summary:'Criar evento no calendário selecionado do aparelho',payload:{title:title.trim(),deviceCalendarId,startMs:new Date(startAt).getTime(),endMs:new Date(endAt).getTime(),timezone:Intl.DateTimeFormat().resolvedOptions().timeZone}});const nativeResult=await executeAthenaAction(action,{confirmationToken:`calendar:${data.event.id}`});if(nativeResult?.ok&&nativeResult?.created){try{await axios.post(`${API_BASE}/events/${data.event.id}/device-links`,{device_calendar_id:String(nativeResult.deviceCalendarId),device_event_id:String(nativeResult.deviceEventId),sync_state:'linked',last_synced_at:new Date().toISOString()},{headers:operationalHeaders(token)});setNotice('Evento criado e sincronizado com o calendário do aparelho.');}catch{setNotice('Evento criado no aparelho, mas o vínculo de sincronização ainda não pôde ser salvo.');}}else{setNotice('Evento criado na Athena, mas não foi possível criar a cópia no calendário do aparelho.');}}}setTitle('');setWhen('');setDeviceEndWhen('');await load();}catch{setNotice('Confira os dados do evento e tente novamente.');}finally{setBusy(false);}};
  const monthName=new Intl.DateTimeFormat('pt-BR',{month:'long',year:'numeric'}).format(month);
  const deviceSyncControl=<DeviceCalendarSyncControl enabled={syncToDevice} calendars={deviceCalendars} selectedCalendarId={deviceCalendarId} endWhen={deviceEndWhen} busy={busy||deviceBusy} onRequestAccess={requestDeviceCalendar} onEnabledChange={setSyncToDevice} onCalendarChange={setDeviceCalendarId} onEndWhenChange={setDeviceEndWhen}/>;
  if(isMobile)return <CalendarMobile monthName={monthName} month={month} setMonth={setMonth} activeDate={activeDate} setActiveDate={setActiveDate} days={days} events={events} calendars={calendars} selectedEvents={selectedEvents} calendarColor={calendarColor} eventDay={eventDay} isoDay={isoDay} title={title} setTitle={setTitle} selected={selected} setSelected={setSelected} when={when} setWhen={setWhen} busy={busy} createEvent={createEvent} notice={notice} deviceSyncControl={deviceSyncControl}/>;
  return <div style={{height:'100%',overflowY:'auto',padding:'22px 24px',animation:'fadeIn .15s ease'}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}><div><div style={{fontSize:19,fontWeight:600,color:C.text,textTransform:'capitalize'}}>Agenda · {monthName}</div><div style={{fontSize:12,color:C.muted,marginTop:4}}>Planeje seu mês, acompanhe compromissos e deixe a Athena lembrar você.</div></div><button onClick={onClose} style={{background:'none',border:0,color:C.muted,cursor:'pointer',fontSize:20}}>×</button></div>{notice&&<div style={{fontSize:12,color:'#ffb36a',marginBottom:10}}>{notice}</div>}<div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(260px,320px)',gap:16,alignItems:'start'}}><section style={{border:`1px solid ${C.border}`,borderRadius:16,background:'linear-gradient(145deg,#0c1728,#08101e)',overflow:'hidden'}}><div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 16px',borderBottom:`1px solid ${C.border}`}}><button onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()-1,1))} style={{width:31,height:31,borderRadius:8,border:`1px solid ${C.border}`,background:C.base,color:C.text,cursor:'pointer'}}>‹</button><button onClick={()=>{const today=new Date();setMonth(today);setActiveDate(today);}} style={{border:0,background:'transparent',color:C.accent,fontSize:12,fontWeight:600,cursor:'pointer'}}>Hoje</button><button onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()+1,1))} style={{width:31,height:31,borderRadius:8,border:`1px solid ${C.border}`,background:C.base,color:C.text,cursor:'pointer'}}>›</button></div><div style={{display:'grid',gridTemplateColumns:'repeat(7,minmax(0,1fr))',padding:'9px 8px 0'}}>{['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(day=><div key={day} style={{textAlign:'center',fontSize:10,fontWeight:700,letterSpacing:'.08em',color:C.dim,padding:'5px 0 9px'}}>{day}</div>)}{days.map(day=>{const sameMonth=day.getMonth()===month.getMonth();const active=isoDay(day)===isoDay(activeDate);const today=isoDay(day)===isoDay(new Date());const dayEvents=events.filter(event=>eventDay(event.start_at)===isoDay(day));return <button key={day.toISOString()} onClick={()=>{setActiveDate(day);setWhen(`${isoDay(day)}T09:00`);}} style={{minHeight:88,padding:'8px 6px',border:0,borderTop:`1px solid ${C.border}`,borderRight:`1px solid ${C.border}`,background:active?'rgba(59,130,246,.12)':'transparent',color:sameMonth?C.text:C.dim,cursor:'pointer',textAlign:'left',overflow:'hidden'}}><span style={{display:'inline-flex',width:25,height:25,borderRadius:8,alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:today?700:500,background:today?C.accent:'transparent',color:today?'#fff':'inherit',outline:active&&!today?`1px solid ${C.accent}`:'none'}}>{day.getDate()}</span><div style={{marginTop:5,display:'grid',gap:3}}>{dayEvents.slice(0,2).map(event=><span key={event.id} title={event.title} style={{display:'block',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',borderLeft:`3px solid ${calendarColor(event.calendar_id)}`,paddingLeft:4,fontSize:9,color:C.soft,lineHeight:1.35}}>{event.title}</span>)}{dayEvents.length>2&&<span style={{fontSize:9,color:C.muted}}>+{dayEvents.length-2} mais</span>}</div></button>})}</div></section><aside style={{display:'grid',gap:12}}><div style={{padding:14,border:`1px solid ${C.border}`,borderRadius:14,background:C.base}}><div style={{fontSize:13,fontWeight:600,color:C.text,textTransform:'capitalize'}}>{new Intl.DateTimeFormat('pt-BR',{weekday:'long',day:'numeric',month:'long'}).format(activeDate)}</div><div style={{marginTop:10,display:'grid',gap:7}}>{selectedEvents.length===0?<div style={{fontSize:12,color:C.muted}}>Sem compromissos neste dia.</div>:selectedEvents.map(event=><div key={event.id} style={{borderLeft:`3px solid ${calendarColor(event.calendar_id)}`,paddingLeft:8}}><div style={{fontSize:12,fontWeight:600,color:C.text}}>{event.title}</div><div style={{fontSize:10,color:C.muted,marginTop:2}}>{dateLabel(event.start_at)}</div></div>)}</div></div><div style={{padding:14,border:`1px solid ${C.border}`,borderRadius:14,background:C.base}}><div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:9}}>Adicionar ao dia</div><input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Ex.: Reunião com cliente" style={{width:'100%',boxSizing:'border-box',padding:9,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,marginBottom:7}}/><select value={selected} onChange={e=>setSelected(e.target.value)} style={{width:'100%',boxSizing:'border-box',padding:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,marginBottom:7}}><option value="">Calendário</option>{calendars.map(calendar=><option key={calendar.id} value={calendar.id}>{calendar.name}</option>)}</select><input type="datetime-local" value={when} onChange={e=>setWhen(e.target.value)} style={{width:'100%',boxSizing:'border-box',padding:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}/><button onClick={createEvent} disabled={busy||!calendars.length} style={{marginTop:8,width:'100%',padding:9,border:0,borderRadius:8,background:C.accent,color:'#fff',cursor:'pointer',opacity:busy||!calendars.length?.6:1}}>{busy?'Agendando…':'Agendar com lembrete'}</button></div><div style={{padding:14,border:`1px solid ${C.border}`,borderRadius:14,background:C.base}}><div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:8}}>Meus calendários</div>{calendars.map((calendar,index)=><div key={calendar.id} style={{display:'flex',alignItems:'center',gap:7,padding:'4px 0',fontSize:11,color:C.muted}}><span style={{width:8,height:8,borderRadius:'50%',background:calendar.color||palette[index%palette.length]}}/>{calendar.name}</div>)}<div style={{display:'flex',gap:6,marginTop:9}}><input value={name} onChange={e=>setName(e.target.value)} placeholder="Novo calendário" style={{minWidth:0,flex:1,padding:7,background:C.card,border:`1px solid ${C.border}`,borderRadius:7,color:C.text,fontSize:11}}/><button onClick={createCalendar} disabled={busy} style={{padding:'7px 9px',border:0,borderRadius:7,background:C.accent,color:'#fff',cursor:'pointer'}}>+</button></div></div></aside></div></div>;
}

function PanelTarefas({token, projects, onClose}) {
  const [tasks,setTasks]=useState([]); const [title,setTitle]=useState(''); const [due,setDue]=useState(''); const [priority,setPriority]=useState('normal'); const [projectId,setProjectId]=useState(''); const [busy,setBusy]=useState(false);
  const load=useCallback(()=>axios.get(`${API_BASE}/tasks`,{headers:operationalHeaders(token)}).then(({data})=>setTasks(data.tasks||[])).catch(()=>setTasks([])),[token]); useEffect(()=>{load();},[load]);
  const create=async()=>{if(!title.trim()||busy)return;setBusy(true);try{const dueAt=localInputToIso(due);await axios.post(`${API_BASE}/tasks`,{title:title.trim(),due_at:dueAt,priority,project_id:projectId||null,timezone:Intl.DateTimeFormat().resolvedOptions().timeZone,reminder_minutes:dueAt?reminderMinutesForTask(dueAt):[]},{headers:operationalHeaders(token)});setTitle('');setDue('');setPriority('normal');await load();}finally{setBusy(false);}}; const setStatus=async(task,status)=>{await axios.patch(`${API_BASE}/tasks/${task.id}`,{status},{headers:operationalHeaders(token)});load();};
  return <div className="workspace-panel" style={{height:'100%',overflowY:'auto',padding:20,animation:'fadeIn .15s ease'}}><div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}><div><div style={{fontSize:16,fontWeight:600,color:C.text}}>Tarefas</div><div style={{fontSize:11,color:C.muted,marginTop:3}}>Organize o que precisa ser feito sem perder o contexto.</div></div><button onClick={onClose} style={{background:'none',border:0,color:C.muted,cursor:'pointer',fontSize:20}}>×</button></div><div style={{padding:14,border:`1px solid ${C.border}`,borderRadius:12,background:C.base,marginBottom:14}}><input value={title} onChange={e=>setTitle(e.target.value)} placeholder="O que precisa ser feito?" style={{width:'100%',boxSizing:'border-box',padding:9,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,marginBottom:8}}/><div className="workspace-task-controls" style={{display:'flex',gap:8,flexWrap:'wrap'}}><select value={priority} onChange={e=>setPriority(e.target.value)} style={{padding:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}><option value="low">Baixa</option><option value="normal">Normal</option><option value="high">Alta</option><option value="urgent">Urgente</option></select><select value={projectId} onChange={e=>setProjectId(e.target.value)} style={{flex:1,minWidth:130,padding:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}><option value="">Sem projeto</option>{projects.map(p=><option key={p.project_id} value={p.project_id}>{p.name}</option>)}</select><input type="datetime-local" value={due} onChange={e=>setDue(e.target.value)} style={{padding:8,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}/><button onClick={create} disabled={busy} style={{padding:'8px 11px',border:0,borderRadius:8,background:C.accent,color:'#fff',cursor:'pointer'}}>Adicionar</button></div></div>{tasks.length===0?<div style={{fontSize:12,color:C.muted}}>Nenhuma tarefa por enquanto.</div>:tasks.map(task=><div key={task.id} style={{display:'flex',gap:10,alignItems:'center',padding:11,marginBottom:7,border:`1px solid ${C.border}`,borderRadius:10,background:C.base}}><button onClick={()=>setStatus(task,task.status==='completed'?'pending':'completed')} title="Concluir tarefa" style={{width:18,height:18,borderRadius:'50%',border:`1px solid ${task.status==='completed'?C.accent:C.muted}`,background:task.status==='completed'?C.accent:'transparent',cursor:'pointer',flexShrink:0}}/><div style={{flex:1,minWidth:0}}><div style={{color:C.text,fontSize:13,textDecoration:task.status==='completed'?'line-through':'none',opacity:task.status==='completed'?.55:1}}>{task.title}</div><div style={{fontSize:10,color:C.muted,marginTop:3}}>{task.priority} · {dateLabel(task.due_at)}</div></div>{task.status!=='completed'&&<button onClick={()=>setStatus(task,'in_progress')} style={{border:0,background:'transparent',color:C.accent,fontSize:11,cursor:'pointer'}}>Iniciar</button>}</div>)}</div>;
}

function PanelTarefasOperacional({token, projects, onClose}) {
  const [tasks,setTasks]=useState([]); const [view,setView]=useState('today'); const [showCreate,setShowCreate]=useState(false); const [title,setTitle]=useState(''); const [description,setDescription]=useState(''); const [due,setDue]=useState(''); const [priority,setPriority]=useState('normal'); const [projectId,setProjectId]=useState(''); const [busy,setBusy]=useState(false);
  const load=useCallback(()=>axios.get(`${API_BASE}/tasks`,{headers:operationalHeaders(token)}).then(({data})=>setTasks(data.tasks||[])).catch(()=>setTasks([])),[token]); useEffect(()=>{load();},[load]);
  const todayKey=new Date().toDateString(); const open=tasks.filter(task=>['pending','in_progress'].includes(task.status)); const completed=tasks.filter(task=>task.status==='completed'); const overdue=open.filter(task=>task.due_at&&new Date(task.due_at)<new Date()&&new Date(task.due_at).toDateString()!==todayKey); const today=open.filter(task=>task.due_at&&new Date(task.due_at).toDateString()===todayKey); const inbox=open.filter(task=>!task.due_at); const upcoming=open.filter(task=>task.due_at&&new Date(task.due_at)>new Date()&&new Date(task.due_at).toDateString()!==todayKey);
  const groups={today,upcoming,inbox,completed,all:open}; const visible=groups[view]||open;
  const create=async()=>{if(!title.trim()||busy)return;setBusy(true);try{const dueAt=localInputToIso(due);await axios.post(`${API_BASE}/tasks`,{title:title.trim(),description:description.trim(),due_at:dueAt,priority,project_id:projectId||null,timezone:Intl.DateTimeFormat().resolvedOptions().timeZone,reminder_minutes:dueAt?reminderMinutesForTask(dueAt):[]},{headers:operationalHeaders(token)});setTitle('');setDescription('');setDue('');setPriority('normal');setProjectId('');setShowCreate(false);await load();}finally{setBusy(false);}};
  const update=async(task,status)=>{await axios.patch(`${API_BASE}/tasks/${task.id}`,{status},{headers:operationalHeaders(token)});await load();}; const remove=async task=>{if(!window.confirm(`Remover “${task.title}”?`))return;await axios.delete(`${API_BASE}/tasks/${task.id}`,{headers:operationalHeaders(token)});await load();};
  const priorityColor={urgent:'#fb7185',high:'#f59e0b',normal:C.accent,low:'#22c55e'}; const tabs=[['today','Hoje',today.length],['upcoming','Próximas',upcoming.length],['inbox','Entrada',inbox.length],['all','Em execução',open.length],['completed','Concluídas',completed.length]];
  const TaskCard=({task})=>{const done=task.status==='completed';return <article style={{display:'grid',gridTemplateColumns:'auto minmax(0,1fr) auto',gap:11,alignItems:'start',padding:'13px 14px',border:`1px solid ${C.border}`,borderRadius:12,background:C.base,opacity:done?.62:1}}><button onClick={()=>update(task,done?'pending':'completed')} title={done?'Reabrir':'Concluir'} style={{marginTop:2,width:20,height:20,borderRadius:'50%',border:`1.5px solid ${done?C.accent:priorityColor[task.priority]||C.muted}`,background:done?C.accent:'transparent',color:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12}}>{done?'✓':''}</button><div style={{minWidth:0}}><div style={{fontSize:13,fontWeight:600,color:C.text,textDecoration:done?'line-through':'none'}}>{task.title}</div>{task.description&&<div style={{fontSize:11,color:C.muted,lineHeight:1.45,marginTop:4}}>{task.description}</div>}<div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:9}}><span style={{fontSize:10,padding:'3px 7px',borderRadius:20,background:`${priorityColor[task.priority]||C.accent}18`,color:priorityColor[task.priority]||C.accent}}>{({urgent:'Urgente',high:'Alta',normal:'Normal',low:'Baixa'})[task.priority]||'Normal'}</span><span style={{fontSize:10,color:C.muted,padding:'3px 0'}}>{task.due_at?dateLabel(task.due_at):'Sem prazo'}</span>{task.status==='in_progress'&&<span style={{fontSize:10,color:'#16c7b9',padding:'3px 0'}}>Em andamento</span>}</div></div><div style={{display:'flex',gap:4}}>{!done&&task.status!=='in_progress'&&<button onClick={()=>update(task,'in_progress')} title="Iniciar" style={{border:0,background:'transparent',color:C.accent,cursor:'pointer',fontSize:11}}>Iniciar</button>}<button onClick={()=>remove(task)} title="Remover" style={{border:0,background:'transparent',color:C.muted,cursor:'pointer',fontSize:16}}>⋮</button></div></article>;};
  return <div className="workspace-panel" style={{height:'100%',overflowY:'auto',padding:'22px 24px 34px',animation:'fadeIn .15s ease'}}><header style={{display:'flex',justifyContent:'space-between',gap:16,alignItems:'start',marginBottom:18}}><div><div style={{fontSize:21,fontWeight:700,color:C.text}}>Meu ritmo de trabalho</div><div style={{fontSize:12,color:C.muted,marginTop:5}}>Organize o próximo passo e execute sem perder o contexto.</div></div><button onClick={()=>setShowCreate(value=>!value)} style={{padding:'10px 13px',border:0,borderRadius:9,background:C.accent,color:'#fff',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>+ Nova tarefa</button></header>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(145px,1fr))',gap:10,marginBottom:16}}>{[[today.length,'Para hoje',C.accent],[overdue.length,'Em atraso','#fb7185'],[open.length,'Em aberto','#16c7b9'],[completed.length,'Concluídas','#a78bfa']].map(([value,label,color])=><div key={label} style={{padding:'13px 14px',borderRadius:12,border:`1px solid ${C.border}`,background:C.base}}><div style={{fontSize:22,fontWeight:700,color}}>{value}</div><div style={{fontSize:11,color:C.muted,marginTop:3}}>{label}</div></div>)}</div>
    {showCreate&&<section style={{padding:14,border:`1px solid ${C.accentD}`,borderRadius:13,background:C.base,marginBottom:16}}><div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:10}}>Planejar uma nova tarefa</div><input value={title} onChange={e=>setTitle(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')create();}} placeholder="Qual é o próximo passo?" style={{width:'100%',boxSizing:'border-box',padding:10,background:C.card,border:`1px solid ${C.border}`,borderRadius:9,color:C.text,marginBottom:8}}/><textarea value={description} onChange={e=>setDescription(e.target.value)} placeholder="Detalhes ou critério de conclusão (opcional)" style={{width:'100%',minHeight:58,boxSizing:'border-box',padding:10,background:C.card,border:`1px solid ${C.border}`,borderRadius:9,color:C.text,resize:'vertical',marginBottom:8}}/><div className="workspace-task-controls" style={{display:'flex',gap:8,flexWrap:'wrap'}}><select value={priority} onChange={e=>setPriority(e.target.value)} style={{padding:9,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}><option value="low">Baixa</option><option value="normal">Normal</option><option value="high">Alta</option><option value="urgent">Urgente</option></select><select value={projectId} onChange={e=>setProjectId(e.target.value)} style={{flex:1,minWidth:170,padding:9,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}><option value="">Sem projeto</option>{projects.map(project=><option key={project.project_id} value={project.project_id}>{project.name}</option>)}</select><input type="datetime-local" value={due} onChange={e=>setDue(e.target.value)} style={{padding:9,background:C.card,border:`1px solid ${C.border}`,borderRadius:8,color:C.text}}/><button onClick={create} disabled={busy||!title.trim()} style={{padding:'9px 14px',border:0,borderRadius:8,background:C.accent,color:'#fff',cursor:'pointer'}}>{busy?'Salvando…':'Adicionar'}</button></div></section>}
    <nav style={{display:'flex',gap:7,overflowX:'auto',paddingBottom:10,marginBottom:8}}>{tabs.map(([id,label,count])=><button key={id} onClick={()=>setView(id)} style={{flexShrink:0,padding:'7px 10px',borderRadius:20,border:`1px solid ${view===id?C.accentD:C.border}`,background:view===id?C.accentB:'transparent',color:view===id?C.accent:C.muted,cursor:'pointer',fontSize:11,fontWeight:view===id?700:500}}>{label} <span style={{opacity:.8}}>({count})</span></button>)}</nav>
    <div className="workspace-tasks-layout" style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(245px,300px)',gap:14,alignItems:'start'}}><section style={{padding:14,border:`1px solid ${C.border}`,borderRadius:14,background:'linear-gradient(145deg,#0c1728,#08101e)'}}><div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:11}}><div><div style={{fontSize:13,fontWeight:700,color:C.text}}>{({today:'Plano de hoje',upcoming:'Próximos passos',inbox:'Caixa de entrada',all:'Em execução',completed:'Concluídas'})[view]}</div><div style={{fontSize:10,color:C.muted,marginTop:2}}>{visible.length} item{visible.length===1?'':'ns'} nesta visão</div></div><button onClick={()=>setShowCreate(true)} style={{border:0,background:C.accentB,color:C.accent,borderRadius:8,padding:'7px 9px',cursor:'pointer',fontSize:11}}>+ Adicionar</button></div><div style={{display:'grid',gap:8}}>{visible.length===0?<div style={{padding:'35px 16px',border:`1px dashed ${C.border}`,borderRadius:13,textAlign:'center',color:C.muted,fontSize:12}}>{view==='today'?'Seu dia está livre. Adicione uma prioridade para começar.':'Nenhuma tarefa nesta visão.'}</div>:visible.map(task=><TaskCard key={task.id} task={task}/>)}</div></section><aside style={{display:'grid',gap:12}}><section style={{padding:15,border:`1px solid ${C.accentD}`,borderRadius:14,background:C.base}}><div style={{fontSize:11,color:C.accent,fontWeight:700,letterSpacing:'.06em'}}>FOCO AGORA</div>{(overdue[0]||today[0]||open[0])?<><div style={{fontSize:14,fontWeight:700,color:C.text,marginTop:9,lineHeight:1.35}}>{(overdue[0]||today[0]||open[0]).title}</div><div style={{fontSize:11,color:C.muted,marginTop:5}}>{overdue.length?'Há pendência em atraso: comece por ela.':today.length?'Uma prioridade de hoje para mover agora.':'Escolha um próximo passo e comece.'}</div><button onClick={()=>update((overdue[0]||today[0]||open[0]),'in_progress')} style={{marginTop:12,width:'100%',padding:9,border:0,borderRadius:8,background:C.accent,color:'#fff',cursor:'pointer'}}>Começar foco</button></>:<div style={{fontSize:12,color:C.muted,marginTop:8,lineHeight:1.5}}>Adicione uma tarefa para a Athena ajudar a organizar seu ritmo.</div>}</section><section style={{padding:15,border:`1px solid ${C.border}`,borderRadius:14,background:C.base}}><div style={{fontSize:12,fontWeight:700,color:C.text}}>Progresso da semana</div><div style={{display:'flex',alignItems:'end',gap:10,marginTop:13}}><div style={{fontSize:28,fontWeight:750,color:C.text}}>{tasks.length?Math.round((completed.length/tasks.length)*100):0}%</div><div style={{fontSize:11,color:C.muted,paddingBottom:4}}>das tarefas concluídas</div></div><div style={{height:7,borderRadius:20,background:C.card,overflow:'hidden',marginTop:10}}><div style={{height:'100%',width:`${tasks.length?(completed.length/tasks.length)*100:0}%`,background:'linear-gradient(90deg,#3B82F6,#16c7b9)',borderRadius:20}}/></div><div style={{fontSize:11,color:C.muted,marginTop:9}}>{completed.length} concluída{completed.length===1?'':'s'} · {open.length} em aberto</div></section><section style={{padding:15,border:`1px solid ${C.border}`,borderRadius:14,background:C.base}}><div style={{fontSize:12,fontWeight:700,color:C.text}}>Organização</div><div style={{display:'grid',gap:8,marginTop:11}}>{[['Entrada',inbox.length,'Sem prazo definido'],['Hoje',today.length,'Para executar hoje'],['Atrasadas',overdue.length,'Pedem atenção']].map(([label,count,detail])=><button key={label} onClick={()=>setView(label==='Entrada'?'inbox':label==='Hoje'?'today':'all')} style={{display:'flex',justifyContent:'space-between',gap:10,textAlign:'left',padding:'8px 0',border:0,borderTop:`1px solid ${C.border}`,background:'transparent',cursor:'pointer'}}><span><span style={{display:'block',fontSize:12,color:C.text,fontWeight:600}}>{label}</span><span style={{display:'block',fontSize:10,color:C.muted,marginTop:2}}>{detail}</span></span><span style={{fontSize:14,fontWeight:700,color:label==='Atrasadas'&&count?'#fb7185':C.accent}}>{count}</span></button>)}</div></section></aside></div>
  </div>;
}

function PanelWorkspaceHome({projects, sessions, onOpenChat, onClose}) {
  const recent=sessions[0];
  return <div style={{height:'100%',overflowY:'auto',padding:'28px 24px',animation:'fadeIn .15s ease'}}>
    <div style={{fontSize:19,fontWeight:600,color:C.text}}>Seu trabalho com Athena</div><div style={{fontSize:12,color:C.muted,marginTop:6}}>Contexto, conversas e projetos reunidos em um só lugar.</div>
    {recent&&<button onClick={()=>onOpenChat(recent.session_id)} style={{display:'block',textAlign:'left',marginTop:22,width:'100%',padding:14,borderRadius:11,border:`1px solid ${C.accentD}`,background:C.base,color:C.text,cursor:'pointer'}}><div style={{fontSize:11,color:C.accent,marginBottom:5}}>RETOMAR</div><div style={{fontWeight:600}}>{recent.preview||recent.title||'Conversa recente'}</div></button>}
    <div style={{marginTop:22,fontSize:11,color:C.dim,letterSpacing:'.08em'}}>PROJETOS RECENTES</div>
    {projects.length===0?<div style={{fontSize:12,color:C.muted,marginTop:9}}>Nenhum projeto ainda.</div>:projects.slice(0,4).map(p=><div key={p.project_id} style={{padding:'10px 0',borderBottom:`1px solid ${C.border}`,color:C.text}}>{p.name}</div>)}
    <button onClick={onClose} style={{marginTop:22,padding:'8px 12px',borderRadius:8,border:`1px solid ${C.border}`,background:C.card,color:C.text,cursor:'pointer'}}>Abrir conversa com Athena</button>
  </div>;
}

// ─── Panel: Memórias ──────────────────────────────────────────────────────────
function MemoryVortex() {
  return <div className="memory-vortex-frame" aria-label="Núcleo de memória da Athena" role="img">
    <video className="memory-vortex-video" autoPlay loop muted playsInline preload="metadata" aria-hidden="true">
      <source src="/athena-assets/memories/athena-memory-tree-alpha.webm" type="video/webm"/>
    </video>
  </div>;
}

function PanelMemorias({token, onClose}) {
  const [search, setSearch]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast]     = useState('');

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2000); };

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_BASE}/memory/list`, {headers:{Authorization:`Bearer ${token}`},withCredentials:true})
      .then(({data}) => mounted && setResults(data.memories || []))
      .catch(() => mounted && showToast('Não consegui carregar suas memórias agora.'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = search
    ? results.filter(m=>m.content.toLowerCase().includes(search.toLowerCase()))
    : results;
  const remove = async id => {
    try {
      await axios.delete(`${API_BASE}/memory/${id}`, {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setResults(prev => prev.filter(memory => memory.id !== id));
      showToast('Memória removida.');
    } catch { showToast('Não consegui remover esta memória agora.'); }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',animation:'fadeIn .15s ease'}}>
      {toast && <Toast msg={toast}/>}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>Memórias</span>
        <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
      </div>
      <div style={{padding:'10px 16px'}}>
        <div style={{position:'relative'}}>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Busca inteligente — ex: Cliente Fermaq"
            style={{width:'100%',padding:'8px 10px 8px 32px',background:C.base,
              border:`1px solid ${C.border}`,borderRadius:8,color:C.text,
              fontSize:12,outline:'none',boxSizing:'border-box'}}
            onFocus={e=>e.target.style.borderColor=`${C.accent}60`}
            onBlur={e=>e.target.style.borderColor=C.border}/>
          <div style={{position:'absolute',top:'50%',left:10,transform:'translateY(-50%)',color:C.dim,pointerEvents:'none'}}>
            <Ico d={D.search} size={13}/>
          </div>
        </div>
      </div>
      <div style={{flex:1,minHeight:0,overflowY:'auto',padding:filtered.length?'4px 16px 24px':0}}>
        {loading ? <div style={{padding:'28px 0',textAlign:'center',color:C.dim,fontSize:12}}>Carregando memórias…</div>
        : filtered.length===0 ? <section className="memory-vortex-stage"><MemoryVortex/></section>
        : <div style={{paddingTop:14}}>{filtered.map(m=>(
            <div key={m.id} style={{padding:'10px 12px',borderRadius:9,marginBottom:8,
              background:C.base,border:`1px solid ${C.border}`}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
                <p style={{fontSize:12,color:C.muted,lineHeight:1.6,margin:0,flex:1}}>{m.content}</p>
              </div>
              <div style={{display:'flex',gap:6,marginTop:8}}>
                <button onClick={()=>remove(m.id)} style={{
                    padding:'3px 8px',borderRadius:6,fontSize:10,cursor:'pointer',
                    background:'#2a1015',border:'1px solid #3d1515',color:'#ef4444',
                  }}>Excluir</button>
              </div>
            </div>
          ))}</div>}
      </div>
    </div>
  );
}

// ─── Panel: Ferramentas ───────────────────────────────────────────────────────
function PanelFerramentas({onClose}) {
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',animation:'fadeIn .15s ease'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>Ferramentas</span>
        <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'14px 16px'}}>
        {TOOLS_DATA.map(t=>(
          <div key={t.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',
            borderRadius:9,marginBottom:6,background:C.base,border:`0.5px solid ${C.border}`}}>
            <span style={{fontSize:18,flexShrink:0}}>{t.icon}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:12,fontWeight:500,color:'#c8ccd8'}}>{t.name}</div>
              <div style={{fontSize:11,color:C.dim}}>{t.desc}</div>
            </div>
            <div style={{
              padding:'2px 8px',borderRadius:20,fontSize:10,
              background:t.status==='Ativo'?C.accentB:'#1a1c30',
              border:`1px solid ${t.status==='Ativo'?C.accentD:C.border}`,
              color:t.status==='Ativo'?C.accent:C.muted,
            }}>{t.status}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Biblioteca: categorias fixas + sugestão visual (usuário escolhe manualmente) ──
const LIBRARY_META = {
  projects:  {label:'Projetos',  icon:'📁'},
  files:     {label:'Arquivos',  icon:'📄'},
  recipes:   {label:'Receitas',  icon:'🍽'},
  work:      {label:'Trabalho',  icon:'💼'},
  favorites: {label:'Favoritos', icon:'⭐'},
};

// ─── Pastas personalizadas: paleta e ícones controlados da ATHENA ─────────────
const LIBRARY_FOLDER_COLORS = {
  amber:  '#F2A900',
  blue:   '#4A9EDE',
  green:  '#4ADE80',
  purple: '#A78BFA',
  pink:   '#F472B6',
  gray:   '#8A8A93',
};
const LIBRARY_FOLDER_ICONS = ['📁','🏋️','📚','⛪','🎯','🎨','🎵','💰','🚗','🏠','🐾','✈️'];

function suggestLibraryCategory(presentationType, renderer) {
  if (renderer === 'recipe' || presentationType === 'receita') return 'recipes';
  if (renderer === 'plan' || ['plano', 'projeto'].includes(presentationType)) return 'work';
  if (['documento', 'relatorio', 'tabela'].includes(presentationType)) return 'files';
  return null;
}

// ─── Salvar na Biblioteca — seletor real (Modal + categorias + contagem real) ──
function LibrarySavePicker({item, token, onClose, showToast, onSaved}) {
  const [counts, setCounts] = useState(null);
  const [folders, setFolders] = useState(null);
  const [saving, setSaving] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_BASE}/library/counts`, {headers:{Authorization:`Bearer ${token}`}, withCredentials:true})
      .then(({data}) => { if (alive) setCounts(data.counts || {}); })
      .catch(() => { if (alive) setCounts({}); });
    axios.get(`${API_BASE}/library/folders`, {headers:{Authorization:`Bearer ${token}`}, withCredentials:true})
      .then(({data}) => { if (alive) setFolders(data.folders || []); })
      .catch(() => { if (alive) setFolders([]); });
    return () => { alive = false; };
  }, [token]);

  const suggested = suggestLibraryCategory(item?.presentation_type, item?.renderer);

  async function handlePick(catId) {
    if (saving) return;
    setSaving(catId);
    try {
      const {data} = await axios.post(`${API_BASE}/library/items`,
        {...item, category: catId},
        {headers:{Authorization:`Bearer ${token}`}, withCredentials:true});
      if (!data?.item?.id) throw new Error('LIBRARY_ITEM_NOT_CONFIRMED');
      const label = LIBRARY_META[catId].label;
      showToast(data.already_existed ? `Já estava em ${label}.` : `Salvo em ${label}.`);
      onSaved && onSaved(catId);
      onClose();
    } catch {
      showToast('Não consegui salvar. Tenta de novo.');
      setSaving(null);
    }
  }

  async function handlePickFolder(folder) {
    if (saving) return;
    setSaving(folder.id);
    try {
      const {data} = await axios.post(`${API_BASE}/library/items`,
        {...item, folder_id: folder.id},
        {headers:{Authorization:`Bearer ${token}`}, withCredentials:true});
      if (!data?.item?.id) throw new Error('LIBRARY_ITEM_NOT_CONFIRMED');
      showToast(data.already_existed ? `Já estava em ${folder.name}.` : `Salvo em ${folder.name}.`);
      onSaved && onSaved(folder.id);
      onClose();
    } catch {
      showToast('Não consegui salvar. Tenta de novo.');
      setSaving(null);
    }
  }

  async function handleCreateFolder(payload) {
    const {data} = await axios.post(`${API_BASE}/library/folders`, payload,
      {headers:{Authorization:`Bearer ${token}`}, withCredentials:true});
    setFolders(prev => [...(prev || []), data.folder]);
    setShowNewFolder(false);
  }

  return (
    <Modal onClose={onClose} width={380}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>Salvar na Biblioteca</span>
        <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
      </div>
      <div style={{padding:'14px 16px',maxHeight:'60vh',overflowY:'auto'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(140px,1fr))',gap:10}}>
          {Object.entries(LIBRARY_META).map(([id, meta]) => {
            const isSuggested = id === suggested;
            const isSaving = saving === id;
            return (
              <button key={id} onClick={()=>handlePick(id)} disabled={!!saving} style={{
                display:'flex',flexDirection:'column',alignItems:'flex-start',gap:8,
                padding:'14px',borderRadius:12,cursor:saving?'default':'pointer',textAlign:'left',
                background:C.base,border:`1px solid ${isSuggested?C.accentD:C.border}`,
                opacity:(saving && !isSaving) ? 0.5 : 1,transition:'all .15s',
              }}
                onMouseEnter={e=>{if(!saving){e.currentTarget.style.borderColor=C.accentD;e.currentTarget.style.background=C.card;}}}
                onMouseLeave={e=>{if(!saving){e.currentTarget.style.borderColor=isSuggested?C.accentD:C.border;e.currentTarget.style.background=C.base;}}}>
                <span style={{fontSize:22}}>{isSaving ? '⏳' : meta.icon}</span>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{meta.label}</div>
                <div style={{fontSize:11,color:C.muted}}>
                  {counts===null ? '…' : `${counts[id]??0} ${counts[id]===1?'item':'itens'}`}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{fontSize:11,color:C.dim,textTransform:'uppercase',letterSpacing:'.08em',fontWeight:600,margin:'18px 0 10px'}}>Minhas pastas</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(140px,1fr))',gap:10}}>
          {(folders || []).map(folder => {
            const isSaving = saving === folder.id;
            return (
              <button key={folder.id} onClick={()=>handlePickFolder(folder)} disabled={!!saving} style={{
                display:'flex',flexDirection:'column',alignItems:'flex-start',gap:8,
                padding:'14px',borderRadius:12,cursor:saving?'default':'pointer',textAlign:'left',
                background:C.base,border:`1px solid ${C.border}`,
                opacity:(saving && !isSaving) ? 0.5 : 1,transition:'all .15s',
              }}
                onMouseEnter={e=>{if(!saving){e.currentTarget.style.borderColor=C.accentD;e.currentTarget.style.background=C.card;}}}
                onMouseLeave={e=>{if(!saving){e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.base;}}}>
                <span style={{fontSize:22}}>{isSaving ? '⏳' : folder.icon}</span>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{folder.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{folder.item_count} {folder.item_count===1?'item':'itens'}</div>
              </button>
            );
          })}
          <button onClick={()=>setShowNewFolder(true)} disabled={!!saving} style={{
            display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:6,minHeight:80,
            padding:'14px',borderRadius:12,cursor:saving?'default':'pointer',
            background:'none',border:`1px dashed ${C.border}`,color:C.muted,fontSize:12,fontWeight:500,
          }}>+ Criar nova pasta</button>
        </div>
      </div>
      {showNewFolder && (
        <NewFolderModal onClose={()=>setShowNewFolder(false)} onCreate={handleCreateFolder}/>
      )}
    </Modal>
  );
}

// ─── Biblioteca: mensagens de estado vazio por categoria ───────────────────────
const LIBRARY_EMPTY_MESSAGES = {
  projects:  ['Você ainda não salvou nenhum projeto.',  'Salve planos e conteúdos de projeto no chat e clique em Salvar na Biblioteca.'],
  files:     ['Nenhum arquivo salvo em Arquivos.',        'Documentos e conteúdos salvos aparecem aqui.'],
  recipes:   ['Você ainda não salvou nenhuma receita.',   'Crie uma receita no chat e clique em Salvar na Biblioteca.'],
  work:      ['Nenhum conteúdo salvo em Trabalho.',       'Salve relatórios, planos e propostas para encontrá-los aqui.'],
  favorites: ['Você ainda não tem favoritos.',             'Marque qualquer item salvo como favorito para vê-lo aqui.'],
};

// ─── Card compacto — reaproveitado nas 5 categorias ────────────────────────────
function LibraryItemCard({item, onOpen, onToggleFavorite, onRename, onMove, onDelete, menuOpen, onToggleMenu}) {
  const badges = [];
  if (item.metadata?.time) badges.push(`⏱ ${item.metadata.time}`);
  if (item.metadata?.difficulty) badges.push(`📊 ${item.metadata.difficulty}`);
  if (item.metadata?.servings) badges.push(`🍽 ${item.metadata.servings}`);
  const savedDate = item.saved_at ? new Date(item.saved_at).toLocaleDateString('pt-BR') : '';
  const preview = item.summary || (item.content || '').replace(/[#*_`]/g, '').slice(0, 90);
  const catMeta = LIBRARY_META[item.category];

  return (
    <div style={{position:'relative',background:C.base,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden',display:'flex',flexDirection:'column'}}>
      <div onClick={onOpen} style={{cursor:'pointer'}}>
        {item.image_url && (
          <div style={{height:110,background:`url(${item.image_url}) center/cover`,backgroundColor:C.card}}/>
        )}
        <div style={{padding:'12px 12px 10px'}}>
          <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.title}</div>
          {preview && (
            <div style={{fontSize:11.5,color:C.muted,lineHeight:1.4,marginBottom:6,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{preview}</div>
          )}
          {badges.length > 0 && (
            <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:6}}>
              {badges.map((b,i)=>(<span key={i} style={{fontSize:10,color:C.muted,background:C.card,border:`1px solid ${C.border}`,borderRadius:20,padding:'2px 7px'}}>{b}</span>))}
            </div>
          )}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontSize:10,color:C.dim}}>{savedDate}</span>
            {catMeta && <span style={{fontSize:10,color:C.dim}}>{catMeta.icon} {catMeta.label}</span>}
          </div>
        </div>
      </div>
      <div style={{position:'absolute',top:8,right:8,display:'flex',gap:4}}>
        <button onClick={e=>{e.stopPropagation();onToggleFavorite();}} title={item.is_favorite?'Remover dos favoritos':'Favoritar'}
          style={{background:'rgba(0,0,0,0.45)',border:'none',borderRadius:'50%',width:26,height:26,cursor:'pointer',color:item.is_favorite?'#facc15':'#cbd5e1',fontSize:13}}>
          {item.is_favorite ? '★' : '☆'}
        </button>
        <button onClick={e=>{e.stopPropagation();onToggleMenu();}}
          style={{background:'rgba(0,0,0,0.45)',border:'none',borderRadius:'50%',width:26,height:26,cursor:'pointer',color:'#cbd5e1',fontSize:13}}>
          ⋮
        </button>
      </div>
      {menuOpen && (
        <div onClick={e=>e.stopPropagation()} style={{
          position:'absolute',top:38,right:8,background:C.card,border:`1px solid ${C.border}`,
          borderRadius:10,boxShadow:'0 8px 24px rgba(0,0,0,.5)',overflow:'hidden',zIndex:5,minWidth:170,
        }}>
          {[
            {label:'Abrir', fn:onOpen},
            {label:'Renomear', fn:onRename},
            {label:'Mover', fn:onMove},
            {label:item.is_favorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos', fn:onToggleFavorite},
            {label:'Excluir', fn:onDelete, danger:true},
          ].map(opt=>(
            <button key={opt.label} onClick={()=>{onToggleMenu();opt.fn();}} style={{
              display:'block',width:'100%',textAlign:'left',padding:'8px 12px',fontSize:12,
              background:'none',border:'none',cursor:'pointer',color:opt.danger?'#f87171':C.text,
            }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.06)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='none';}}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Modais compactos: renomear / mover / excluir (reaproveitam Modal) ─────────
function LibraryRenamePrompt({item, onClose, onConfirm}) {
  const [title, setTitle] = useState(item.title || '');
  return (
    <Modal onClose={onClose} width={340}>
      <div style={{padding:'18px 20px'}}>
        <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:12}}>Renomear item</div>
        <input autoFocus value={title} onChange={e=>setTitle(e.target.value)}
          style={{width:'100%',boxSizing:'border-box',background:C.base,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px',fontSize:13,color:C.text,outline:'none',marginBottom:14}}/>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{padding:'7px 14px',borderRadius:8,border:`1px solid ${C.border}`,background:'none',color:C.muted,cursor:'pointer',fontSize:12}}>Cancelar</button>
          <button onClick={()=>title.trim() && onConfirm(title.trim())} style={{padding:'7px 14px',borderRadius:8,border:'none',background:C.accent,color:'#04121f',cursor:'pointer',fontSize:12,fontWeight:600}}>Salvar</button>
        </div>
      </div>
    </Modal>
  );
}

function LibraryMoveTo({item, folders, currentFolderId, onClose, onConfirm}) {
  return (
    <Modal onClose={onClose} width={340}>
      <div style={{padding:'18px 20px'}}>
        <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:12}}>Mover para…</div>
        <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:360,overflowY:'auto'}}>
          {Object.entries(LIBRARY_META).filter(([id])=>id!==item.category).map(([id, meta])=>(
            <button key={id} onClick={()=>onConfirm({type:'category', id})} style={{
              display:'flex',alignItems:'center',gap:8,padding:'9px 12px',borderRadius:8,
              background:C.base,border:`1px solid ${C.border}`,color:C.text,cursor:'pointer',fontSize:13,textAlign:'left',
            }}>
              <span>{meta.icon}</span><span>{meta.label}</span>
            </button>
          ))}
          {(folders || []).filter(f=>f.id !== currentFolderId).length > 0 && (
            <div style={{fontSize:10,color:C.dim,textTransform:'uppercase',letterSpacing:'.08em',marginTop:8,marginBottom:2}}>Minhas pastas</div>
          )}
          {(folders || []).filter(f=>f.id !== currentFolderId).map(f=>(
            <button key={f.id} onClick={()=>onConfirm({type:'folder', id:f.id})} style={{
              display:'flex',alignItems:'center',gap:8,padding:'9px 12px',borderRadius:8,
              background:C.base,border:`1px solid ${C.border}`,color:C.text,cursor:'pointer',fontSize:13,textAlign:'left',
            }}>
              <span>{f.icon}</span><span>{f.name}</span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function LibraryDeleteConfirm({item, onClose, onConfirm}) {
  return (
    <Modal onClose={onClose} width={340}>
      <div style={{padding:'18px 20px'}}>
        <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:8}}>Excluir "{item.title}"?</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Essa ação não pode ser desfeita.</div>
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onClose} style={{padding:'7px 14px',borderRadius:8,border:`1px solid ${C.border}`,background:'none',color:C.muted,cursor:'pointer',fontSize:12}}>Cancelar</button>
          <button onClick={onConfirm} style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#ef4444',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:600}}>Excluir</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Pastas personalizadas: criar / renomear / excluir (modais) ───────────────
function NewFolderModal({onClose, onCreate}) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(LIBRARY_FOLDER_ICONS[0]);
  const [color, setColor] = useState('amber');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleCreate() {
    if (saving || !name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await onCreate({name: name.trim(), icon, color});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Não foi possível criar a pasta.');
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} width={340}>
      <div style={{padding:'18px 20px'}}>
        <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:14}}>Nova pasta</div>
        <div style={{fontSize:11,color:C.muted,marginBottom:6}}>Nome da pasta</div>
        <input autoFocus value={name} onChange={e=>setName(e.target.value)} maxLength={40}
          placeholder="Ex: Treinos"
          onKeyDown={e=>{if(e.key==='Enter') handleCreate();}}
          style={{width:'100%',boxSizing:'border-box',background:C.base,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px',fontSize:13,color:C.text,outline:'none',marginBottom:14}}/>
        <div style={{fontSize:11,color:C.muted,marginBottom:6}}>Ícone</div>
        <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:14}}>
          {LIBRARY_FOLDER_ICONS.map(ic=>(
            <button key={ic} onClick={()=>setIcon(ic)} type="button" style={{
              width:30,height:30,borderRadius:8,cursor:'pointer',fontSize:15,
              background:icon===ic?C.card:C.base,border:`1px solid ${icon===ic?C.accentD:C.border}`,
            }}>{ic}</button>
          ))}
        </div>
        <div style={{fontSize:11,color:C.muted,marginBottom:6}}>Cor</div>
        <div style={{display:'flex',gap:8,marginBottom:16}}>
          {Object.entries(LIBRARY_FOLDER_COLORS).map(([id,hex])=>(
            <button key={id} onClick={()=>setColor(id)} type="button" title={id} style={{
              width:24,height:24,borderRadius:'50%',cursor:'pointer',background:hex,
              border:color===id?`2px solid ${C.text}`:'2px solid transparent',
            }}/>
          ))}
        </div>
        {error && <div style={{fontSize:11.5,color:'#f87171',marginBottom:10}}>{error}</div>}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onClose} disabled={saving} style={{padding:'7px 14px',borderRadius:8,border:`1px solid ${C.border}`,background:'none',color:C.muted,cursor:'pointer',fontSize:12}}>Cancelar</button>
          <button onClick={handleCreate} disabled={saving || !name.trim()} style={{padding:'7px 14px',borderRadius:8,border:'none',background:C.accent,color:'#04121f',cursor:saving?'default':'pointer',fontSize:12,fontWeight:600,opacity:(saving||!name.trim())?0.6:1}}>
            {saving ? 'Criando…' : 'Criar pasta'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RenameFolderModal({folder, onClose, onConfirm}) {
  const [name, setName] = useState(folder.name || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (saving || !name.trim()) return;
    setSaving(true);
    setError('');
    try {
      await onConfirm(name.trim());
    } catch (e) {
      setError(e?.response?.data?.detail || 'Não foi possível renomear.');
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} width={340}>
      <div style={{padding:'18px 20px'}}>
        <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:12}}>Renomear pasta</div>
        <input autoFocus value={name} onChange={e=>setName(e.target.value)} maxLength={40}
          onKeyDown={e=>{if(e.key==='Enter') handleSave();}}
          style={{width:'100%',boxSizing:'border-box',background:C.base,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 10px',fontSize:13,color:C.text,outline:'none',marginBottom:10}}/>
        {error && <div style={{fontSize:11.5,color:'#f87171',marginBottom:10}}>{error}</div>}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onClose} disabled={saving} style={{padding:'7px 14px',borderRadius:8,border:`1px solid ${C.border}`,background:'none',color:C.muted,cursor:'pointer',fontSize:12}}>Cancelar</button>
          <button onClick={handleSave} disabled={saving || !name.trim()} style={{padding:'7px 14px',borderRadius:8,border:'none',background:C.accent,color:'#04121f',cursor:'pointer',fontSize:12,fontWeight:600,opacity:saving?0.6:1}}>Salvar</button>
        </div>
      </div>
    </Modal>
  );
}

function DeleteFolderModal({folder, otherFolders, onClose, onConfirm}) {
  const hasItems = (folder.item_count || 0) > 0;
  const [action, setAction] = useState('unassign');
  const [targetId, setTargetId] = useState(otherFolders[0]?.id || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleConfirm() {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await onConfirm(hasItems ? action : 'unassign', action === 'move' ? targetId : undefined);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Não foi possível excluir a pasta.');
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} width={380}>
      <div style={{padding:'18px 20px'}}>
        <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:8}}>Excluir pasta "{folder.name}"?</div>
        {hasItems ? (
          <>
            <div style={{fontSize:12,color:C.muted,marginBottom:14}}>
              Esta pasta contém {folder.item_count} {folder.item_count===1?'item':'itens'}. Escolha o que fazer:
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:14}}>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5,color:C.text,cursor:'pointer'}}>
                <input type="radio" checked={action==='unassign'} onChange={()=>setAction('unassign')}/>
                Mover para Arquivos (Biblioteca geral)
              </label>
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5,color:C.text,cursor:otherFolders.length?'pointer':'not-allowed',opacity:otherFolders.length?1:0.5}}>
                <input type="radio" checked={action==='move'} onChange={()=>setAction('move')} disabled={!otherFolders.length}/>
                Mover para outra pasta
              </label>
              {action==='move' && (
                <select value={targetId} onChange={e=>setTargetId(e.target.value)}
                  style={{marginLeft:24,background:C.base,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 8px',fontSize:12,color:C.text}}>
                  {otherFolders.map(f=>(<option key={f.id} value={f.id}>{f.icon} {f.name}</option>))}
                </select>
              )}
              <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12.5,color:'#f87171',cursor:'pointer'}}>
                <input type="radio" checked={action==='delete'} onChange={()=>setAction('delete')}/>
                Excluir também os itens
              </label>
            </div>
          </>
        ) : (
          <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Essa pasta está vazia. Essa ação não pode ser desfeita.</div>
        )}
        {error && <div style={{fontSize:11.5,color:'#f87171',marginBottom:10}}>{error}</div>}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button onClick={onClose} disabled={saving} style={{padding:'7px 14px',borderRadius:8,border:`1px solid ${C.border}`,background:'none',color:C.muted,cursor:'pointer',fontSize:12}}>Cancelar</button>
          <button onClick={handleConfirm} disabled={saving || (action==='move' && !targetId)} style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#ef4444',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:600,opacity:saving?0.6:1}}>Excluir</button>
        </div>
      </div>
    </Modal>
  );
}

function FolderCard({folder, onOpen, onRename, onDelete, menuOpen, onToggleMenu}) {
  const hex = LIBRARY_FOLDER_COLORS[folder.color] || LIBRARY_FOLDER_COLORS.amber;
  return (
    <div style={{position:'relative'}}>
      <button onClick={onOpen} style={{
        display:'flex',flexDirection:'column',alignItems:'flex-start',gap:8,width:'100%',boxSizing:'border-box',
        padding:'14px',borderRadius:12,cursor:'pointer',textAlign:'left',
        background:C.base,border:`1px solid ${C.border}`,borderLeft:`3px solid ${hex}`,transition:'all .15s',
      }}
        onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accentD;e.currentTarget.style.borderLeftColor=hex;e.currentTarget.style.background=C.card;}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.borderLeftColor=hex;e.currentTarget.style.background=C.base;}}>
        <span style={{fontSize:22}}>{folder.icon}</span>
        <div style={{fontSize:13,fontWeight:600,color:C.text,maxWidth:'100%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{folder.name}</div>
        <div style={{fontSize:11,color:C.muted}}>{folder.item_count} {folder.item_count===1?'item':'itens'}</div>
      </button>
      <button onClick={e=>{e.stopPropagation();onToggleMenu();}}
        style={{position:'absolute',top:8,right:8,background:'rgba(0,0,0,0.35)',border:'none',borderRadius:'50%',width:24,height:24,cursor:'pointer',color:C.muted,fontSize:13}}>⋮</button>
      {menuOpen && (
        <div onClick={e=>e.stopPropagation()} style={{
          position:'absolute',top:34,right:8,background:C.card,border:`1px solid ${C.border}`,
          borderRadius:10,boxShadow:'0 8px 24px rgba(0,0,0,.5)',overflow:'hidden',zIndex:5,minWidth:140,
        }}>
          {[{label:'Renomear', fn:onRename}, {label:'Excluir', fn:onDelete, danger:true}].map(opt=>(
            <button key={opt.label} onClick={()=>{onToggleMenu();opt.fn();}} style={{
              display:'block',width:'100%',textAlign:'left',padding:'8px 12px',fontSize:12,
              background:'none',border:'none',cursor:'pointer',color:opt.danger?'#f87171':C.text,
            }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.06)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='none';}}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Nível 2: lista de itens de uma categoria ──────────────────────────────────
function LibraryCategoryList({category, folderId, folderMeta, folders, token, showToast, onBack, onOpenItem, onCountsChanged}) {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('recent');
  const [menuFor, setMenuFor] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  const [moveItem, setMoveItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const load = useCallback(() => {
    const qs = folderId ? `folder_id=${folderId}` : (category === 'favorites' ? 'favorite=true' : `category=${category}`);
    axios.get(`${API_BASE}/library/items?${qs}`, {headers:{Authorization:`Bearer ${token}`}, withCredentials:true})
      .then(({data}) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [category, folderId, token]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = items || [];
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(i => (i.title || '').toLowerCase().includes(q));
    }
    list = [...list];
    if (sort === 'title') list.sort((a,b) => (a.title||'').localeCompare(b.title||''));
    else if (sort === 'oldest') list.sort((a,b) => (a.saved_at||'').localeCompare(b.saved_at||''));
    else list.sort((a,b) => (b.saved_at||'').localeCompare(a.saved_at||''));
    return list;
  }, [items, query, sort]);

  async function patchItem(id, patch) {
    const {data} = await axios.patch(`${API_BASE}/library/items/${id}`, patch,
      {headers:{Authorization:`Bearer ${token}`}, withCredentials:true});
    setItems(prev => prev.map(i => i.id === id ? data.item : i));
    onCountsChanged && onCountsChanged();
    return data.item;
  }

  async function toggleFavorite(item) {
    const next = !item.is_favorite;
    await patchItem(item.id, {is_favorite: next});
    if (category === 'favorites' && !next) {
      setItems(prev => prev.filter(i => i.id !== item.id));
    }
  }

  async function moveItemTo(item, target) {
    const patch = target.type === 'folder' ? {folder_id: target.id} : {folder_id: '', category: target.id};
    await patchItem(item.id, patch);
    const staysHere = target.type === 'folder' ? (folderId && target.id === folderId) : (!folderId && target.id === category);
    if (!staysHere) {
      setItems(prev => prev.filter(i => i.id !== item.id));
    }
  }

  async function removeItem(id) {
    await axios.delete(`${API_BASE}/library/items/${id}`, {headers:{Authorization:`Bearer ${token}`}, withCredentials:true});
    setItems(prev => prev.filter(i => i.id !== id));
    onCountsChanged && onCountsChanged();
  }

  const metaIcon = folderId ? folderMeta?.icon : LIBRARY_META[category]?.icon;
  const metaLabel = folderId ? folderMeta?.name : LIBRARY_META[category]?.label;
  const [emptyTitle, emptyBody] = folderId
    ? [`Nenhum item em ${folderMeta?.name || 'pasta'} ainda.`, 'Salve conteúdos do chat nessa pasta pra vê-los aqui.']
    : (LIBRARY_EMPTY_MESSAGES[category] || ['Nada por aqui ainda.', '']);

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',animation:'fadeIn .15s ease'}}>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <button onClick={onBack} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:16}}>←</button>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>{metaIcon} {metaLabel}</span>
        <span style={{fontSize:11,color:C.muted,marginLeft:'auto'}}>{items ? `${filtered.length} ${filtered.length===1?'item':'itens'}` : '…'}</span>
      </div>
      <div style={{display:'flex',gap:8,padding:'10px 16px'}}>
        <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Buscar por título…"
          style={{flex:1,minWidth:0,background:C.base,border:`1px solid ${C.border}`,borderRadius:8,padding:'7px 10px',fontSize:12,color:C.text,outline:'none'}}/>
        <select value={sort} onChange={e=>setSort(e.target.value)}
          style={{background:C.base,border:`1px solid ${C.border}`,borderRadius:8,padding:'7px 8px',fontSize:12,color:C.text,outline:'none'}}>
          <option value="recent">Mais recentes</option>
          <option value="oldest">Mais antigos</option>
          <option value="title">Título</option>
        </select>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'6px 16px 16px'}}>
        {items === null ? (
          <div style={{textAlign:'center',color:C.muted,fontSize:12,padding:'40px 0'}}>Carregando…</div>
        ) : filtered.length === 0 ? (
          <div style={{textAlign:'center',padding:'40px 16px'}}>
            <div style={{fontSize:28,marginBottom:8}}>{metaIcon}</div>
            <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:4}}>{emptyTitle}</div>
            <div style={{fontSize:12,color:C.muted}}>{emptyBody}</div>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(220px,1fr))',gap:12}}>
            {filtered.map(item => (
              <LibraryItemCard
                key={item.id} item={item}
                onOpen={()=>onOpenItem(item)}
                onToggleFavorite={()=>toggleFavorite(item)}
                onRename={()=>setRenameItem(item)}
                onMove={()=>setMoveItem(item)}
                onDelete={()=>setDeleteItem(item)}
                menuOpen={menuFor===item.id}
                onToggleMenu={()=>setMenuFor(prev=>prev===item.id?null:item.id)}
              />
            ))}
          </div>
        )}
      </div>
      {renameItem && (
        <LibraryRenamePrompt item={renameItem} onClose={()=>setRenameItem(null)}
          onConfirm={async title=>{ await patchItem(renameItem.id,{title}); setRenameItem(null); showToast('Renomeado.'); }}/>
      )}
      {moveItem && (
        <LibraryMoveTo item={moveItem} folders={folders} currentFolderId={folderId} onClose={()=>setMoveItem(null)}
          onConfirm={async target=>{
            await moveItemTo(moveItem, target);
            setMoveItem(null);
            const label = target.type === 'folder' ? (folders || []).find(f=>f.id===target.id)?.name : LIBRARY_META[target.id].label;
            showToast(`Movido para ${label}.`);
          }}/>
      )}
      {deleteItem && (
        <LibraryDeleteConfirm item={deleteItem} onClose={()=>setDeleteItem(null)}
          onConfirm={async()=>{ await removeItem(deleteItem.id); setDeleteItem(null); showToast('Item removido.'); }}/>
      )}
    </div>
  );
}

// ─── Nível 3: abrir item salvo — reaproveita o mesmo dispatcher do chat ────────
function LibraryItemDetail({item, onBack, cognitiveProfile, folderMeta}) {
  const meta = folderMeta ? {icon:folderMeta.icon, label:folderMeta.name} : LIBRARY_META[item.category];
  const fileUrl = item?.metadata?.download_url;
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',animation:'fadeIn .15s ease'}}>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <button onClick={onBack} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:16}}>←</button>
        <span style={{fontSize:13,color:C.muted}}>{meta?.icon} {meta?.label}</span>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'16px 20px'}}>
        {fileUrl ? (
          <>
            <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:10}}>{item.title}</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:18}}>{item.summary || 'Arquivo salvo na Biblioteca.'}</div>
            <a href={`${API_BASE}${fileUrl}`} target="_blank" rel="noreferrer" style={{display:'inline-flex',padding:'9px 13px',borderRadius:9,background:C.accent,color:'#04121f',fontSize:12,fontWeight:700,textDecoration:'none'}}>Baixar arquivo</a>
          </>
        ) : hasPremiumRenderer(item.metadata) ? (
          <PresentationRenderer
            wp={item.metadata}
            content={item.content}
            AthenaPresentationEngine={AthenaPresentationEngine}
            cognitiveProfile={cognitiveProfile}
            onAction={()=>{}}
          />
        ) : (
          <>
            <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:10}}>{item.title}</div>
            <AthenaPresentationEngine content={item.content || item.summary || ''} cognitiveProfile={cognitiveProfile}/>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Panel: Biblioteca — categorias → lista → detalhe ──────────────────────────
function PanelBiblioteca({onClose, showToast, token, cognitiveProfile}) {
  const [counts, setCounts] = useState(null);
  const [folders, setFolders] = useState(null);
  const [view, setView] = useState({mode:'categories'});
  const [menuFor, setMenuFor] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [renameFolder, setRenameFolder] = useState(null);
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef(null);

  const loadCounts = useCallback(() => {
    axios.get(`${API_BASE}/library/counts`, {headers:{Authorization:`Bearer ${token}`}, withCredentials:true})
      .then(({data}) => setCounts(data.counts || {}))
      .catch(() => setCounts({}));
  }, [token]);
  const loadFolders = useCallback(() => {
    axios.get(`${API_BASE}/library/folders`, {headers:{Authorization:`Bearer ${token}`}, withCredentials:true})
      .then(({data}) => setFolders(data.folders || []))
      .catch(() => setFolders([]));
  }, [token]);
  useEffect(() => { loadCounts(); loadFolders(); }, [loadCounts, loadFolders]);

  async function handleCreateFolder(payload) {
    const {data} = await axios.post(`${API_BASE}/library/folders`, payload,
      {headers:{Authorization:`Bearer ${token}`}, withCredentials:true});
    setFolders(prev => [...(prev || []), data.folder]);
    setShowNewFolder(false);
    showToast(`Pasta "${data.folder.name}" criada.`);
  }

  async function handleRenameFolder(folder, name) {
    const {data} = await axios.patch(`${API_BASE}/library/folders/${folder.id}`, {name},
      {headers:{Authorization:`Bearer ${token}`}, withCredentials:true});
    setFolders(prev => prev.map(f => f.id === folder.id ? data.folder : f));
    setRenameFolder(null);
    showToast('Pasta renomeada.');
  }

  async function handleDeleteFolder(folder, itemAction, targetFolderId) {
    await axios.delete(`${API_BASE}/library/folders/${folder.id}`, {
      headers:{Authorization:`Bearer ${token}`}, withCredentials:true,
      data: {item_action: itemAction, target_folder_id: targetFolderId},
    });
    setFolders(prev => prev.filter(f => f.id !== folder.id));
    setDeleteFolder(null);
    loadCounts();
    showToast('Pasta excluída.');
  }

  async function handleUpload(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || uploading) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API_BASE}/files`, form, {headers:{Authorization:`Bearer ${token}`}, withCredentials:true});
      loadCounts();
      showToast(`“${file.name}” foi salvo na Biblioteca.`);
    } catch (error) {
      showToast(error?.response?.data?.error || 'Não consegui enviar este arquivo.');
    } finally { setUploading(false); }
  }

  if (view.mode === 'detail') {
    return (
      <LibraryItemDetail item={view.item} cognitiveProfile={cognitiveProfile} folderMeta={view.folder}
        onBack={()=>setView(view.folder ? {mode:'folder', folder:view.folder} : {mode:'list', category:view.item.category})}/>
    );
  }
  if (view.mode === 'list') {
    return (
      <LibraryCategoryList category={view.category} folders={folders || []} token={token} showToast={showToast}
        onBack={()=>setView({mode:'categories'})}
        onOpenItem={item=>setView({mode:'detail', item})}
        onCountsChanged={()=>{loadCounts();loadFolders();}}/>
    );
  }
  if (view.mode === 'folder') {
    return (
      <LibraryCategoryList folderId={view.folder.id} folderMeta={view.folder} folders={folders || []} token={token} showToast={showToast}
        onBack={()=>setView({mode:'categories'})}
        onOpenItem={item=>setView({mode:'detail', item, folder:view.folder})}
        onCountsChanged={()=>{loadCounts();loadFolders();}}/>
    );
  }

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',animation:'fadeIn .15s ease'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px 12px',borderBottom:`1px solid ${C.border}`}}>
        <span style={{fontSize:15,fontWeight:600,color:C.text}}>Biblioteca</span>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <input ref={uploadRef} type="file" accept=".txt,.md,.json,.csv,text/plain,text/markdown,application/json,text/csv" onChange={handleUpload} style={{display:'none'}}/>
          <button onClick={()=>uploadRef.current?.click()} disabled={uploading} style={{display:'flex',alignItems:'center',gap:4,padding:'6px 10px',borderRadius:8,background:C.accent,border:'none',color:'#04121f',cursor:uploading?'wait':'pointer',fontSize:12,fontWeight:700}}>{uploading?'Enviando…':'↑ Enviar arquivo'}</button>
          <button onClick={()=>setShowNewFolder(true)} style={{
            display:'flex',alignItems:'center',gap:4,padding:'6px 10px',borderRadius:8,
            background:C.card,border:`1px solid ${C.border}`,color:C.text,cursor:'pointer',fontSize:12,fontWeight:500,
          }}>+ Nova pasta</button>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18}}>×</button>
        </div>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'14px 16px'}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(150px,1fr))',gap:10}}>
          {Object.entries(LIBRARY_META).map(([id, meta])=>(
            <button key={id} onClick={()=>setView({mode:'list', category:id})} style={{
              display:'flex',flexDirection:'column',alignItems:'flex-start',gap:8,
              padding:'14px',borderRadius:12,cursor:'pointer',textAlign:'left',
              background:C.base,border:`1px solid ${C.border}`,transition:'all .15s',
            }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accentD;e.currentTarget.style.background=C.card;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.base;}}>
              <span style={{fontSize:22}}>{meta.icon}</span>
              <div style={{fontSize:13,fontWeight:600,color:C.text}}>{meta.label}</div>
              <div style={{fontSize:11,color:C.muted}}>{counts?.[id] ?? 0} {counts?.[id]===1?'item':'itens'}</div>
            </button>
          ))}
        </div>

        <div style={{fontSize:11,color:C.dim,textTransform:'uppercase',letterSpacing:'.08em',fontWeight:600,margin:'22px 0 10px'}}>Minhas pastas</div>
        {folders === null ? (
          <div style={{textAlign:'center',color:C.muted,fontSize:12,padding:'20px 0'}}>Carregando…</div>
        ) : folders.length === 0 ? (
          <div style={{textAlign:'center',padding:'24px 16px',background:C.base,border:`1px solid ${C.border}`,borderRadius:12}}>
            <div style={{fontSize:12,color:C.muted,marginBottom:10}}>Você ainda não criou nenhuma pasta.</div>
            <button onClick={()=>setShowNewFolder(true)} style={{padding:'7px 14px',borderRadius:8,border:'none',background:C.accent,color:'#04121f',cursor:'pointer',fontSize:12,fontWeight:600}}>+ Criar pasta</button>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(150px,1fr))',gap:10}}>
            {folders.map(folder=>(
              <FolderCard key={folder.id} folder={folder}
                onOpen={()=>setView({mode:'folder', folder})}
                onRename={()=>setRenameFolder(folder)}
                onDelete={()=>setDeleteFolder(folder)}
                menuOpen={menuFor===folder.id}
                onToggleMenu={()=>setMenuFor(prev=>prev===folder.id?null:folder.id)}/>
            ))}
          </div>
        )}
      </div>
      {showNewFolder && (
        <NewFolderModal onClose={()=>setShowNewFolder(false)} onCreate={handleCreateFolder}/>
      )}
      {renameFolder && (
        <RenameFolderModal folder={renameFolder} onClose={()=>setRenameFolder(null)}
          onConfirm={name=>handleRenameFolder(renameFolder, name)}/>
      )}
      {deleteFolder && (
        <DeleteFolderModal folder={deleteFolder} otherFolders={(folders||[]).filter(f=>f.id!==deleteFolder.id)}
          onClose={()=>setDeleteFolder(null)}
          onConfirm={(action, targetId)=>handleDeleteFolder(deleteFolder, action, targetId)}/>
      )}
    </div>
  );
}

// ─── Panel: Personalizar — helpers ───────────────────────────────────────────
function PSettingSection({label, children}) {
  return (
    <div style={{marginBottom:20}}>
      <div style={{fontSize:10,color:C.dim,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:8,fontWeight:500,paddingLeft:2}}>{label}</div>
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:'0 14px'}}>
        {children}
      </div>
    </div>
  );
}

function PSettingRow({icon, label, sub, arrow, danger, toggle, toggled, onToggle, onClick}) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:12,padding:'11px 0',
      borderBottom:`1px solid ${C.border}40`,cursor:onClick||toggle?'pointer':'default'}}
      onClick={toggle ? onToggle : onClick}>
      {icon && <span style={{fontSize:17,width:24,textAlign:'center',flexShrink:0}}>{icon}</span>}
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12.5,color:danger?'#ef4444':C.text,fontWeight:400}}>{label}</div>
        {sub && <div style={{fontSize:10.5,color:C.dim,marginTop:1}}>{sub}</div>}
      </div>
      {arrow && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.dim} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>}
      {toggle && (
        <div style={{width:36,height:20,borderRadius:20,position:'relative',flexShrink:0,
          background:toggled?C.accentD:C.border,border:`1px solid ${toggled?C.accent:C.border}`,transition:'all .2s'}}>
          <div style={{position:'absolute',top:2,left:toggled?17:2,width:14,height:14,borderRadius:'50%',
            background:toggled?C.accent:C.soft,transition:'all .2s'}}/>
        </div>
      )}
    </div>
  );
}

// ─── Panel: Personalizar (legacy — não usado) ─────────────────────────────────
function _LegacyPanelPersonalizar({onClose, user, onLogout}) {
  const username  = user?.name  || 'Vitor';
  const userEmail = user?.email || '';

  const CARD = '#111827';

  // Inline SVG icon helper
  const PSvg = ({d, size=18, color='#3B82F6'}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={d}/>
    </svg>
  );

  const ITEMS = [
    { id:'perfil',   label:'Perfil',                      d:'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z' },
    { id:'conta',    label:'Conta',                       d:'M17 21v-2a4 4 0 00-3-3.87M9 11a4 4 0 100-8 4 4 0 000 8zm-4 7.5v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75' },
    { id:'memoria',  label:'Memória',                     d:'M12 2a7 7 0 015.29 11.55c-.58.69-1.29 1.27-1.29 2.2V17a2 2 0 01-2 2h-4a2 2 0 01-2-2v-1.25c0-.93-.71-1.51-1.29-2.2A7 7 0 0112 2zm-2 17h4' },
    { id:'intel',    label:'Inteligência personalizada',  d:'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' },
    { id:'apps',     label:'Aplicativos conectados',      d:'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z' },
    { id:'perms',    label:'Permissões',                  d:'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4' },
    { id:'config',   label:'Configurações do aplicativo', d:'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06-.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z' },
    { id:'collab',   label:'Conectores de colaboração',   d:'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm14 2l-4 4-2-2' },
  ];

  const chevronD = 'M9 18l6-6-6-6';

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',background:'#0d1120',animation:'fadeIn .15s ease'}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'18px 20px 14px',flexShrink:0}}>
        <span style={{fontSize:16,fontWeight:600,color:'rgba(255,255,255,0.92)',letterSpacing:'-0.01em'}}>
          Personalização
        </span>
        <button onClick={onClose} style={{
          width:30,height:30,borderRadius:'50%',background:'rgba(255,255,255,0.08)',border:'none',
          cursor:'pointer',color:'rgba(255,255,255,0.5)',fontSize:17,lineHeight:1,
          display:'flex',alignItems:'center',justifyContent:'center',
        }}>×</button>
      </div>

      <div style={{flex:1,overflowY:'auto',padding:'0 14px 36px'}}>

        {/* Profile card */}
        <div style={{background:CARD,borderRadius:18,padding:'18px 16px',marginBottom:14,position:'relative'}}>
          <button style={{
            position:'absolute',top:14,right:14,width:30,height:30,borderRadius:9,
            background:'rgba(255,255,255,0.07)',border:'none',cursor:'pointer',
            color:'rgba(255,255,255,0.4)',fontSize:13,
            display:'flex',alignItems:'center',justifyContent:'center',
          }}>✏</button>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{
              width:76,height:76,borderRadius:'50%',flexShrink:0,
              background:'linear-gradient(135deg, #60a5fa 0%, #3B82F6 55%, #1d4ed8 100%)',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:28,fontWeight:700,color:'#fff',
              boxShadow:'0 0 24px rgba(59,130,246,0.32)',
            }}>
              {username.slice(0,1).toUpperCase()}
            </div>
            <div>
              <div style={{fontSize:17,fontWeight:600,color:'rgba(255,255,255,0.95)',marginBottom:6}}>
                {username}
              </div>
              {userEmail && (
                <div style={{display:'flex',alignItems:'center',gap:5,color:'rgba(255,255,255,0.36)',fontSize:12}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  {userEmail}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Settings rows */}
        <div style={{display:'flex',flexDirection:'column',gap:9}}>
          {ITEMS.map(item => (
            <button key={item.id} style={{
              display:'flex',alignItems:'center',gap:14,
              padding:'0 14px',height:60,width:'100%',
              background:CARD,borderRadius:15,border:'none',cursor:'pointer',textAlign:'left',
              transition:'background .15s',
            }}
              onMouseEnter={e=>e.currentTarget.style.background='#1a2438'}
              onMouseLeave={e=>e.currentTarget.style.background=CARD}
            >
              <div style={{
                width:38,height:38,borderRadius:11,flexShrink:0,
                background:'rgba(59,130,246,0.14)',
                display:'flex',alignItems:'center',justifyContent:'center',
              }}>
                <PSvg d={item.d}/>
              </div>
              <span style={{flex:1,fontSize:14,color:'rgba(255,255,255,0.84)',fontWeight:400}}>{item.label}</span>
              <PSvg d={chevronD} size={16} color='rgba(255,255,255,0.22)'/>
            </button>
          ))}
        </div>

        {/* Sair da conta */}
        <div style={{marginTop:16}}>
          <button onClick={onLogout} style={{
            display:'flex',alignItems:'center',gap:14,
            padding:'0 14px',height:66,width:'100%',
            background:CARD,borderRadius:15,border:'none',cursor:'pointer',textAlign:'left',
            transition:'background .15s',
          }}
            onMouseEnter={e=>e.currentTarget.style.background='#1f1219'}
            onMouseLeave={e=>e.currentTarget.style.background=CARD}
          >
            <div style={{
              width:38,height:38,borderRadius:11,flexShrink:0,
              background:'rgba(239,68,68,0.12)',
              display:'flex',alignItems:'center',justifyContent:'center',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
              </svg>
            </div>
            <div>
              <div style={{fontSize:14,fontWeight:500,color:'#ef4444'}}>Sair da conta</div>
              <div style={{fontSize:11,color:'rgba(239,68,68,0.42)',marginTop:2}}>Encerrar sessão da Athena</div>
            </div>
          </button>
        </div>

      </div>
    </div>
  );
}

// ─── Message action bar ───────────────────────────────────────────────────────
function MsgActions({onCopy, onPin, onSaveMemory, onMakeSkill, onShare, onEditPrompt}) {
  return (
    <div style={{display:'flex',gap:4,padding:'4px 6px',borderRadius:8,
      background:C.card,border:`1px solid ${C.border}`,
      boxShadow:'0 4px 12px rgba(0,0,0,.4)',animation:'fadeIn .1s ease'}}>
      {[
        {icon:D.copy,   tip:'Copiar',           fn:onCopy},
        {icon:D.pin,    tip:'Fixar',             fn:onPin},
        {icon:D.brain,  tip:'Salvar memória',    fn:onSaveMemory},
        {icon:D.star,   tip:'Criar skill',       fn:onMakeSkill},
        {icon:D.share,  tip:'Compartilhar',      fn:onShare || (()=>{})},
        ...(onEditPrompt ? [{icon:D.edit, tip:'Editar Prompt', fn:onEditPrompt}] : []),
      ].map((a,i)=>(
        <button key={i} title={a.tip} onClick={a.fn} style={{
          width:26,height:26,borderRadius:6,background:'none',border:'none',
          cursor:'pointer',color:C.soft,display:'flex',alignItems:'center',justifyContent:'center',
          transition:'color .15s,background .15s',
        }}
          onMouseEnter={e=>{e.currentTarget.style.background=C.border;e.currentTarget.style.color=C.accent;}}
          onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=C.soft;}}
        ><Ico d={a.icon} size={13}/></button>
      ))}
    </div>
  );
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = e => {
      const dataUrl = e.target.result;          // "data:image/jpeg;base64,/9j/..."
      const b64 = dataUrl.split(',')[1] || '';  // strip prefix
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function attachmentDisplayName(attachment) {
  return attachment?.displayName || attachment?.name || attachment?.file?.name || 'Arquivo';
}

function attachmentMimeType(attachment) {
  return attachment?.mimeType || attachment?.type || attachment?.file?.type || 'application/octet-stream';
}

function attachmentSize(attachment) {
  const value = Number(attachment?.size ?? attachment?.file?.size ?? 0);
  return Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}

function isNativeDeviceAttachment(attachment) {
  return attachment?.localRef?.provider === 'android-private-cache'
    && typeof attachment?.localRef?.attachmentId === 'string';
}

function isWebDeviceAttachment(attachment) {
  return typeof File !== 'undefined'
    && attachment?.localRef?.provider === 'web-file'
    && attachment?.localRef?.file instanceof File;
}

function attachmentPreview(attachment) {
  return attachment?.previewUrl || null;
}

function formatAttachmentSize(value) {
  const bytes = attachmentSize({ size: value });
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function getAttachIcon(mime) {
  if (!mime) return D.upload;
  if (mime.startsWith('audio/')) return D.wave;
  if (mime.startsWith('video/')) return 'M23 7l-7 5 7 5V7zM14 5H3a2 2 0 00-2 2v10a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2z';
  if (mime.includes('pdf'))      return 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6';
  if (mime.includes('sheet') || mime.includes('csv') || mime.includes('excel') || mime.includes('ods'))
    return 'M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18';
  return D.folder;
}

// ─── MessageItem — memo so hover state is local; parent list never re-renders ─
const COLLAPSE_LIMIT = 260;
const MessageItem = React.memo(function MessageItem({m, i, isLast, onSaveMemory, showToast, socialPrefs, prevUserMsg, cognitiveProfile, isMobile, sessionId, onOpenLibrarySave, onEditPrompt}) {
  const [hovered,  setHovered]  = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [compact,  setCompact]  = useState(() => document.documentElement.dataset.compact === 'true');

  useEffect(() => {
    const sync = () => setCompact(document.documentElement.dataset.compact === 'true');
    window.addEventListener('athena-prefs-changed', sync);
    return () => window.removeEventListener('athena-prefs-changed', sync);
  }, []);

  // Parse GIF tag from assistant responses — stable across re-renders
  const { gifCategory, cleanText } = useMemo(() => {
    if (m.role !== 'assistant') return { gifCategory: null, cleanText: m.content };
    const result = parseGifFromResponse(m.content);
    if (result.gifCategory) {
      console.log('[ATHENA GIF] tag detectada:', result.gifCategory, '| texto limpo:', result.cleanText.slice(0, 60));
    }
    return result;
  }, [m.content, m.role]);

  // Smart component detection — rendered before the text (only for assistant messages)
  const smartComp = useMemo(() => {
    if (m.role !== 'assistant' || !cleanText) return null;
    return detectSmartComponent(cleanText, prevUserMsg || '', cognitiveProfile);
  }, [cleanText, prevUserMsg, cognitiveProfile, m.role]);

  // Response Design System: envelopes válidos têm precedência. Respostas
  // antigas em Markdown só entram na versão editorial quando já chegam
  // organizadas; conversa curta continua no renderer normal.
  const adaptivePresentation = useMemo(
    () => m.role === 'assistant' ? resolveAdaptiveResponsePresentation(m.response_presentation, cleanText) : null,
    [m.response_presentation, m.role, cleanText],
  );
  const hasAdaptivePresentation = Boolean(adaptivePresentation);

  // Monta o payload de "salvar na Biblioteca" a partir da resposta desta mensagem —
  // preserva estrutura de cards especializados (receita/estudo/etc.) via metadata.
  const buildLibraryPayload = useMemo(() => () => {
    const wp = m.workspace_presentation;
    const title = wp?.title || m.presentation_meta?.title ||
      (cleanText || '').split('\n').map(l => l.replace(/^#+\s*/, '').replace(/\*\*/g, '').trim()).find(Boolean)?.slice(0, 80) ||
      'Resposta da Athena';
    return {
      title,
      content: cleanText || '',
      presentation_type: m.presentation_type || null,
      renderer: wp?.layout || null,
      metadata: wp || m.response_presentation || null,
      image_url: wp?.hero_image?.url || wp?.image_url || null,
      source_message_id: m.timestamp ? `${m.role}-${m.timestamp}` : `msg-${i}`,
      conversation_id: sessionId || null,
      session_id: sessionId || null,
      user_prompt: prevUserMsg || null,
      origin: 'chat',
    };
  }, [m, cleanText, prevUserMsg, sessionId, i]);

  // shouldRenderGif é o único gate — social_prefs.gifsLevel é soberano
  const gifPath = useMemo(() => {
    if (!gifCategory) return null;
    const allowed = shouldRenderGif({ gifCategory, socialPrefs, prevUserMsg: prevUserMsg || '' });
    if (!allowed) return null;
    const path = pickGifPath(gifCategory);
    console.log('[ATHENA GIF] categoria:', gifCategory, '| arquivo:', path);
    return path;
  }, [gifCategory, socialPrefs, prevUserMsg]);

  // Visual aid card — rendered in-place; bypasses normal message bubble
  if (m.type === 'visual_aid') {
    const _mob = isMobile ?? (typeof window !== 'undefined' && window.innerWidth < 768);
    return (
      <div style={{paddingLeft:20,paddingRight:20,marginBottom:16}}>
        <ImageGenerationCard
          imageUrl={m.imageUrl} prompt={m.prompt}
          isLoading={!!m.isLoading} isError={!!m.error}
          isMobile={_mob} provider={m.provider}
        />
      </div>
    );
  }

  const shouldCollapse = compact && m.role === 'user' && (m.content?.length ?? 0) > COLLAPSE_LIMIT;
  const displayContent = shouldCollapse && !expanded ? m.content.slice(0, COLLAPSE_LIMIT) + '…' : m.content;

  return (
    <div className="athena-msg" style={{marginBottom: compact ? 12 : 22, animation:isLast?'fadeIn .2s ease':'none',position:'relative'}}
      onMouseEnter={()=>setHovered(true)}
      onMouseLeave={()=>setHovered(false)}>
      {m.role==='user' ? (
        <div style={{display:'flex',justifyContent:'flex-end',alignItems:'flex-end',gap:8}}>
          {hovered && (
            <MsgActions
              onCopy={()=>{navigator.clipboard?.writeText(m.content);showToast('Copiado!');}}
              onPin={()=>showToast('Fixar mensagens ainda não está disponível; nada foi salvo.')}
              onSaveMemory={()=>{onSaveMemory&&onSaveMemory(m.content);showToast('Salvo na memória ✓');}}
              onMakeSkill={()=>showToast('Criar skills a partir de mensagens ainda não está disponível; nenhuma skill foi criada.')}
              onShare={()=>{ void shareAthenaText({text:m.content, title:'Compartilhar mensagem'}); }}
            />
          )}
          <div style={{maxWidth:'min(72%, 660px)',borderRadius:18,borderBottomRightRadius:4,
            background:'#1a1f3a',border:'1px solid #2a3060',overflow:'hidden'}}>
            {m.isVoice && (
              <div style={{display:'flex',alignItems:'center',gap:4,padding:'6px 14px 0',color:'rgba(180,195,235,0.4)'}}>
                <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
                <span style={{fontSize:9.5,letterSpacing:'.04em'}}>mensagem de voz</span>
              </div>
            )}
            {m.attachments?.length > 0 && (
              <div style={{display:'flex',flexWrap:'wrap',gap:4,padding:'10px 12px 6px'}}>
                {m.attachments.map((a,ai)=>(
                  <div key={ai} style={{borderRadius:8,overflow:'hidden',
                    background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)'}}>
                    {a.preview ? (
                      <img src={a.preview} alt={a.name}
                        style={{width:88,height:88,objectFit:'cover',display:'block'}}/>
                    ) : (
                      <div style={{display:'flex',alignItems:'center',gap:5,padding:'6px 10px',maxWidth:160}}>
                        <Ico d={getAttachIcon(a.type)} size={12}/>
                        <span style={{fontSize:10.5,color:'#8899cc',overflow:'hidden',
                          textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.name}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {m.content && (
              <div style={{padding: compact ? '8px 14px' : '12px 16px', color:'#dde4ff',fontSize:13,lineHeight:1.6}}>
                {displayContent}
                {shouldCollapse && (
                  <button onClick={()=>setExpanded(v=>!v)} style={{
                    display:'block',marginTop:5,fontSize:11,color:'#6b7590',
                    background:'none',border:'none',cursor:'pointer',padding:0,
                  }}>
                    {expanded ? '▲ Recolher' : '▼ Ver mais'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{
          display:'flex',
          flexDirection: (isMobile && (hasPremiumRenderer(m.workspace_presentation) || hasAdaptivePresentation)) ? 'column' : 'row',
          gap: (isMobile && (hasPremiumRenderer(m.workspace_presentation) || hasAdaptivePresentation)) ? 6 : 12,
          alignItems: (isMobile && (hasPremiumRenderer(m.workspace_presentation) || hasAdaptivePresentation)) ? 'stretch' : 'flex-start',
        }}>
          {(isMobile && (hasPremiumRenderer(m.workspace_presentation) || hasAdaptivePresentation)) ? (
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <AthenaMiniAvatar size={22} />
              <div style={{fontSize:12,fontWeight:600,color:C.accent,letterSpacing:'.04em'}}>Athena</div>
            </div>
          ) : (
            <div style={{flexShrink:0,marginTop:2}}>
              <AthenaMiniAvatar size={28} />
            </div>
          )}
          <div style={{flex:1,minWidth:0}}>
            {!(isMobile && (hasPremiumRenderer(m.workspace_presentation) || hasAdaptivePresentation)) && (
              <div style={{fontSize:12,fontWeight:600,color:C.accent,marginBottom:4,letterSpacing:'.04em'}}>Athena</div>
            )}
            {gifPath && (
              <video
                src={gifPath}
                autoPlay loop muted playsInline
                preload="auto"
                style={{
                  display:'block', borderRadius:14, marginBottom:10,
                  width:'100%', maxWidth:260,
                  objectFit:'cover', aspectRatio:'1/1',
                }}
                onError={e => console.warn('[ATHENA GIF] erro ao carregar:', gifPath, e)}
                onCanPlay={() => console.log('[ATHENA GIF] renderizando:', gifPath)}
              />
            )}
            {/* SmartComponentRenderer: only when no structured layout */}
            {smartComp && !adaptivePresentation && (!m.layout_type || m.layout_type === 'simple_conversation') && (
              <SmartComponentRenderer type={smartComp.type} data={smartComp.data} />
            )}
            {/* PresentationRenderer: single dispatcher for ALL structured layouts.
                Handles recipe, study, bible_study, plan, checklist and future types.
                Falls back to WorkspacePresentationRenderer for legacy layouts. */}
            {m.regenerating ? (
              <div style={{color:C.muted,fontSize:13,fontStyle:'italic',padding:'10px 2px',animation:'fadeIn .2s ease'}}>
                ✦ Gerando nova versão…
              </div>
            ) : adaptivePresentation ? (
              <div key={m._regenVersion||0} style={{color:C.text,maxWidth:'100%',animation:'fadeIn .3s ease'}}>
                <AdaptiveResponseRenderer
                  presentation={m.response_presentation}
                  content={cleanText}
                  streaming={!!m.streaming}
                  sessionId={sessionId}
                  messageId={m.response_message_id || m.card_id || m.id || m.timestamp || `message-${i}`}
                  timestamp={m.timestamp}
                  fallback={() => <AthenaPresentationEngine content={cleanText} cognitiveProfile={cognitiveProfile} />}
                />
              </div>
            ) : hasPremiumRenderer(m.workspace_presentation) ? (
              <div key={m._regenVersion||0} style={{color:C.text, maxWidth: ['recipe','study','bible_study','plan','checklist','report','comparison','tutorial','analysis'].includes(m.workspace_presentation?.layout) ? '100%' : 760, animation:'fadeIn .3s ease'}}>
                <PresentationRenderer
                  wp={m.workspace_presentation}
                  content={cleanText}
                  streaming={!!m.streaming}
                  onAction={(id)=>{
                    if(id==='copy'){ navigator.clipboard?.writeText(cleanText); showToast('Copiado!'); }
                    else if(id==='save'){ onOpenLibrarySave&&onOpenLibrarySave(buildLibraryPayload()); }
                    else if(id==='share'){ navigator.clipboard?.writeText(cleanText); showToast('Copiado para compartilhar'); }
                    else showToast('Em breve: '+id.replace(/^(adapt|follow):/,''));
                  }}
                  AthenaPresentationEngine={AthenaPresentationEngine}
                  cognitiveProfile={cognitiveProfile}
                />
              </div>
            ) : (
              /* Legacy: WorkspacePresentationRenderer for layout_type-based routing */
              <div key={m._regenVersion||0} style={{color:C.text,maxWidth:760,animation:'fadeIn .3s ease'}}>
                <WorkspacePresentationRenderer
                  layoutType={m.layout_type}
                  presentationType={m.presentation_type}
                  presentationMeta={m.presentation_meta}
                  content={cleanText}
                  AthenaPresentationEngine={AthenaPresentationEngine}
                  cognitiveProfile={cognitiveProfile}
                  workspacePresentation={m.workspace_presentation}
                />
              </div>
            )}
            {hovered && !m.regenerating && (
              <div style={{marginTop:6}}>
                <MsgActions
                  onCopy={()=>{navigator.clipboard?.writeText(cleanText);showToast('Copiado!');}}
                  onPin={()=>showToast('Fixar respostas ainda não está disponível; nada foi salvo.')}
                  onSaveMemory={()=>{onOpenLibrarySave&&onOpenLibrarySave(buildLibraryPayload());}}
                  onMakeSkill={()=>showToast('Criar skills a partir de respostas ainda não está disponível; nenhuma skill foi criada.')}
                  onShare={()=>{ void shareAthenaText({text:cleanText, title:'Compartilhar resposta da Athena'}); }}
                  onEditPrompt={m.card_id ? ()=>onEditPrompt&&onEditPrompt(m.card_id, prevUserMsg) : undefined}
                />
                <ResponseActions
                  visible={true}
                  responseText={cleanText}
                  userMessage={prevUserMsg}
                  messageId={m.timestamp ? `${m.role}-${m.timestamp}` : `msg-${i}`}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

// ─── MessageList — isolated; only re-renders when messages or voice state change
const VISIBLE_LIMIT = 60;
const MessageList = React.memo(function MessageList({
  messages, onSaveMemory, showToast,
  endRef, isMobile,
  voiceState, thinkingMessages, cognitiveProfile,
  sessionId, onOpenLibrarySave, onEditPrompt,
}) {
  // socialPrefs é reativo: atualiza quando usuário muda configurações
  const [socialPrefs, setSocialPrefs] = useState(() => loadSocialPrefs());
  useEffect(() => {
    const sync = () => setSocialPrefs(loadSocialPrefs());
    window.addEventListener('athena-prefs-changed', sync);
    return () => window.removeEventListener('athena-prefs-changed', sync);
  }, []);

  const hidden = Math.max(0, messages.length - VISIBLE_LIMIT);
  const visible = hidden > 0 ? messages.slice(-VISIBLE_LIMIT) : messages;

  return (
    <div className="workspace-message-list" style={{flex:1,paddingTop:24,
      paddingBottom:isMobile?'calc(var(--keyboard-h,0px) + 220px)':180,
      maxWidth:isMobile?'100%':'min(1060px, calc(100% - 48px))',width:'100%',margin:'0 auto',
      boxSizing:'border-box',paddingLeft:isMobile?8:20,paddingRight:isMobile?8:20}}>
      {hidden > 0 && (
        <div style={{textAlign:'center',fontSize:11,color:C.dim,
          background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`,
          borderRadius:8,padding:'8px 12px',marginBottom:16}}>
          {hidden} mensagem{hidden!==1?'s':''} anterior{hidden!==1?'es':''} oculta{hidden!==1?'s':''} para manter a Athena fluida
        </div>
      )}
      {visible.map((m,i)=>{
        const gi = hidden + i;
        // Mensagem de usuário imediatamente anterior (para detectar pedido explícito de GIF)
        const _prevMsg = gi > 0 ? messages[gi - 1] : null;
        const _prevUserMsg = _prevMsg?.role === 'user' ? (_prevMsg.content || '') : '';
        return (
          <MessageItem
            key={m.timestamp ? `${m.role}-${m.timestamp}` : `msg-${gi}`}
            m={m} i={gi}
            isLast={gi===messages.length-1}
            onSaveMemory={onSaveMemory}
            showToast={showToast}
            socialPrefs={socialPrefs}
            prevUserMsg={_prevUserMsg}
            cognitiveProfile={cognitiveProfile}
            isMobile={isMobile}
            sessionId={sessionId}
            onOpenLibrarySave={onOpenLibrarySave}
            onEditPrompt={onEditPrompt}
          />
        );
      })}
      {/* Status da Athena — dentro do scroll, nasce abaixo da última mensagem */}
      {(voiceState === 'processing' || voiceState === 'speaking') && (
        <div style={{paddingLeft:0,paddingRight:0,marginTop:4,marginBottom:4}}>
          {voiceState === 'processing' ? (
            <ThinkingIndicator messages={thinkingMessages}/>
          ) : (
            <div style={{paddingLeft:20,paddingTop:6}}>
              <span style={{fontSize:12,color:C.muted,fontStyle:'italic'}}>Athena está falando…</span>
            </div>
          )}
        </div>
      )}
      <div ref={endRef} style={{height:8}}/>
    </div>
  );
});

// ─── Composer — isolated input area; owns its own state ──────────────────────
const Composer = React.memo(function Composer({
  onSend, voiceState, voiceMode, onMicToggle, onToggleVoiceMode,
  continuousVoice, onToggleContinuousVoice,
  onOpenVisualCall,
  cogMode, setCogMode, onOpenSkills,
  setInputRef, inputValueRef, composerRef,
  isMobile, isEmpty, token, projectId, sessionId,
  pendingDeviceItems = [], onConsumeDeviceItem, onRemoveDeviceItem, onAttachmentError,
}) {
  const [input,              setInput]              = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [showAttach,         setShowAttach]         = useState(false);
  const [showModeMenu,       setShowModeMenu]       = useState(false);
  const [isUploading,        setIsUploading]        = useState(false);
  const [sendFeedback,       setSendFeedback]       = useState('');
  const [usedSharedTextIds,  setUsedSharedTextIds]  = useState(() => new Set());
  const [attachSettings,     setAttachSettings]     = useState(()=>{
    try{return JSON.parse(localStorage.getItem('athena_attach_settings')||'{}');}catch{return{};}
  });

  useEffect(() => {
    const onNativeBack = (event) => {
      if (showModeMenu) setShowModeMenu(false);
      else if (showAttach) setShowAttach(false);
      else return;
      event.preventDefault();
    };
    window.addEventListener('athena-native-back', onNativeBack);
    return () => window.removeEventListener('athena-native-back', onNativeBack);
  }, [showAttach, showModeMenu]);
  const taRef = useRef(null);

  useEffect(()=>{ if(setInputRef) setInputRef.current = setInput; }, [setInputRef]);
  useEffect(()=>{ if(inputValueRef) inputValueRef.current = input; }, [inputValueRef, input]);
  useEffect(()=>{
    const ta=taRef.current; if(!ta) return;
    ta.style.height='auto';
    ta.style.height=Math.min(ta.scrollHeight,120)+'px';
  },[input]);

  const isBusy = voiceState==='processing' || isUploading;

  const toggleAttachSetting = useCallback(id => {
    setAttachSettings(prev=>{
      const next = {...prev, [id]: !prev[id]};
      localStorage.setItem('athena_attach_settings', JSON.stringify(next));
      return next;
    });
  }, []);

  const addPendingAttachments = useCallback((items) => {
    const normalized = (Array.isArray(items) ? items : [items]).filter(Boolean);
    if (!normalized.length) return;
    setPendingAttachments((previous) => {
      const seen = new Set(previous.map((item) => item.id || item?.localRef?.attachmentId));
      const additions = normalized.filter((item) => {
        const key = item.id || item?.localRef?.attachmentId;
        return key && !seen.has(key);
      });
      return additions.length ? [...previous, ...additions] : previous;
    });
  }, []);

  const addBrowserFile = useCallback((file) => {
    if (!(typeof File !== 'undefined' && file instanceof File) || file.size <= 0 || file.size > 20 * 1024 * 1024) {
      onAttachmentError?.('Escolha um arquivo de até 20 MB.');
      return;
    }
    const mimeType = file.type || 'application/octet-stream';
    let previewUrl = null;
    if (mimeType.startsWith('image/')) {
      try { previewUrl = URL.createObjectURL(file); } catch { previewUrl = null; }
    }
    addPendingAttachments({
      id: `web:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`,
      displayName: file.name || 'Arquivo',
      mimeType,
      size: file.size,
      localRef: { provider: 'web-file', file },
      previewUrl,
      status: 'pending',
    });
  }, [addPendingAttachments, onAttachmentError]);

  const removePendingAttachment = useCallback((attachment) => {
    if (attachmentPreview(attachment)) {
      try { URL.revokeObjectURL(attachmentPreview(attachment)); } catch { /* already released */ }
    }
    if (isNativeDeviceAttachment(attachment)) void discardAthenaContextItem(attachment);
    setPendingAttachments((previous) => previous.filter((item) => item.id !== attachment.id));
  }, []);

  const pickFromDevice = useCallback(async (shortcut) => {
    let result;
    if (shortcut.id === 'camera') result = await captureAthenaPhoto();
    else if (shortcut.id === 'fotos') result = await pickAthenaImage();
    else result = await pickAthenaDocuments({ multiple: shortcut.id !== 'fotos' && shortcut.id !== 'camera' });

    if (result?.items?.length) addPendingAttachments(result.items);
    const issue = result?.errors?.[0]?.code || result?.status;
    if (issue && issue !== 'cancelled') onAttachmentError?.('Não foi possível adicionar este arquivo agora.');
    // A capability call was attempted even when the person canceled it; do
    // not fall back to an uncontrolled URI path inside the Android WebView.
    return true;
  }, [addPendingAttachments, onAttachmentError]);

  const applySharedText = useCallback((item) => {
    const sharedText = typeof item?.text === 'string' ? item.text.trim() : '';
    if (!sharedText) return;
    setInput((previous) => previous.trim() ? `${previous.trim()}\n${sharedText}` : sharedText);
    if (Array.isArray(item?.attachments) && item.attachments.length > 0) {
      setUsedSharedTextIds((previous) => new Set(previous).add(item.id));
    } else {
      void onConsumeDeviceItem?.(item.id);
    }
  }, [onConsumeDeviceItem]);

  const applySharedAttachments = useCallback((item) => {
    const attachments = Array.isArray(item?.attachments) ? item.attachments : [];
    if (!attachments.length) return;
    addPendingAttachments(attachments);
    void onConsumeDeviceItem?.(item.id);
  }, [addPendingAttachments, onConsumeDeviceItem]);

  const send = async () => {
    if((!input.trim() && pendingAttachments.length === 0) || isBusy) return;
    const text = input.trim();
    setSendFeedback('');
    setIsUploading(true);
    try {
      const attachments = [];
      for (const attachment of pendingAttachments) {
        if (isNativeDeviceAttachment(attachment)) {
          let serverRef = attachment.attachmentRef;
          if (!serverRef?.file?.id) {
            const uploaded = await uploadAthenaContextItem(attachment, { token, projectId, sessionId });
            if (!uploaded.ok || !uploaded.file?.id) throw new Error(uploaded.status || 'native_upload_failed');
            serverRef = { file: uploaded.file, attachment: uploaded.attachment || null };
            setPendingAttachments((previous) => previous.map((item) => item.id === attachment.id
              ? { ...item, attachmentRef: serverRef, status: 'uploaded' }
              : item));
          }
          attachments.push({
            name: attachmentDisplayName(attachment),
            type: attachmentMimeType(attachment),
            size: attachmentSize(attachment),
            file_id: serverRef.file.id,
            attachment_id: serverRef.attachment?.id || null,
            storage: 'native_upload',
          });
          continue;
        }

        const file = isWebDeviceAttachment(attachment) ? attachment.localRef.file : attachment.file;
        if (!file) throw new Error('attachment_unavailable');
        // The legacy PWA chat contract only accepts a very small compatibility
        // payload. Larger binary data must use the persistent upload path,
        // never base64 in a chat request.
        if (file.size > 48 * 1024) throw new Error('web_persistent_upload_required');
        attachments.push({
          name: attachmentDisplayName(attachment),
          type: attachmentMimeType(attachment),
          size: attachmentSize(attachment),
          data: await readFileAsBase64(file),
        });
      }
      // Upload preparation is complete. From here sendMessage owns the chat
      // state. Its explicit result lets the composer retain a draft that was
      // refused before a request could start.
      setIsUploading(false);
      const outcome = await onSend(text, attachments);
      if (outcome?.accepted === false) {
        const feedback = outcome.reason === 'processing'
          ? 'A Athena ainda está respondendo. Sua mensagem foi mantida.'
          : outcome.reason === 'duplicate'
            ? 'Essa mensagem já está sendo enviada. Seu texto foi mantido.'
            : outcome.reason === 'restoring'
              ? 'A conversa ainda está sendo restaurada. Seu texto foi mantido.'
              : outcome.reason === 'not_authenticated'
                ? 'Sua sessão não está disponível. Entre novamente para enviar.'
                : 'Não consegui enviar agora. Seu texto foi mantido para você tentar de novo.';
        setSendFeedback(feedback);
        return;
      }
      setInput('');
      pendingAttachments.forEach((attachment) => {
        if (attachmentPreview(attachment)) {
          try { URL.revokeObjectURL(attachmentPreview(attachment)); } catch { /* already released */ }
        }
      });
      setPendingAttachments([]);
      if (outcome?.queued) {
        setSendFeedback('Mensagem guardada. Ela será enviada assim que a conversa terminar de restaurar.');
      }
    } catch (error) {
      const message = error?.message === 'native_upload_unavailable'
        ? 'O envio nativo ainda não está disponível nesta versão da Athena.'
        : error?.message === 'web_persistent_upload_required'
          ? 'Arquivos maiores precisam do envio persistente seguro, que ainda não está ativo nesta versão web.'
          : 'Não consegui enviar este anexo. Ele continua pronto para você tentar de novo.';
      onAttachmentError?.(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div ref={composerRef} className="workspace-composer-shell" style={{
      ...(isMobile ? {
        position:'fixed', bottom:0, left:0, right:0,
        zIndex:100, willChange:'transform',
      } : {flexShrink:0}),
      paddingTop:    isMobile ? 8 : (isEmpty ? 0 : 8),
      paddingLeft:   20,
      paddingRight:  20,
      paddingBottom: isMobile ? 16 : (isEmpty ? 24 : 18),
      maxWidth:isEmpty?680:740,width:'100%',
      margin:'0 auto',boxSizing:'border-box',
    }}>
      {showAttach && <AttachModal onClose={()=>setShowAttach(false)}
        onAttach={file=>{
          addBrowserFile(file);
          setShowAttach(false);
        }}
        onDevicePick={pickFromDevice}
        onInsertPrompt={t=>{setInput(t); setShowAttach(false);}}
        onOpenSkills={()=>{ onOpenSkills(); setShowAttach(false); }}
        onOpenVisualCall={()=>{ onOpenVisualCall(); setShowAttach(false); }}
        settings={attachSettings}
        onToggleSetting={toggleAttachSetting}/>}

      {pendingDeviceItems.length > 0 && (
        <div style={{margin:'0 2px 8px',display:'grid',gap:6}}>
          {pendingDeviceItems.slice(0, 2).map((item) => {
            const attachmentCount = Array.isArray(item.attachments) ? item.attachments.length : 0;
            const textAlreadyUsed = usedSharedTextIds.has(item.id);
            return (
              <div key={item.id} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',
                background:'rgba(11,25,47,.88)',border:'1px solid rgba(59,130,246,.28)',borderRadius:11,
                boxShadow:'0 4px 16px rgba(0,0,0,.24)'}}>
                <div style={{width:25,height:25,borderRadius:8,background:'rgba(59,130,246,.16)',color:C.accent,
                  display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <Ico d={attachmentCount ? D.folder : D.copy} size={12}/>
                </div>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{fontSize:11.5,fontWeight:600,color:C.text}}>Recebido no dispositivo</div>
                  <div style={{fontSize:10.5,color:C.muted,marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {attachmentCount ? `${attachmentCount} arquivo${attachmentCount === 1 ? '' : 's'} pronto${attachmentCount === 1 ? '' : 's'} para adicionar` : 'Texto compartilhado — escolha se quer usar'}
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:5,flexShrink:0}}>
                  {item.text && (
                    <button onClick={()=>applySharedText(item)} disabled={textAlreadyUsed} style={{padding:'5px 7px',borderRadius:7,cursor:textAlreadyUsed?'default':'pointer',fontSize:10.5,
                      color:textAlreadyUsed?C.muted:C.accent,background:textAlreadyUsed?C.card:C.accentB,border:`1px solid ${textAlreadyUsed?C.border:C.accentD}`}}>{textAlreadyUsed?'No rascunho':'Usar texto'}</button>
                  )}
                  {attachmentCount > 0 && (
                    <button onClick={()=>applySharedAttachments(item)} style={{padding:'5px 7px',borderRadius:7,cursor:'pointer',fontSize:10.5,
                      color:C.accent,background:C.accentB,border:`1px solid ${C.accentD}`}}>Adicionar</button>
                  )}
                  <button onClick={()=>{ void onRemoveDeviceItem?.(item.id); }} aria-label="Descartar conteúdo recebido"
                    title="Descartar" style={{width:24,height:24,padding:0,border:0,borderRadius:7,cursor:'pointer',
                      color:C.muted,background:'transparent'}}>×</button>
                </div>
              </div>
            );
          })}
          {pendingDeviceItems.length > 2 && <div style={{fontSize:10,color:C.muted,paddingLeft:4}}>+{pendingDeviceItems.length - 2} itens recebidos</div>}
        </div>
      )}

      <div className="workspace-composer" style={{background:'rgba(8,12,28,.60)',backdropFilter:'blur(18px)',WebkitBackdropFilter:'blur(18px)',border:'1px solid rgba(255,255,255,.06)',borderRadius:20,
        padding:'14px 16px',boxShadow:'0 4px 24px rgba(0,0,0,.4)'}}>
        {sendFeedback && (
          <div role="status" style={{marginBottom:9,fontSize:11.5,lineHeight:1.4,color:'#93c5fd'}}>
            {sendFeedback}
          </div>
        )}
        {pendingAttachments.length > 0 && (
          <div style={{display:'flex',flexWrap:'wrap',gap:6,paddingBottom:10}}>
            {pendingAttachments.map(a=>(
              <div key={a.id} style={{position:'relative',display:'inline-flex',
                alignItems:'center',background:C.base,border:`1px solid ${C.border}`,
                borderRadius:8,overflow:'hidden',maxWidth:160}}>
                {attachmentPreview(a) ? (
                  <img src={attachmentPreview(a)} alt={attachmentDisplayName(a)}
                    style={{width:52,height:52,objectFit:'cover',display:'block',flexShrink:0}}/>
                ) : (
                  <div style={{display:'flex',alignItems:'center',gap:5,padding:'6px 8px'}}>
                    <Ico d={getAttachIcon(attachmentMimeType(a))} size={13}/>
                    <span style={{fontSize:10.5,color:C.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:100}}>
                      {attachmentDisplayName(a)}{formatAttachmentSize(a.size) ? ` · ${formatAttachmentSize(a.size)}` : ''}
                    </span>
                  </div>
                )}
                <button onClick={()=>removePendingAttachment(a)} style={{position:'absolute',top:2,right:2,width:15,height:15,borderRadius:'50%',
                  background:'rgba(0,0,0,.75)',border:'none',cursor:'pointer',
                  color:'#fff',fontSize:8,display:'flex',alignItems:'center',
                  justifyContent:'center',padding:0,lineHeight:1,flexShrink:0}}>✕</button>
              </div>
            ))}
          </div>
        )}
        <textarea ref={taRef} value={input}
          onChange={e=>{ setInput(e.target.value); if(sendFeedback) setSendFeedback(''); }}
          onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();send();}}}
          placeholder={
            voiceState==='listening'?'Ouvindo...':
            voiceState==='processing'?'Athena está pensando...':
            voiceState==='speaking'?'Athena está falando...':
            'Conversar com Athena...'}
          disabled={isBusy} rows={1}
          style={{display:'block',width:'100%',background:'none',border:'none',
            outline:'none',color:isBusy?C.muted:C.text,fontSize:13,
            padding:'0 0 10px',lineHeight:1.5,boxSizing:'border-box',
            cursor:isBusy?'default':'text',
            resize:'none',overflow:'hidden',minHeight:22,fontFamily:'inherit'}}/>

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <button onClick={()=>setShowAttach(true)} style={{width:28,height:28,borderRadius:7,
            background:'none',border:`1px solid #252840`,cursor:'pointer',color:C.soft,
            display:'flex',alignItems:'center',justifyContent:'center',transition:'all .15s'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accentD;e.currentTarget.style.color=C.accent;}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='#252840';e.currentTarget.style.color=C.soft;}}>
            <Ico d={D.plus} size={12}/>
          </button>
          <div style={{display:'flex',alignItems:'center',gap:7}}>
            <div style={{position:'relative'}}>
              <button onClick={()=>setShowModeMenu(v=>!v)} style={{padding:'3px 10px',borderRadius:20,
                background:showModeMenu?C.accentB:'#1a1c2e',
                border:`1px solid ${showModeMenu?C.accentD:'#252840'}`,fontSize:11,
                color:showModeMenu?C.accent:C.muted,cursor:'pointer',
                display:'flex',alignItems:'center',gap:5,transition:'all .15s'}}
                onMouseEnter={e=>{if(!showModeMenu)e.currentTarget.style.borderColor=C.accentD;}}
                onMouseLeave={e=>{if(!showModeMenu)e.currentTarget.style.borderColor='#252840';}}>
                <AthenaLogo size={12} showText={false}/>
                {COG_MODES.find(m=>m.id===cogMode)?.label || 'Athena 2.0'}
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  style={{transform:showModeMenu?'rotate(180deg)':'rotate(0deg)',transition:'transform .2s'}}>
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              </button>
              {showModeMenu && (
                <>
                  <div style={{position:'fixed',inset:0,zIndex:300}} onClick={()=>setShowModeMenu(false)}/>
                  <div style={{position:'absolute',right:0,bottom:'calc(100% + 8px)',zIndex:301,
                    background:'#11131f',border:`1px solid ${C.border}`,borderRadius:14,
                    padding:7,minWidth:240,boxShadow:'0 -16px 40px rgba(0,0,0,.55)',
                    animation:'fadeIn .15s ease'}}>
                    {COG_MODES.map(m=>{
                      const active = cogMode===m.id;
                      return (
                        <button key={m.id} onClick={()=>{
                          setCogMode(m.id);
                          localStorage.setItem('athena_cog_mode', m.id);
                          setShowModeMenu(false);
                        }} style={{display:'flex',alignItems:'flex-start',gap:10,width:'100%',
                          padding:'9px 9px',borderRadius:9,marginBottom:1,
                          background:active?`${C.accent}0d`:'none',border:'none',
                          cursor:'pointer',textAlign:'left',transition:'background .12s'}}
                          onMouseEnter={e=>{if(!active)e.currentTarget.style.background='rgba(255,255,255,0.03)';}}
                          onMouseLeave={e=>{if(!active)e.currentTarget.style.background='none';}}>
                          <div style={{width:13,height:13,borderRadius:'50%',marginTop:3,flexShrink:0,
                            border:`1.5px solid ${active?C.accent:C.soft}`,
                            display:'flex',alignItems:'center',justifyContent:'center',transition:'border-color .12s'}}>
                            {active && <div style={{width:5,height:5,borderRadius:'50%',background:C.accent}}/>}
                          </div>
                          <span style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12.5,fontWeight:600,lineHeight:1.3,color:active?C.accent:C.text,display:'flex',alignItems:'center',gap:6}}>
                              {m.label}
                              {m.soon && <span style={{fontSize:9,fontWeight:500,background:'rgba(120,130,220,0.15)',color:'rgba(140,150,230,0.8)',padding:'1px 6px',borderRadius:8,letterSpacing:'.04em',flexShrink:0}}>EM BREVE</span>}
                            </div>
                            <div style={{fontSize:11,color:C.muted,marginTop:2,lineHeight:1.4}}>{m.desc}</div>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <button onClick={onMicToggle} title={voiceState==='listening'?'Parar':'Falar'} style={{
              width:28,height:28,borderRadius:7,cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',
              background:voiceState==='listening'?C.accentB:'none',
              border:voiceState==='listening'?`1px solid ${C.accentD}`:'none',
              color:voiceState==='listening'?C.accent:C.soft,transition:'all .2s'}}>
              <Ico d={D.mic} size={14}/>
            </button>
            <button onClick={onToggleVoiceMode}
              title={voiceMode?'Desligar voz':'Ativar voz'}
              aria-label={voiceMode?'Desligar voz da Athena':'Ativar voz da Athena'}
              aria-pressed={voiceMode} style={{
              width:28,height:28,borderRadius:7,cursor:'pointer',
              display:'flex',alignItems:'center',justifyContent:'center',
              background:voiceMode?C.accentB:'none',
              border:voiceMode?`1px solid ${C.accentD}`:'none',
              color:voiceMode?C.accent:C.soft,transition:'all .2s'}}>
              <Ico d={D.vol} size={14}/>
            </button>
            <button
              onClick={onToggleContinuousVoice}
              title={continuousVoice?'Parar conversa contínua':'Conversa contínua (voz bidirecional)'}
              style={{width:28,height:28,borderRadius:7,cursor:'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',gap:1,
                background:continuousVoice?C.accentB:'none',
                border:continuousVoice?`1px solid ${C.accentD}`:'none',
                color:continuousVoice?C.accent:C.soft,transition:'all .2s'}}>
              {[5,9,13,9,5,9,13].map((h,i)=>(
                <div key={i} style={{width:2,height:h,borderRadius:2,background:'currentColor',
                  opacity: continuousVoice ? 1 : .8}}/>
              ))}
            </button>
            {(input.trim()||pendingAttachments.length>0||isBusy) && (
              <button onClick={isBusy?undefined:send} style={{
                width:30,height:30,borderRadius:8,
                background:isBusy?C.card:C.accentB,
                border:`1px solid ${isBusy?C.border:C.accentD}`,
                display:'flex',alignItems:'center',justifyContent:'center',
                cursor:isBusy?'default':'pointer',color:isBusy?C.muted:C.accent,
                transition:'all .15s'}}
                onMouseEnter={e=>{if(!isBusy){e.currentTarget.style.background='#0f2820';e.currentTarget.style.borderColor=C.accent;}}}
                onMouseLeave={e=>{if(!isBusy){e.currentTarget.style.background=C.accentB;e.currentTarget.style.borderColor=C.accentD;}}}>
                <Ico d={D.send} size={13}/>
              </button>
            )}
          </div>
        </div>
      </div>
      <div style={{textAlign:'center',fontSize:10,color:C.dim,marginTop:7}}>
        Athena pode cometer erros. Verifique informações importantes.
      </div>
    </div>
  );
});

// ─── NotifBell — sino de notificações do topo do Workspace ───────────────────
// Contador e lista vêm do log real (useNotifLog), não de um valor fixo.
function NotifBell({isMobile}) {
  const { log, unreadCount } = useNotifLog();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [panelTop, setPanelTop] = useState(0);
  const ref = useRef(null);
  const size = isMobile ? 36 : 40;
  const edgeGap = isMobile ? 10 : 14; // mesma margem lateral do padding do Topbar

  useEffect(() => {
    if (!open) return;
    const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const togglePanel = () => {
    if (!open && ref.current) setPanelTop(ref.current.getBoundingClientRect().bottom + 8);
    setOpen(v => !v);
  };

  return (
    <div ref={ref} style={{position:'relative'}}>
      <button onClick={togglePanel}
        title="Notificações" aria-label="Abrir notificações"
        style={{width:size,height:size,borderRadius:10,
          background: open ? `${C.accent}14` : 'none',
          border: `1px solid ${open ? `${C.accent}40` : 'transparent'}`,
          cursor:'pointer',color:open?C.accent:C.soft,position:'relative',
          display:'flex',alignItems:'center',justifyContent:'center',transition:'all .18s'}}
        onMouseEnter={e=>{if(!open){e.currentTarget.style.background=`${C.accent}0c`;e.currentTarget.style.color=C.text;}}}
        onMouseLeave={e=>{if(!open){e.currentTarget.style.background='none';e.currentTarget.style.color=C.soft;}}}>
        <svg width={isMobile?19:21} height={isMobile?19:21} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d={D.bell}/>
        </svg>
        {unreadCount > 0 && (
          <span style={{position:'absolute',top:3,right:3,minWidth:15,height:15,borderRadius:8,
            background:C.accent,color:'#fff',fontSize:9.5,fontWeight:700,padding:'0 3px',
            display:'flex',alignItems:'center',justifyContent:'center',
            boxShadow:`0 0 6px ${C.accent}99`,border:`1.5px solid ${C.bar}`}}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{position:'fixed',top:panelTop,right:edgeGap,width:`min(300px, calc(100vw - ${edgeGap*2}px))`,maxHeight:380,
          overflowY:'auto',background:C.card,border:`1px solid ${C.border}`,borderRadius:12,
          boxShadow:'0 12px 32px rgba(0,0,0,.55)',zIndex:400,animation:'fadeIn .15s ease'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
            padding:'12px 14px',borderBottom:`1px solid ${C.border}`}}>
            <span style={{fontSize:13,fontWeight:600,color:C.text}}>Notificações</span>
            {unreadCount > 0 && (
              <button onClick={()=>markAllNotifRead()} style={{
                background:'none',border:'none',cursor:'pointer',color:C.accent,fontSize:11}}>
                Marcar todas como lidas
              </button>
            )}
          </div>
          {log.length === 0 ? (
            <div style={{padding:'28px 14px',textAlign:'center',color:C.muted,fontSize:12.5}}>
              Nenhuma notificação por aqui ainda.
            </div>
          ) : (
            log.map(n => (
              <button key={n.id} onClick={()=>{
                markNotifClicked(n.id);
                if (n.route) navigate(n.route);
                setOpen(false);
              }} style={{
                display:'flex',gap:8,width:'100%',textAlign:'left',padding:'10px 14px',
                background:n.read?'none':`${C.accent}0a`,border:'none',cursor:'pointer',
                borderBottom:`1px solid ${C.border}`}}>
                <div style={{width:6,height:6,borderRadius:'50%',marginTop:5,flexShrink:0,
                  background:n.read?'transparent':C.accent}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12.5,color:C.text,lineHeight:1.4}}>{n.body}</div>
                  <div style={{fontSize:10.5,color:C.muted,marginTop:2}}>{fmtTime(n.createdAt)}</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main: ModoDiscreto ───────────────────────────────────────────────────────
function ModoDiscreto({
  user, messages, onSend, onRegenerateCard, onExit,
  voiceState='idle', onMicToggle,
  voiceMode, onToggleVoiceMode,
  continuousVoice, onToggleContinuousVoice,
  sessionList, onNewConversation, onLoadConversation,
  projects, onNewProject,
  onSaveMemory, token, sessionId,
  cogMode, setCogMode,
  setInputRef, inputValueRef,
  thinkingType,
  chatBgState='idle',
  focusMission=null, setFocusMission,
  focusWin=null, setFocusWin,
  showFocusResume=false, setShowFocusResume,
  cognitiveProfile=null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const deviceContext = usePendingDeviceContext();
  const [activeUniv,       setActiveUniv]       = useState('athena');
  // O Workspace abre no chat restaurado; ferramentas são painéis explícitos.
  const [activePanel,      setActivePanel]      = useState(null);
  const [showPersonalizar, setShowPersonalizar] = useState(false);
  const [libSaveItem,      setLibSaveItem]      = useState(null);
  const openLibrarySave = useCallback(payload => setLibSaveItem(payload), []);
  const [editingCard, setEditingCard] = useState(null); // {cardId, prompt} — Editar Prompt
  const [fontScale, setFontScale] = useState(() => {
    const saved = localStorage.getItem('athena_ws_font_scale');
    if (saved) return parseFloat(saved);
    const old = localStorage.getItem('athena_text_size');
    if (old === 'small') return 0.92;
    if (old === 'large') return 1.18;
    return 1.0;
  });

  useEffect(() => {
    localStorage.setItem('athena_ws_font_scale', String(fontScale));
  }, [fontScale]);

  useEffect(() => {
    const segment = location.pathname.replace(/^\/workspace\/?/, '').split('/')[0];
    const routes = { '': null, chats: null, today: 'hoje', inbox: 'inbox', vault: 'vault', projects: 'projetos', planning: 'planejamento', calendar: 'calendario', tasks: 'tarefas', library: 'biblioteca', tools: 'ferramentas' };
    setActivePanel(Object.prototype.hasOwnProperty.call(routes, segment) ? routes[segment] : null);
  }, [location.pathname]);
  const activeProjectId = location.pathname.match(/^\/workspace\/projects\/([^/]+)/)?.[1] || null;
  const openProjectConversation = useCallback(async(projectId, existingSessionId=null)=>{
    if(existingSessionId){ onLoadConversation(existingSessionId); navigate('/workspace/chats'); return; }
    try { const {data}=await axios.post(`${API_BASE}/memory/sessions/new`,{project_id:projectId},{headers:operationalHeaders(token)}); onNewConversation(data.session_id); navigate('/workspace/chats'); }
    catch { navigate('/workspace/chats'); }
  },[token,onNewConversation,onLoadConversation,navigate]);

  // Response Actions — quick follow-up prompts from message bubbles.
  // Carrega a resposta selecionada (não a última mensagem global) para que o
  // backend continue exatamente o assunto clicado, não o assunto da Athena.
  useEffect(() => {
    function handleQuickAction(e) {
      const { prompt, actionType, sourceResponseText, sourceUserMessage, sourceMessageId } = e.detail || {};
      if (!prompt) return;
      onSend(prompt, [], false, { actionType, sourceResponseText, sourceUserMessage, sourceMessageId });
    }
    window.addEventListener('athena-quick-action', handleQuickAction);
    return () => window.removeEventListener('athena-quick-action', handleQuickAction);
  }, [onSend]);
  const [isMobile,         setIsMobile]         = useState(window.innerWidth < 768);
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [sidebarWidth,     setSidebarWidth]     = useState(320);
  const [isResizing,       setIsResizing]       = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showLifeOSRoutines, setShowLifeOSRoutines] = useState(false);
  const [hoveredNew,       setHoveredNew]       = useState(false);
  const [newConvClicked,   setNewConvClicked]   = useState(false);
  const [activeConvId,     setActiveConvId]     = useState(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [showProfile,   setShowProfile]   = useState(false);
  const [showSkills,    setShowSkills]    = useState(false);
  const [showSkillsModal, setShowSkillsModal] = useState(false);
  const [showProfileDrop, setShowProfileDrop] = useState(false);
  // Sidebar profile (avatar/nome) — mesma fonte de dados que a página Personalizar (/api/auth/me)
  const [profileData, setProfileData] = useState(null);
  const fetchProfileData = useCallback(() => {
    fetch(`${API_BASE}/auth/me`, {headers: token ? {Authorization: `Bearer ${token}`} : {}})
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setProfileData(d); })
      .catch(() => {});
  }, [token]);
  useEffect(() => { fetchProfileData(); }, [fetchProfileData]);
  const [toast,         setToast]         = useState('');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [univExpanded,  setUnivExpanded]  = useState({});
  const [pinned,        setPinned]        = useState(()=>{try{return JSON.parse(localStorage.getItem(_PINNED_KEY)||'[]');}catch{return[];}});
  const [deletedIds,    setDeletedIds]    = useState(()=>new Set());
  const [longPressCtx,  setLongPressCtx]  = useState(null);
  const longPressTimer = useRef(null);
  const endRef            = useRef(null);
  const composerRef       = useRef(null);
  const scrollContainerRef = useRef(null);
  const atBottomRef       = useRef(true);
  const [showScrollBtn,   setShowScrollBtn]   = useState(false);

  const thinkingMessages = useMemo(()=>{
    const lastUser = [...messages].reverse().find(m=>m.role==='user');
    return getThinkingMessages(lastUser?.content||'', thinkingType, cogMode);
  },[messages, thinkingType, cogMode]);

  useEffect(() => {
    const handler = e => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(()=>{
    if(!atBottomRef.current) return;
    requestAnimationFrame(()=>{
      const sc=scrollContainerRef.current;
      if(sc) sc.scrollTop=sc.scrollHeight;
    });
  },[messages, voiceState]);

  useEffect(()=>{
    if (window.innerWidth >= 640) return;
    const apply = (kb) => {
      requestAnimationFrame(()=>{
        const el = composerRef.current;
        if (el) el.style.transform = kb > 40 ? `translateY(-${kb}px)` : '';
        document.documentElement.style.setProperty('--keyboard-h', kb > 40 ? kb+'px' : '0px');
        const sc = scrollContainerRef.current;
        if (sc && atBottomRef.current) sc.scrollTop = sc.scrollHeight;
      });
    };
    if (IS_NATIVE_APP) {
      // O Android já reduz a WebView com `adjustResize`. Aplicar também a
      // altura do IME por transform faria o compositor subir duas vezes e
      // deixaria um espaço vazio no fim da conversa.
      const onNativeKeyboard = () => apply(0);
      window.addEventListener('athena-native-keyboard', onNativeKeyboard);
      apply(0);
      return () => {
        window.removeEventListener('athena-native-keyboard', onNativeKeyboard);
        apply(0);
      };
    }
    if ('virtualKeyboard' in navigator) {
      navigator.virtualKeyboard.overlaysContent = true;
      const onGeo = () => apply(navigator.virtualKeyboard.boundingRect.height);
      navigator.virtualKeyboard.addEventListener('geometrychange', onGeo);
      return () => navigator.virtualKeyboard.removeEventListener('geometrychange', onGeo);
    }
    const vv = window.visualViewport;
    if (!vv) return;
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const onVV = () => {
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      if (isIOS) {
        document.documentElement.style.setProperty('--keyboard-h', kb > 40 ? kb+'px' : '0px');
        const sc = scrollContainerRef.current;
        if (sc && atBottomRef.current) sc.scrollTop = sc.scrollHeight;
      } else {
        apply(kb);
      }
    };
    vv.addEventListener('resize', onVV);
    vv.addEventListener('scroll', onVV);
    return () => { vv.removeEventListener('resize', onVV); vv.removeEventListener('scroll', onVV); };
  }, []);

  useEffect(() => {
    const onNativeBack = (event) => {
      if (showProfileDrop) setShowProfileDrop(false);
      else if (showPersonalizar) setShowPersonalizar(false);
      else if (showSkillsModal) setShowSkillsModal(false);
      else if (showSkills) setShowSkills(false);
      else if (showProfile) setShowProfile(false);
      else if (sidebarOpen) setSidebarOpen(false);
      else if (activePanel) setActivePanel(null);
      else return;
      event.preventDefault();
    };
    window.addEventListener('athena-native-back', onNativeBack);
    return () => window.removeEventListener('athena-native-back', onNativeBack);
  }, [activePanel, showPersonalizar, showProfile, showProfileDrop, showSkills, showSkillsModal, sidebarOpen]);

  const isBusy  = voiceState==='processing';
  const isEmpty = messages.length===0;
  const username = user?.name || profileData?.name || 'Vitor';
  const avatarUrl = profileData?.avatar || user?.avatar || null;
  const hour = new Date().getHours();
  const greeting = hour<12?'Bom dia':hour<18?'Boa tarde':'Boa noite';
  const univData = UNIVERSOS.find(u=>u.id===activeUniv);

  const showToast = useCallback(msg => { setToast(msg); setTimeout(()=>setToast(''),2500); }, []);
  const openSkills = useCallback(()=>setShowSkillsModal(true), []);

  // Camera command patterns
  const CAM_OPEN_RE  = /\b(abr[ia]*\s*(a\s*)?(câmera|camera|chamada\s*visual|visão)|câmera\s*visual|chamada\s*visual|mostrar\s*(a\s*)?câmera|ativar\s*vis[aã]o|ligar\s*câmera)\b/i;
  const CAM_CLOSE_RE = /\b(fech[ae]*r?\s*(a\s*)?(câmera|camera|chamada)|encerr[ae]*r?\s*(câmera|chamada|visual)|deslig[ae]*r?\s*(a\s*)?câmera|parar\s*câmera)\b/i;

  const handleSend = useCallback((text, attachments) => {
    if (text?.trim()) {
      if (CAM_OPEN_RE.test(text))  { navigate('/visual-call'); return { accepted: true, handoff: true }; }
    }
    return onSend(text, attachments);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSend]);

  const createNewConversation = useCallback(async () => {
    try {
      const {data} = await axios.post(`${API_BASE}/memory/sessions/new`, {},
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      onNewConversation(data.session_id);
    } catch { onNewConversation(null); }
  }, [token, onNewConversation]);

  const togglePin = useCallback((id) => {
    setPinned(prev => {
      const updated = prev.includes(id) ? prev.filter(p=>p!==id) : [...prev, id];
      localStorage.setItem(_PINNED_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const deleteSession = useCallback(async (id) => {
    try {
      await axios.delete(`${API_BASE}/memory/sessions/${id}`,
        {headers:{Authorization:`Bearer ${token}`},withCredentials:true});
      setDeletedIds(prev => new Set([...prev, id]));
      showToast('Conversa excluída');
    } catch { showToast('Erro ao excluir'); }
  }, [token, showToast]);

  const startLongPress = useCallback((r, e) => {
    e.preventDefault();
    const el = e.currentTarget;
    longPressTimer.current = setTimeout(() => {
      const rect = el?.getBoundingClientRect();
      setLongPressCtx({id:r.id, label:r.label,
        x: Math.min(rect?.left || 60, window.innerWidth - 200),
        y: Math.min((rect?.bottom || 100) + 4, window.innerHeight - 150)});
    }, 500);
  }, []);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  const handlePanel = id => {
    if(id==='skills')       { setShowSkills(v=>!v); return; }
    if(id==='inicio')       { onExit(); return; }
    if(id==='personalizar') { setShowPersonalizar(v=>!v); return; }
    const paths = { conversas:'/workspace/chats', hoje:'/workspace/today', inbox:'/workspace/inbox', vault:'/workspace/vault', projetos:'/workspace/projects', planejamento:'/workspace/planning', calendario:'/workspace/calendar', tarefas:'/workspace/tasks', biblioteca:'/workspace/library', ferramentas:'/workspace/tools' };
    if (paths[id]) navigate(paths[id]); else setActivePanel(prev => prev===id ? null : id);
  };

  const openLifeOSItem = useCallback((item) => {
    const type = String(item?.type || '').toLowerCase();
    if (item?.project_id) {
      navigate(`/workspace/projects/${item.project_id}`);
    } else if (type === 'task') {
      navigate('/workspace/tasks');
    } else if (type === 'calendar_event' || type === 'reminder') {
      navigate('/workspace/calendar');
    } else if (type === 'routine_block') {
      setShowLifeOSRoutines(true);
    } else if (type === 'open_loop') {
      navigate('/workspace/chats');
    }
  }, [navigate]);

  const SIDEBAR_ITEMS = [
    {id:'inicio',    label:'Início',      D:'M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z'},
    {id:'conversas', label:'Chats',       D:'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z'},
    {id:'hoje',      label:'Hoje',        D:D.wave},
    {id:'inbox',     label:'Inbox',       D:D.book},
    {id:'vault',     label:'Vault',       D:D.folder, page:true},
    {id:'projetos',  label:'Projetos',    D:'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z'},
    {id:'planejamento',label:'Planejamento',D:D.book},
    {id:'calendario',label:'Calendário',D:D.book},
    {id:'tarefas',label:'Tarefas',D:D.check},
    {id:'ferramentas',label:'Ferramentas',D:'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z'},
    {id:'personalizar',label:'Personalizar',D:'M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z'},
    {id:'skills',    label:'Skills 🧠',   D:'M9.5 2A2.5 2.5 0 007 4.5v.1a4 4 0 00-4 4 3.5 3.5 0 002 3.4V17.5a2.5 2.5 0 005 0V12a3.5 3.5 0 002-3.4 4 4 0 00-4-4v-.1A2.5 2.5 0 009.5 2zm5 0A2.5 2.5 0 0117 4.5v.1a4 4 0 014 4A3.5 3.5 0 0119 12v5.5a2.5 2.5 0 01-5 0V12a3.5 3.5 0 01-2-3.4 4 4 0 014-4v-.1A2.5 2.5 0 0014.5 2z'},
    {id:'biblioteca',label:'Biblioteca', D:D.book, page:true},
  ];

  const RECENTS = useMemo(()=>{
    const seen = new Set();
    return sessionList
      .filter(s=>!deletedIds.has(s.session_id) && !seen.has(s.session_id) && seen.add(s.session_id))
      .slice(0,30)
      .map(s=>({id:s.session_id,label:s.preview||'Conversa sem título',updatedAt:s.updatedAt}));
  }, [sessionList, deletedIds]);

  // Collapse só se aplica no desktop — mobile mantém o drawer existente
  const collapsed = !isMobile && sidebarCollapsed;

  return (
    <div style={{height:'100dvh',display:'flex',
      background:'transparent',
      color:C.text,fontFamily:'Inter,system-ui,sans-serif',fontSize:13,overflow:'hidden',
      position:'relative',isolation:'isolate',zIndex:1,
      '--athena-font-scale':fontScale}}>

      <style>{KF}</style>
      <AthenaWorkspaceBg state={chatBgState}/>
      {toast && <Toast msg={toast}/>}

      {/* Modals */}
      {showProfile && <ProfileModal user={user} onClose={()=>setShowProfile(false)}
        onSave={vals=>showToast('Perfil salvo — Athena vai aprender com isso!')}/>}
      {showSkills && <SkillsPanel onClose={()=>setShowSkills(false)} token={token} showToast={showToast}/>}
      {showSkillsModal && <SkillsModal onClose={()=>setShowSkillsModal(false)} token={token} showToast={showToast}
        onOpenLegacy={()=>{setShowSkillsModal(false);setShowSkills(true);}}/>}
      {libSaveItem && <LibrarySavePicker item={libSaveItem} token={token} showToast={showToast}
        onClose={()=>setLibSaveItem(null)}/>}
      {editingCard && <EditPromptModal
        initialPrompt={editingCard.prompt}
        onClose={()=>setEditingCard(null)}
        onRegenerate={async(text)=>{ await onRegenerateCard(editingCard.cardId, text); }}
      />}

      {/* Long-press context menu */}
      {longPressCtx && (
        <>
          <div style={{position:'fixed',inset:0,zIndex:500}} onClick={()=>setLongPressCtx(null)}/>
          <div style={{position:'fixed',left:longPressCtx.x,top:longPressCtx.y,zIndex:501,
            background:C.card,border:`1px solid ${C.border}`,borderRadius:10,
            padding:'4px 0',minWidth:190,boxShadow:'0 8px 28px rgba(0,0,0,.8)',
            animation:'fadeIn .12s ease'}}>
            {[
              {icon:'📌', label: pinned.includes(longPressCtx.id)?'Desafixar':'Fixar',
                fn:()=>{togglePin(longPressCtx.id);setLongPressCtx(null);}},
              {icon:'🗂', label:'Transformar em Projeto',
                fn:()=>{showToast('Conversa movida para Projetos');setLongPressCtx(null);}},
              {icon:'🗑', label:'Excluir', danger:true,
                fn:()=>{deleteSession(longPressCtx.id);setLongPressCtx(null);}},
            ].map(item=>(
              <button key={item.label} onClick={item.fn} style={{
                display:'flex',alignItems:'center',gap:9,width:'100%',
                padding:'9px 14px',background:'none',border:'none',cursor:'pointer',
                color:item.danger?'#ef4444':C.muted,fontSize:12,textAlign:'left',
              }}
                onMouseEnter={e=>{e.currentTarget.style.background=C.border;e.currentTarget.style.color=item.danger?'#f87171':C.text;}}
                onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=item.danger?'#ef4444':C.muted;}}
              >{item.icon} {item.label}</button>
            ))}
          </div>
        </>
      )}

      {/* ── SIDEBAR ── */}
      {/* Mobile backdrop — só no mobile */}
      {isMobile && sidebarOpen && (
        <div onClick={()=>setSidebarOpen(false)} style={{
          position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:199,
          backdropFilter:'blur(3px)',
        }}/>
      )}
      {/* Desktop: sempre visível como flex-child | Mobile: overlay quando aberto */}
      {(!isMobile || sidebarOpen) && (
        <div ref={sidebarRef} style={{
          ...(isMobile
            ? {position:'fixed',top:0,left:0,height:'100vh',zIndex:200,boxShadow:'4px 0 40px rgba(0,0,0,0.9)'}
            : {position:'relative',flexShrink:0}),
          width:isMobile ? 260 : (collapsed ? 48 : sidebarWidth),
          transition:isResizing ? 'none' : 'width 0.22s ease-in-out',
          background:SB.bg,
          borderRight:`1px solid ${SB.border}`,display:'flex',flexDirection:'column',
        }}>

          {/* Conteúdo completo do sidebar — some quando recolhido (desktop) */}
          <div style={{
            display:'flex',flexDirection:'column',flex:1,minHeight:0,
            opacity:collapsed ? 0 : 1,
            transform:collapsed ? 'translateX(-12px)' : 'translateX(0)',
            transition:'opacity 0.2s ease, transform 0.2s ease',
            pointerEvents:collapsed ? 'none' : 'auto',
            overflow:'hidden',
          }}>

          {/* Brand row */}
          <div style={{padding:'18px 16px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:`1px solid ${SB.divider}`}}>
            <div style={{display:'flex',flexDirection:'column',gap:0}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <AthenaLogo size={26} showText={true}/>
              </div>
              <div style={{
                display:'flex',alignItems:'center',gap:6,
                fontSize:11,color:BLUE.textMut,marginTop:2,paddingLeft:2,
              }}>
                <span style={{color:BLUE.textSec,fontWeight:500}}>v2.0</span>
                <span style={{color:BLUE.textMut}}>·</span>
                <span style={{
                  display:'flex',alignItems:'center',gap:4,
                  color:'#22c55e',fontSize:10,fontWeight:600,
                }}>
                  <span style={{
                    width:6,height:6,borderRadius:'50%',
                    background:'#22c55e',
                    boxShadow:'0 0 6px rgba(34,197,94,0.6)',
                    display:'inline-block',
                  }}/>
                  Online
                </span>
              </div>
            </div>
            <div style={{display:'flex',gap:3}}>
              {!isMobile && (
              <button
                onClick={()=>setSidebarCollapsed(v=>!v)}
                title={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
                style={{
                  width:32,height:32,borderRadius:8,border:'none',
                  background:'rgba(255,255,255,0.05)',
                  color:'rgba(150,180,220,0.7)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  cursor:'pointer',flexShrink:0,
                  fontSize:14,fontWeight:700,
                  transition:'all 0.15s ease',
                }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}
                onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}>
                {sidebarCollapsed ? '›' : '‹'}
              </button>
              )}
              {isMobile && (
              <button onClick={()=>setSidebarOpen(false)} style={{width:27,height:27,borderRadius:6,
                background:'none',border:'none',cursor:'pointer',color:C.soft,
                display:'flex',alignItems:'center',justifyContent:'center'}}
                onMouseEnter={e=>e.currentTarget.style.background=C.card}
                onMouseLeave={e=>e.currentTarget.style.background='none'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/>
                </svg>
              </button>
              )}
            </div>
          </div>

          {/* + Nova conversa */}
          <div style={{padding:'12px 12px 10px'}}>
            <button
              onClick={()=>{
                createNewConversation();setSidebarOpen(false);setActiveConvId(null);
                setNewConvClicked(true);setTimeout(()=>setNewConvClicked(false),150);
              }}
              onMouseEnter={()=>setHoveredNew(true)}
              onMouseLeave={()=>setHoveredNew(false)}
              style={{
                display:'flex',alignItems:'center',gap:9,width:'100%',
                padding:'8px 12px',borderRadius:9,cursor:'pointer',fontSize:13,
                background:hoveredNew ? SB.accentSoft : 'rgba(255,255,255,0.03)',
                border:`1px solid ${hoveredNew ? SB.accentBdr : SB.border}`,
                color:hoveredNew ? SB.neon : SB.textSec,
                fontWeight:500,
                boxShadow:'none',
                transform:newConvClicked ? 'scale(0.97)' : 'scale(1)',
                transition:'all 0.18s ease',
                outline:'none',
              }}>
              <span style={{
                width:18,height:18,borderRadius:5,
                border:`1px solid ${hoveredNew ? SB.accentBdr : 'rgba(255,255,255,0.15)'}`,
                display:'flex',alignItems:'center',justifyContent:'center',
                flexShrink:0,fontSize:14,lineHeight:1,color:'inherit',
                transition:'border-color 0.18s',
              }}>+</span>
              <span>Nova conversa</span>
            </button>
          </div>

          {/* Região rolável única — nav + Fixados + Conversas Recentes.
              Nova conversa (acima) e o rodapé de perfil (abaixo) ficam fixos, fora deste scroll. */}
          <div className="athena-sidebar-scroll" style={{flex:1,minHeight:0,overflowY:'auto',
            scrollbarWidth:'thin',scrollbarColor:`${BLUE.scrollbar} transparent`}}>

          {/* Nav items — agrupados por seção */}
          <div style={{padding:'2px 10px 8px'}}>
            {[
              {label:'principal',      ids:['inicio','conversas','hoje','inbox','vault','projetos','planejamento','calendario','tarefas']},
              {label:'criar',          ids:['ferramentas','skills']},
              {label:'personalização', ids:['personalizar','biblioteca']},
            ].map((sec,si)=>(
              <React.Fragment key={sec.label}>
                {si>0 && <SectionDivider/>}
                <SectionHeader label={sec.label} first={si===0}/>
                {sec.ids.map(id=>{
                  const item = SIDEBAR_ITEMS.find(i=>i.id===id);
                  if(!item) return null;
                  const active = item.id==='conversas' ? !activePanel : activePanel===item.id;
                  return (
                    <button key={item.id} onClick={()=>handlePanel(item.id)} style={{
                      display:'flex',alignItems:'center',gap:9,width:'100%',marginBottom:2,
                      padding:'7.5px 10px 7.5px 7px',borderRadius:'0 10px 10px 0',cursor:'pointer',fontSize:12.5,
                      background:active?SB.activeBg:'transparent',border:'none',
                      borderLeft:active?`3px solid ${SB.activeRail}`:'3px solid transparent',
                      color:active?SB.text:SB.textSec,fontWeight:active?600:400,
                      boxShadow:active?`0 0 10px ${SB.accentGlow}`:'none',
                      transition:'all .15s',textAlign:'left',
                      letterSpacing:active?'-0.01em':'0',
                    }}
                      onMouseEnter={e=>{if(!active){e.currentTarget.style.background=SB.hoverBg;e.currentTarget.style.color=SB.text;}}}
                      onMouseLeave={e=>{if(!active){e.currentTarget.style.background='transparent';e.currentTarget.style.color=SB.textSec;}}}
                    >
                      <span style={{display:'flex',opacity:active?1:0.7,filter:active?'brightness(1.4)':'none',color:active?SB.neon:SB.textSec,transition:'all .15s'}}><Ico d={item.D} size={15}/></span>
                      <span style={{flex:1}}>{item.label}</span>
                      {item.page && <span style={{color:SB.textMut,fontSize:12}}>›</span>}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          <div style={{height:1,background:SB.divider,margin:'8px 14px 12px'}}/>

          {/* Fixados — seção de linha única, estilo ChatGPT (sem aparência de botão/card) */}
          <div style={{padding:'0 14px'}}>
            {(()=>{
              const pinnedConvs = RECENTS.filter(r => pinned.includes(r.id));
              const fixOpen = !!univExpanded['fixados'];
              return (
                <>
                  <button
                    onClick={()=>setUnivExpanded(v=>({...v,fixados:!v.fixados}))}
                    style={{
                      display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%',
                      padding:'3px 0',cursor:'pointer',fontSize:13,fontWeight:600,
                      background:'none',border:'none',color:SB.textSec,transition:'color .15s',
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.color=SB.text;}}
                    onMouseLeave={e=>{e.currentTarget.style.color=SB.textSec;}}
                  >
                    <span style={{display:'flex',alignItems:'center',gap:9}}>
                      <span style={{display:'flex',opacity:.75}}><Ico d={D.pin} size={14}/></span>
                      Fixados
                      {pinnedConvs.length>0 && (
                        <span style={{fontSize:10,color:SB.textMut,fontWeight:400}}>{pinnedConvs.length}</span>
                      )}
                    </span>
                    <span style={{color:SB.textMut,fontSize:12,display:'inline-block',
                      transform:fixOpen?'rotate(90deg)':'none',transition:'transform .2s'}}>›</span>
                  </button>

                  {fixOpen && (
                    <div style={{paddingTop:6,paddingBottom:2}}>
                      {pinnedConvs.length===0 ? (
                        <div style={{fontSize:11.5,color:SB.textMut,padding:'2px 0 2px 23px'}}>Nenhuma conversa fixada.</div>
                      ) : pinnedConvs.map((r,i)=>(
                        <button key={r.id||i} onClick={()=>{
                          if(onLoadConversation&&r.id&&r.id.length>10) onLoadConversation(r.id);
                          setActivePanel(null); setSidebarOpen(false);
                        }} style={{
                          display:'flex',alignItems:'center',gap:8,width:'100%',
                          padding:'6px 4px 6px 23px',borderRadius:6,marginBottom:1,
                          background:'none',border:'none',cursor:'pointer',textAlign:'left',transition:'all .12s',
                        }}
                          onMouseEnter={e=>e.currentTarget.style.background=SB.hoverBg}
                          onMouseLeave={e=>e.currentTarget.style.background='none'}>
                          <span style={{fontSize:12,color:SB.textSec,flex:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div style={{height:1,background:SB.divider,margin:'12px 14px'}}/>

          {/* Conversas Recentes */}
          <div style={{padding:'0 10px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 6px 5px',marginTop:2,marginBottom:8}}>
              <span style={{fontSize:9.5,fontWeight:700,color:'rgba(120,160,220,0.38)',letterSpacing:'.14em',textTransform:'lowercase'}}>conversas recentes</span>
              <button onClick={()=>handlePanel('conversas')} style={{fontSize:10,color:'rgba(120,160,220,0.38)',background:'none',border:'none',cursor:'pointer',padding:'0 2px',transition:'color .15s'}}
                onMouseEnter={e=>e.currentTarget.style.color=SB.textSec}
                onMouseLeave={e=>e.currentTarget.style.color='rgba(120,160,220,0.38)'}>
                ver todas ›
              </button>
            </div>
            {RECENTS.map((r,idx)=>{
              const isActiveConv = activeConvId===r.id;
              const isPinned = pinned.includes(r.id);
              return (
              <button key={r.id||idx} className="recent-row"
                onClick={()=>{
                  if(onLoadConversation&&r.id&&r.id.length>10){ onLoadConversation(r.id); setActiveConvId(r.id); }
                  setActivePanel(null);
                  setSidebarOpen(false);
                }}
                onContextMenu={e=>{e.preventDefault();startLongPress(r,e);cancelLongPress();}}
                onTouchStart={e=>startLongPress(r,e)}
                onTouchEnd={cancelLongPress}
                onTouchCancel={cancelLongPress}
                onMouseDown={e=>{ if(e.button===2) return; startLongPress(r,e); }}
                onMouseUp={cancelLongPress}
                onMouseLeave={e=>{cancelLongPress();e.currentTarget.style.background=isActiveConv?BLUE.accentSoft:'none';}}
                style={{
                display:'flex',alignItems:'center',gap:8,width:'100%',
                padding:'7px 8px 7px 5px',borderRadius:'0 10px 10px 0',marginBottom:3,
                background:isActiveConv?SB.activeBg:'transparent',
                border:'none',
                borderLeft:isActiveConv?`3px solid ${SB.activeRail}`:'3px solid transparent',
                cursor:'pointer',textAlign:'left',
                transition:'all .15s',
              }}
                onMouseEnter={e=>{if(!isActiveConv)e.currentTarget.style.background=SB.hoverBg;}}
                onMouseLeave={e=>{if(!isActiveConv)e.currentTarget.style.background='transparent';}}
              >
                <div style={{width:28,height:28,borderRadius:8,flexShrink:0,
                  background:isActiveConv?SB.accentSoft:SB.card,
                  border:`1px solid ${isActiveConv?SB.accentBdr:SB.border}`,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  transition:'all .15s',
                  fontSize:13,
                }}>
                  {r.icon || <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isActiveConv?SB.neon:SB.textMut} strokeWidth="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12.5,fontWeight:isActiveConv?600:400,color:isActiveConv?SB.text:SB.textSec,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',lineHeight:1.3}}>
                    {r.label}
                  </div>
                  {r.updatedAt && (
                    <div style={{fontSize:10,color:SB.textMut,marginTop:1}}>{r.updatedAt}</div>
                  )}
                </div>
                <span
                  role="button"
                  aria-label={isPinned ? 'Desafixar conversa' : 'Fixar conversa'}
                  className={`pin-btn${isPinned ? ' is-pinned' : ''}`}
                  onMouseDown={e=>e.stopPropagation()}
                  onTouchStart={e=>e.stopPropagation()}
                  onClick={e=>{ e.stopPropagation(); togglePin(r.id); }}
                  style={{
                    display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
                    width:20,height:20,borderRadius:6,color:isPinned?SB.neon:SB.textMut,
                  }}
                ><Ico d={D.pin} size={12}/></span>
                {isActiveConv && (
                  <div style={{width:6,height:6,borderRadius:'50%',background:SB.accent,flexShrink:0,
                    boxShadow:`0 0 6px ${SB.accentGlow}`}}/>
                )}
              </button>
              );
            })}
          </div>

          </div>{/* fim da região rolável única */}

          {/* Profile footer — fixo no rodapé, fora do scroll */}
          <div style={{padding:'14px 12px 12px',borderTop:`1px solid ${SB.divider}`,position:'relative',flexShrink:0}}>
            <div style={{
              background:SB.glass,border:`1px solid ${SB.border}`,borderRadius:14,
              padding:'12px',
            }}>
              <button onClick={()=>setShowProfileDrop(v=>!v)} style={{
                display:'flex',alignItems:'center',gap:11,width:'100%',
                background:'none',border:'none',cursor:'pointer',textAlign:'left',padding:0,
              }}>
                <div style={{
                  width:38,height:38,borderRadius:'50%',flexShrink:0,
                  background: avatarUrl ? 'transparent' : 'linear-gradient(135deg, #1B6FE8 0%, #0A52CC 100%)',
                  border:`1.5px solid ${SB.accentBdr}`,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:14,fontWeight:700,color:'#fff',overflow:'hidden',
                  boxShadow:`0 0 10px ${SB.accentGlow}`,
                }}>
                  {avatarUrl
                    ? <img src={avatarUrl} alt={username} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                    : username.slice(0,1).toUpperCase()}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13.5,fontWeight:600,color:SB.text,lineHeight:1.2}}>{username}</div>
                  {['listening','processing','speaking'].includes(voiceState) ? (
                    <div style={{fontSize:10,marginTop:3,color:
                      voiceState==='listening'?SB.accent:voiceState==='processing'?'#f59e0b':SB.neon}}>
                      {voiceState==='listening'?'● Ouvindo...':voiceState==='processing'?'● Processando...':'● Falando...'}
                    </div>
                  ) : (
                    <div style={{display:'flex',alignItems:'center',gap:6,marginTop:3}}>
                      <span style={{fontSize:10,color:SB.textMut}}>Premium 👑</span>
                      <span style={{fontSize:8,fontWeight:700,letterSpacing:'.04em',color:SB.neon,
                        background:SB.neonSoft,border:`1px solid ${SB.accentBdr}`,borderRadius:4,padding:'1px 4px'}}>TI</span>
                    </div>
                  )}
                </div>
                <span style={{color:SB.textMut,fontSize:14,flexShrink:0}}>›</span>
              </button>
            </div>

            {/* Configurações — item de menu solto, sem container */}
            <button onClick={()=>setShowPersonalizar(true)} style={{
              display:'flex',alignItems:'center',gap:9,width:'100%',marginTop:4,
              background:'none',border:'none',cursor:'pointer',textAlign:'left',
              padding:'8px 8px',borderRadius:8,fontSize:12,color:SB.textSec,transition:'all .15s',
            }}
              onMouseEnter={e=>{e.currentTarget.style.background=SB.hoverBg;e.currentTarget.style.color=SB.text;}}
              onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=SB.textSec;}}>
              <Ico d={D.gear} size={13}/>
              <span style={{flex:1}}>Configurações</span>
              <span style={{color:SB.textMut,fontSize:12}}>›</span>
            </button>

            {showProfileDrop && (
              <>
                <div style={{position:'fixed',inset:0,zIndex:300}} onClick={()=>setShowProfileDrop(false)}/>
                <div style={{position:'absolute',left:12,right:12,bottom:'100%',marginBottom:8,zIndex:301,
                  background:C.card,border:`1px solid ${C.border}`,borderRadius:10,
                  padding:'6px 0',boxShadow:'0 -8px 24px rgba(0,0,0,.7)',
                  animation:'fadeIn .15s ease'}}>
                  {[
                    {icon:D.user,  label:'Perfil',          fn:()=>{setShowProfile(true);setShowProfileDrop(false);}},
                    {icon:D.bell,  label:'Notificações',     fn:()=>setShowProfileDrop(false)},
                    {icon:D.gear,  label:'Configurações',    fn:()=>{setShowPersonalizar(true);setShowProfileDrop(false);}},
                    {icon:D.help,  label:'Ajuda',            fn:()=>setShowProfileDrop(false)},
                    ...(installPrompt ? [{icon:'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1M16 12l-4 4m0 0l-4-4m4 4V4', label:'Instalar App', fn:async()=>{installPrompt.prompt();const r=await installPrompt.userChoice;if(r.outcome==='accepted')setInstallPrompt(null);setShowProfileDrop(false);}}] : []),
                    {icon:D.logOut,label:'Sair',             fn:()=>{setShowProfileDrop(false);onExit();}, danger:true},
                  ].map(item=>(
                    <button key={item.label} onClick={item.fn} style={{
                      display:'flex',alignItems:'center',gap:9,width:'100%',
                      padding:'8px 14px',background:'none',border:'none',cursor:'pointer',
                      color:item.danger?'#ef4444':C.muted,fontSize:12,textAlign:'left',
                    }}
                      onMouseEnter={e=>{e.currentTarget.style.background=C.border;e.currentTarget.style.color=item.danger?'#ef4444':C.text;}}
                      onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=item.danger?'#ef4444':C.muted;}}
                    ><Ico d={item.icon} size={13}/>{item.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
          </div>{/* fim do conteúdo completo do sidebar */}

          {/* Faixa recolhida — só desktop */}
          {collapsed && (
            <div style={{
              position:'absolute',top:10,left:0,right:0,bottom:10,
              display:'flex',flexDirection:'column',alignItems:'center',gap:3,
            }}>
              <button onClick={()=>setSidebarCollapsed(false)} title="Expandir menu" style={{
                width:34,height:34,borderRadius:9,border:'none',
                background:'transparent',
                color:SB.textMut,
                display:'flex',alignItems:'center',justifyContent:'center',
                cursor:'pointer',fontSize:15,fontWeight:300,
                transition:'all .15s',
              }}
                onMouseEnter={e=>{e.currentTarget.style.background=SB.hoverBg;e.currentTarget.style.color=SB.textSec;}}
                onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color=SB.textMut;}}>›</button>
              <div style={{height:1,width:22,background:SB.divider,margin:'3px 0'}}/>
              {SIDEBAR_ITEMS.map(item=>{
                const isAct = item.id==='conversas' ? !activePanel : activePanel===item.id;
                return (
                  <button key={item.id} title={item.label} onClick={()=>handlePanel(item.id)} style={{
                    width:34,height:34,borderRadius:9,border:'none',
                    background:isAct?SB.accentSoft:'transparent',
                    color:isAct?SB.neon:SB.textMut,
                    display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',
                    boxShadow:isAct?`0 0 8px ${SB.accentGlow}`:'none',
                    transition:'all .15s',
                  }}
                    onMouseEnter={e=>{if(!isAct){e.currentTarget.style.background=SB.hoverBg;e.currentTarget.style.color=SB.textSec;}}}
                    onMouseLeave={e=>{if(!isAct){e.currentTarget.style.background='transparent';e.currentTarget.style.color=SB.textMut;}}}>
                    <Ico d={item.D} size={15}/>
                  </button>
                );
              })}
            </div>
          )}

          {/* Alça de redimensionamento — só desktop, sidebar expandido */}
          {!isMobile && !sidebarCollapsed && (
            <div
              onMouseDown={(e)=>{
                e.preventDefault();
                setIsResizing(true);
                const startX = e.clientX;
                const startWidth = sidebarWidth;
                const onMove = (ev)=>{
                  const newWidth = Math.min(420, Math.max(260, startWidth + (ev.clientX - startX)));
                  setSidebarWidth(newWidth);
                };
                const onUp = ()=>{
                  setIsResizing(false);
                  window.removeEventListener('mousemove', onMove);
                  window.removeEventListener('mouseup', onUp);
                };
                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
              style={{
                position:'absolute',top:0,right:0,bottom:0,width:4,
                cursor:'col-resize',zIndex:10,
                background:isResizing?'rgba(29,124,255,0.5)':'transparent',
                transition:'background 0.15s',
              }}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(29,124,255,0.25)'}
              onMouseLeave={e=>{if(!isResizing)e.currentTarget.style.background='transparent';}}
            />
          )}
        </div>
      )}

      {/* ── MAIN AREA ── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',
        cursor:isResizing?'col-resize':'default',userSelect:isResizing?'none':'auto'}}>

        {/* Topbar */}
        <div style={{padding:'8px 14px',display:'flex',alignItems:'center',gap:8,
          borderBottom:`1px solid ${C.border}20`,flexShrink:0,minHeight:48}}>

          {/* Hamburger — só no mobile */}
          {isMobile && !sidebarOpen && (
            <button onClick={()=>setSidebarOpen(v=>!v)} style={{width:34,height:34,borderRadius:8,
              background:'none',border:'none',cursor:'pointer',color:C.soft,
              display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}
              onMouseEnter={e=>e.currentTarget.style.background=C.card}
              onMouseLeave={e=>e.currentTarget.style.background='none'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
          )}

          {/* Logo — só no mobile (desktop tem logo no sidebar permanente) */}
          {isMobile && (!activePanel
            ? <AthenaLogo size={28} showText={true}/>
            : <span style={{fontSize:13,color:C.muted}}>
                {SIDEBAR_ITEMS.find(i=>i.id===activePanel)?.label}
              </span>
          )}

          <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:4}}>
            {univData && (
              <div style={{display:'flex',alignItems:'center',gap:5,
                padding:'3px 10px',borderRadius:20,
                background:`${univData.color}10`,border:`1px solid ${univData.color}30`}}>
                <div style={{width:5,height:5,borderRadius:'50%',background:univData.color}}/>
                <span style={{fontSize:10,fontWeight:600,color:univData.color}}>{univData.label}</span>
              </div>
            )}

            {/* Controles do topo: Notificações · Configurações | Avatar */}
            <div className="workspace-top-actions" style={{display:'flex',alignItems:'center',gap:isMobile?6:10}}>
              <NotifBell isMobile={isMobile}/>

              {/* ⚙ Configurações — reutiliza o painel Personalizar/Configurações existente */}
              <button onClick={()=>setShowPersonalizar(true)}
                title="Configurações" aria-label="Abrir configurações"
                style={{width:isMobile?36:40,height:isMobile?36:40,borderRadius:10,background:'none',
                  border:'1px solid transparent',cursor:'pointer',color:C.soft,
                  display:'flex',alignItems:'center',justifyContent:'center',transition:'all .18s'}}
                onMouseEnter={e=>{e.currentTarget.style.background=`${C.accent}0c`;e.currentTarget.style.color=C.text;}}
                onMouseLeave={e=>{e.currentTarget.style.background='none';e.currentTarget.style.color=C.soft;}}>
                <svg width={isMobile?19:21} height={isMobile?19:21} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d={D.gear}/>
                </svg>
              </button>

              <span className="workspace-top-divider" style={{width:1,height:24,background:C.border,flexShrink:0}}/>

              {/* Avatar — abre Personalizar; mesma imagem/fonte do perfil do sidebar */}
              <button onClick={()=>setShowPersonalizar(p=>!p)}
                title="Personalizar perfil" aria-label="Abrir personalização"
                style={{width:isMobile?34:40,height:isMobile?34:40,borderRadius:'50%',padding:2,flexShrink:0,
                  background:showPersonalizar?`${C.accent}18`:'none',
                  border:`1.5px solid ${showPersonalizar?C.accent:`${C.accent}40`}`,
                  cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'all .18s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accent;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=showPersonalizar?C.accent:`${C.accent}40`;}}>
                <div style={{width:'100%',height:'100%',borderRadius:'50%',overflow:'hidden',
                  background:avatarUrl?'transparent':'linear-gradient(135deg, #1B6FE8 0%, #0A52CC 100%)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:13,fontWeight:700,color:'#fff'}}>
                  {avatarUrl
                    ? <img src={avatarUrl} alt={username} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                    : username.slice(0,1).toUpperCase()}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Content: panel or chat */}
        {activePanel ? (
          <div style={{flex:1,overflow:'hidden',background:'#0a0b18'}}>
            {activePanel==='projetos'   && (activeProjectId ? <PanelProjetoAmbiente token={token} projectId={activeProjectId} onBack={()=>navigate('/workspace/projects')} onOpenConversation={sessionId=>openProjectConversation(activeProjectId,sessionId)}/> : <PanelProjetos projects={projects} onClose={()=>navigate('/workspace')} onOpenProject={project=>navigate(`/workspace/projects/${project.project_id}`)}/>)}
            {activePanel==='planejamento' && <PanelPlanejamento token={token} projects={projects} onClose={()=>navigate('/workspace')}/>}
            {activePanel==='calendario' && <PanelCalendario token={token} projects={projects} onClose={()=>navigate('/workspace')}/>}
            {activePanel==='tarefas' && <PanelTarefasOperacional token={token} projects={projects} onClose={()=>navigate('/workspace')}/>}
            {activePanel==='hoje' && (
              <div style={{height:'100%',overflowY:'auto',padding:isMobile ? '12px' : '22px',WebkitOverflowScrolling:'touch'}}>
                <div style={{width:'100%',maxWidth:960,margin:'0 auto',display:'grid',gap:12}}>
                  <TodayPanel
                    onSelectItem={openLifeOSItem}
                    onOpenRoutines={()=>setShowLifeOSRoutines(value=>!value)}
                    onCreateRoutine={()=>setShowLifeOSRoutines(true)}
                  />
                  {showLifeOSRoutines && (
                    <RoutinePanel
                      onClose={()=>setShowLifeOSRoutines(false)}
                      onChanged={()=>showToast('Rotina atualizada.')}
                    />
                  )}
                </div>
              </div>
            )}
            {activePanel==='inbox' && (
              <div style={{height:'100%',overflowY:'auto',padding:isMobile ? '12px' : '22px',WebkitOverflowScrolling:'touch'}}>
                <div style={{width:'100%',maxWidth:960,margin:'0 auto'}}>
                  <InboxPanel projects={projects} onActionComplete={({action})=>{
                    if (action === 'capture') showToast('Captura adicionada à Inbox.');
                  }}/>
                </div>
              </div>
            )}
            {activePanel==='vault' && (
              <div style={{height:'100%',overflowY:'auto',padding:isMobile ? '12px' : '22px',WebkitOverflowScrolling:'touch'}}>
                <div style={{width:'100%',maxWidth:1080,margin:'0 auto'}}>
                  <VaultPanel />
                </div>
              </div>
            )}
            {activePanel==='memorias'   && <PanelMemorias token={token} onClose={()=>navigate('/workspace')}/>}
            {activePanel==='ferramentas'  && <PanelFerramentas onClose={()=>navigate('/workspace')}/>}
            {activePanel==='biblioteca'   && <PanelBiblioteca showToast={showToast} token={token}
              cognitiveProfile={cognitiveProfile} onClose={()=>setActivePanel(null)}/>}
            {/* personalizar abre via modal showPersonalizar */}
          </div>
        ) : (
          <>
            {/* Chat messages */}
            <div ref={scrollContainerRef}
              onScroll={e=>{
                const el=e.currentTarget;
                const bottom=(el.scrollHeight-el.scrollTop-el.clientHeight)<80;
                atBottomRef.current=bottom;
                setShowScrollBtn(!bottom);
              }}
              style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column'}}>
              {isEmpty ? (
                <div style={{flex:1,display:'flex',flexDirection:'column',
                  alignItems:'center',justifyContent:'center',
                  gap:24,padding:'0 24px'}}>
                  <ScaledOrb size={96}
                    isListening={voiceState==='listening'} isSpeaking={voiceState==='speaking'} isProcessing={voiceState==='processing'}/>
                  <div style={{textAlign:'center'}}>
                    <h1 style={{fontSize:28,fontWeight:500,color:C.text,margin:0,letterSpacing:'-.02em',lineHeight:1.3}}>
                      Bem-vindo à <span style={{color:C.accent}}>Athena</span>
                    </h1>
                    <p style={{fontSize:15,color:C.muted,marginTop:8}}>
                      Como posso te ajudar hoje?
                    </p>
                  </div>
                  {!isMobile && (
                  <div style={{
                    display:'grid',
                    gridTemplateColumns:'repeat(3,1fr)',
                    gap:8,width:'100%',maxWidth:520,opacity:0.5,
                  }}>
                    {[
                      {label:'Criar campanha para Instagram', icon:'M23 3a10.9 10.9 0 01-3.14 1.53A4.48 4.48 0 0016 2a4.48 4.48 0 00-4.33 5.5A12.78 12.78 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z'},
                      {label:'Analisar dados do FERMAQ',      icon:'M18 20V10M12 20V4M6 20v-6'},
                      {label:'Gerar relatório estratégico',   icon:'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8'},
                      {label:'Pesquisar tendências de IA',    icon:'M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z'},
                      {label:'Criar apresentação PDF',        icon:'M2 3h20v14H2zM8 21h8M12 17v4'},
                      {label:'Editar vídeo do projeto',       icon:'M23 7l-7 5 7 5V7zM1 5h15v14H1z'},
                    ].map((s,i)=>(
                      <button key={i} onClick={()=>onSend(s.label)} style={{
                        display:'flex',flexDirection:'column',alignItems:'flex-start',
                        gap:8,padding:'12px',borderRadius:12,
                        background:'rgba(255,255,255,0.03)',
                        border:'1px solid rgba(255,255,255,0.07)',
                        cursor:'pointer',textAlign:'left',transition:'all .2s',
                      }}
                        onMouseEnter={e=>{
                          e.currentTarget.closest('div').style.opacity='1';
                          e.currentTarget.style.borderColor=`${C.accentD}`;
                          e.currentTarget.style.background='rgba(0,200,160,0.05)';
                        }}
                        onMouseLeave={e=>{
                          e.currentTarget.closest('div').style.opacity='0.5';
                          e.currentTarget.style.borderColor='rgba(255,255,255,0.07)';
                          e.currentTarget.style.background='rgba(255,255,255,0.03)';
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                          stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round">
                          <path d={s.icon}/>
                        </svg>
                        <span style={{fontSize:11,color:'rgba(255,255,255,0.4)',lineHeight:1.4}}>
                          {s.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  )}
                </div>
              ) : (
                <>
                  {cogMode==='foco_total' && showFocusResume && focusMission && (
                    <FocusResumePrompt
                      mission={focusMission}
                      onResume={()=>setShowFocusResume(false)}
                      onDismiss={()=>{
                        const u={...focusMission,dismissed:(focusMission.dismissed||0)+1};
                        setFocusMission(u); saveMission(u); setShowFocusResume(false);
                      }}
                    />
                  )}
                  <MessageList
                    messages={messages}
                    onSaveMemory={onSaveMemory}
                    showToast={showToast}
                    endRef={endRef}
                    isMobile={isMobile}
                    voiceState={voiceState}
                    thinkingMessages={thinkingMessages}
                    cognitiveProfile={cognitiveProfile}
                    sessionId={sessionId}
                    onOpenLibrarySave={openLibrarySave}
                    onEditPrompt={(cardId, prompt)=>setEditingCard({cardId, prompt: prompt||''})}
                  />
                </>
              )}
            </div>

            {/* ── Ir ao fim — aparece quando usuário sobe na conversa */}
            {showScrollBtn && !isEmpty && (
              <div style={{flexShrink:0,display:'flex',justifyContent:'center',padding:'4px 20px'}}>
                <button onClick={()=>{
                  atBottomRef.current=true;
                  setShowScrollBtn(false);
                  const sc=scrollContainerRef.current;
                  if(sc) sc.scrollTo({top:sc.scrollHeight,behavior:'smooth'});
                }} style={{
                  display:'flex',alignItems:'center',gap:5,
                  background:'rgba(8,14,38,0.92)',
                  border:'1px solid rgba(0,200,160,0.28)',
                  color:'rgba(0,200,160,0.85)',
                  borderRadius:20,padding:'5px 15px',fontSize:11.5,
                  cursor:'pointer',backdropFilter:'blur(10px)',
                  boxShadow:'0 2px 14px rgba(0,0,0,0.5)',letterSpacing:'.02em',
                }}>
                  ↓ Ir ao fim
                </button>
              </div>
            )}

            {/* Focus Mode overlays — strictly gated to Foco Total mode */}
            {cogMode==='foco_total' && focusMission!=null && (
              <FocusMissionCard
                mission={focusMission}
                onAdvance={()=>{
                  if(typeof setFocusMission!=='function') return;
                  const u=advanceMission(focusMission);
                  setFocusMission(u); saveMission(u);
                  const w=u.wins[u.wins.length-1]?.text;
                  if(w&&typeof setFocusWin==='function') setTimeout(()=>setFocusWin(w),100);
                }}
                onDismiss={()=>{
                  saveMission(null);
                  if(typeof setFocusMission==='function') setFocusMission(null);
                }}
              />
            )}
            {cogMode==='foco_total' && typeof focusWin!=='undefined' && (
              <FocusWinToast
                win={focusWin||null}
                onDone={()=>{ if(typeof setFocusWin==='function') setFocusWin(null); }}
              />
            )}

            <Composer
              onSend={handleSend}
              voiceState={voiceState}
              voiceMode={voiceMode}
              onMicToggle={onMicToggle}
              onToggleVoiceMode={onToggleVoiceMode}
              continuousVoice={continuousVoice}
              onToggleContinuousVoice={onToggleContinuousVoice}
              onOpenVisualCall={()=>navigate('/visual-call')}
              cogMode={cogMode}
              setCogMode={setCogMode}
              onOpenSkills={openSkills}
              setInputRef={setInputRef}
              inputValueRef={inputValueRef}
              composerRef={composerRef}
              isMobile={isMobile}
              isEmpty={isEmpty}
              token={token}
              projectId={activeProjectId}
              sessionId={sessionId}
              pendingDeviceItems={deviceContext.items}
              onConsumeDeviceItem={deviceContext.consume}
              onRemoveDeviceItem={deviceContext.remove}
              onAttachmentError={showToast}
            />
          </>
        )}
      </div>

      {/* Modal de Personalização — overlay sobre a workspace */}
      {showPersonalizar && (
        <PanelPersonalizar
          onClose={() => { setShowPersonalizar(false); fetchProfileData(); }}
          user={user}
          onLogout={onExit}
          token={token}
          fontScale={fontScale}
          onFontScale={setFontScale}
        />
      )}

    </div>
  );
}

// ─── Main WorkspacePage ────────────────────────────────────────────────────────
export default function WorkspacePage({user, onNavigateHome}) {
  const navigate  = useNavigate();
  const location = useLocation();
  // Read fresh on every call — avoids stale token after 60-min JWT expiry
  const getToken  = () => localStorage.getItem('athena_token');
  const token     = getToken(); // used only for non-callback contexts
  const { prepareChatContext } = useAthenaDeviceContext();

  const [messages,     setMessages]     = useState([]);
  const [sessionId,    setSessionId]    = useState(null);
  const [sessionList,  setSessionList]  = useState([]);
  const [projects,     setProjects]     = useState([]);
  const workspaceContext = useMemo(() => {
    const parts = location.pathname.replace(/^\/workspace\/?/, '').split('/').filter(Boolean);
    const sectionMap = { chats: 'chats', today: 'today', inbox: 'inbox', vault: 'vault', projects: 'projects', planning: 'planning', calendar: 'calendar', tasks: 'tasks', memories: 'memories', library: 'library' };
    return { section: sectionMap[parts[0]] || 'workspace_home', project_id: parts[0] === 'projects' ? parts[1] : undefined, planning_id: parts[0] === 'planning' ? parts[1] : undefined };
  }, [location.pathname]);
  const sendMessageRef    = useRef(null);
  const savedInputRef     = useRef('');
  const {
    voiceState,
    continuousMode: continuousVoice,
    startActive, toggleManual, finishActive, startSpeaking, doneSpeaking, finishResponseWithoutSpeech,
    stop: engineStop,
    setProcessing: engineSetProcessing, setIdle: engineSetIdle,
    setContinuousMode: setEngineContMode,
  } = useVoiceEngine({
    context: 'workspace',
    // O contínuo só passa a captar depois que o usuário o ativa. Isso evita
    // abrir o microfone no carregamento e deixa manual/contínuo previsíveis.
    initialContinuousMode: false,
    onDispatch: async (rawText) => {
      const full = (savedInputRef.current + ' ' + rawText).trim();
      savedInputRef.current = '';
      setInputRef.current?.('');
      if (!full) {
        finishResponseWithoutSpeech();
        return;
      }
      const send = sendMessageRef.current;
      if (!send) {
        finishResponseWithoutSpeech();
        return;
      }
      try {
        const outcome = await send(full, [], true);
        // VoiceEngine has already entered PROCESSING by the time a final
        // transcript is dispatched. A refusal before a request starts must
        // close that turn, otherwise the Workspace composer stays disabled.
        if (outcome?.accepted === false) finishResponseWithoutSpeech();
      } catch {
        finishResponseWithoutSpeech();
      }
    },
    onPartialResult: (partial) => {
      setInputRef.current?.(savedInputRef.current + partial);
    },
    onCaptureFailure: (failure) => {
      const denied = ['not-allowed', 'service-not-allowed'].includes(failure?.reason);
      const recognizerPermissionError = failure?.reason === 'recognizer_permission_error';
      showToast(
        denied
          ? 'A Athena não recebeu acesso ao microfone. Libere a permissão e tente novamente.'
          : recognizerPermissionError
            ? 'A permissão está ativa, mas o serviço de voz do Android não conseguiu acessar o áudio. Verifique o microfone global e o reconhecimento de voz do aparelho.'
            : 'Não consegui transcrever sua fala. Tente novamente.',
      );
    },
  });
  // [WorkspaceVoice] Fonte única de verdade do TTS do Workspace — desligado por padrão.
  const [voiceMode,       setVoiceMode]       = useState(false);
  const voiceModeRef      = useRef(false); // evita stale closure em sendMessage/speakText
  const [cogMode,         setCogMode]         = useState(()=>{
    const s = localStorage.getItem('athena_cog_mode');
    // Migrate legacy mode names to Athena 2.0
    if (!s || s === 'basico' || s === 'complexo' || s === 'continuo') return 'athena2';
    return s;
  });
  const voiceStateRef     = useRef('idle');  // sync mirror para callbacks de setInterval
  const currentAudioRef   = useRef(null);
  const ttsAbortRef       = useRef(null);
  const speakingLockRef   = useRef(false);
  const speechQueueRef    = useRef([]);
  const processingLockRef = useRef(false);
  const lastSentRef       = useRef({text:'',ts:0});
  const setInputRef           = useRef(null);
  const inputValueRef         = useRef('');
  const lastActivityRef       = useRef(Date.now());
  const lastAmbientRef        = useRef(0);
  // [SessionLifecycle] Session race condition fix
  const sessionIdRef        = useRef(null);
  const pendingCalendarRequestRef = useRef('');
  const pendingTaskRequestRef = useRef('');
  const sessionRestoringRef = useRef(true);
  const restoreGenRef       = useRef(0);
  const pendingMsgRef       = useRef(null);
  const speakTextRef          = useRef(null);
  const speechGenRef          = useRef(0); // invalida TTS em voo quando cancelado/superado

  const [focusProfile,     setFocusProfile]    = useState(null);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [driftSuggestion,  setDriftSuggestion]  = useState(null);
  const [insightSuggestion,setInsightSuggestion] = useState(null);
  const focusProfileRef = useRef(null);
  const workspaceProfile = useMemo(
    () => focusProfile?.cognitive ? buildWorkspaceProfile(focusProfile.cognitive) : null,
    [focusProfile]
  );
  const [focusMission,    setFocusMission]    = useState(null);
  const [focusWin,        setFocusWin]        = useState(null);
  const [showFocusResume, setShowFocusResume] = useState(false);
  const focusMissionRef   = useRef(null);

  const [thinkingType, setThinkingType] = useState(null);
  const [chatBgState, setChatBgState] = useState('idle');

  useEffect(()=>{ voiceStateRef.current = voiceState; },[voiceState]);
  useEffect(()=>{
    voiceModeRef.current = voiceMode;
    if(process.env.NODE_ENV!=='production') console.log(`[WorkspaceVoice] enabled=${voiceMode}`);
  },[voiceMode]);
  useEffect(()=>{ focusMissionRef.current = focusMission; },[focusMission]);
  // Reset profile selection whenever user leaves Foco Total mode
  useEffect(()=>{ if(cogMode !== 'foco_total') setFocusProfile(null); },[cogMode]);
  useEffect(()=>{
    // On mount: load persisted focus mission if in Foco Total mode
    const saved = loadMission();
    if(saved && saved.dismissed < 3 && saved.progress < 100){
      setFocusMission(saved);
      // Only show resume prompt if cogMode starts as foco_total
      const storedMode = localStorage.getItem('athena_cog_mode');
      if(storedMode === 'foco_total') setShowFocusResume(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  // Contextual notifications scheduler (runs while workspace is open)
  useEffect(()=>{
    const cleanup = startContextualScheduler({
      userName: user?.name || 'Vitor',
      getLastSummary: () => {
        const last = messages.filter(m=>m.role==='assistant').slice(-1)[0];
        return last ? last.content.slice(0, 80) : '';
      },
    });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aplica preferências visuais salvas (fonte, tamanho) ao carregar o workspace
  useEffect(() => {
    try {
      const cached = localStorage.getItem('athena_prefs');
      if (cached) applyPreferences(JSON.parse(cached));
    } catch {}
  }, []);

  useEffect(() => {
    if (voiceState === 'processing') {
      setChatBgState('processing');
    } else if (voiceState === 'speaking') {
      setChatBgState('streaming');
    } else {
      setChatBgState(prev => (prev === 'streaming' || prev === 'processing') ? 'done' : 'idle');
      const t = setTimeout(() => setChatBgState('idle'), 2200);
      return () => clearTimeout(t);
    }
  }, [voiceState]);

  // B — Ambient message: check every 60s, fire only after 10min inactivity
  useEffect(()=>{
    const TEN_MIN = 10*60*1000;
    const timer = setInterval(async ()=>{
      if(document.visibilityState!=='visible') return;
      if(voiceStateRef.current!=='idle') return;
      const now=Date.now();
      if(now-lastActivityRef.current < TEN_MIN) return;
      if(now-lastAmbientRef.current < TEN_MIN) return;
      const t=getToken(); if(!t) return;
      try{
        const ambientSessionId=sessionIdRef.current;
        const {data}=await axios.post(`${API_BASE}/ambient-message`,{session_id:ambientSessionId},{headers:{Authorization:`Bearer ${t}`},withCredentials:true});
        if(ambientSessionId!==sessionIdRef.current) return;
        if(data.message){
          lastAmbientRef.current=Date.now();
          setMessages(prev=>[...prev,{role:'assistant',content:data.message}]);
          speakTextRef.current?.(data.message);
        }
      }catch{}
    }, 60000);
    return ()=>clearInterval(timer);
    /* eslint-disable-next-line */
  }, []);

  // Load session list + last session
  useEffect(()=>{
    const t = getToken();
    if(!t){
      sessionRestoringRef.current=false;
      return;
    }
    // `sendMessage` claims its processing lock synchronously before the
    // first await. Replaying in this same turn prevents a second UI event from
    // stealing that lock in the tiny timer window that used to exist here.
    // If a replay is refused, restore the text without replacing a newer
    // draft that may already be in the composer.
    const replayQueuedMessage = (pending) => {
      const queued = typeof pending === 'string'
        ? { text:pending, attachments:[], isVoice:false, sourceContext:null }
        : pending;
      const text = String(queued?.text || '').trim();
      if(!text && !(queued?.attachments?.length)) return;
      const send = sendMessageRef.current;
      if(!send){
        pendingMsgRef.current=queued;
        return;
      }
      void Promise.resolve(send(text,queued.attachments||[],queued.isVoice===true,queued.sourceContext||null))
        .then((outcome)=>{
          if(outcome?.accepted!==false) return;
          if(queued?.isVoice) finishResponseWithoutSpeech();
          setInputRef.current?.((current)=>String(current||'').trim()?current:text);
        })
        .catch(()=>{
          if(queued?.isVoice) finishResponseWithoutSpeech();
          setInputRef.current?.((current)=>String(current||'').trim()?current:text);
        });
    };
    // [SessionLifecycle] Generation counter prevents stale restoration from overwriting active session
    const _gen = ++restoreGenRef.current;
    (async()=>{
      try {
        const {data: projectData} = await axios.get(`${API_BASE}/projects`,
          {headers:{Authorization:`Bearer ${t}`},withCredentials:true,timeout:15_000})
          .catch(()=>({data:{projects:[]}}));
        if(_gen !== restoreGenRef.current) return;
        setProjects(projectData?.projects||[]);
        const {data} = await axios.get(`${API_BASE}/memory/sessions`,
          {headers:{Authorization:`Bearer ${t}`},withCredentials:true,timeout:15_000});
        if(_gen !== restoreGenRef.current) return;
        const sessions = data?.sessions||[];
        setSessionList(sessions.map(s=>{
          const now = new Date();
          const upd = s.updated_at ? new Date(s.updated_at) : null;
          let updatedAt = '';
          if(upd){
            const diffMin = Math.floor((now-upd)/60000);
            if(diffMin<1) updatedAt = 'agora';
            else if(diffMin<60) updatedAt = `há ${diffMin} min`;
            else {
              const h = Math.floor(diffMin/60);
              if(h<24) updatedAt = `há ${h}h`;
              else if(h<48) updatedAt = 'ontem';
              else updatedAt = upd.toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit'});
            }
          }
          return {
            session_id: s.session_id,
            preview: s.title || s.last_message?.slice(0,45) || 'Conversa sem título',
            updatedAt,
          };
        }));
        const activeSessionId = localStorage.getItem(ACTIVE_SESSION_STORAGE_KEY);
        const recent = sessions.find(s=>s.session_id===activeSessionId) || sessions[0];
        if(recent?.session_id && !sessionIdRef.current){
          const {data:full} = await axios.get(`${API_BASE}/memory/sessions/${recent.session_id}`,
            {headers:{Authorization:`Bearer ${t}`},withCredentials:true,timeout:15_000});
          if(_gen !== restoreGenRef.current || sessionIdRef.current) return;
          sessionIdRef.current = recent.session_id;
          setSessionId(recent.session_id);
          localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, recent.session_id);
          const msgs=full?.messages||[];
          setMessages(msgs);
          sessionRestoringRef.current = false;
          console.log('[SessionLifecycle] workspace restoration done, sessionId:', recent.session_id);
          const _pending = pendingMsgRef.current;
          if(_pending){
            pendingMsgRef.current=null;
            replayQueuedMessage(_pending);
          }
          else if(msgs.length>=2){
            axios.post(`${API_BASE}/chat/reentry`,{session_id:recent.session_id},
              {headers:{Authorization:`Bearer ${t}`},withCredentials:true,timeout:15_000})
              .then(({data:rd})=>{
                if(_gen!==restoreGenRef.current || sessionIdRef.current!==recent.session_id) return;
                if(rd.message) setMessages(prev=>[...prev,{role:'assistant',content:rd.message}]);
              }).catch(()=>{});
          }
          return;
        }
      } catch(err) {
        const failure=normalizeAthenaApiFailure(err);
        if(isAthenaSessionFailure(failure)){
          signalAthenaAuthExpired({token:t});
          navigate('/login',{replace:true});
        }
      }
      if(_gen === restoreGenRef.current){
        sessionRestoringRef.current = false;
        console.log('[SessionLifecycle] workspace restoration done (no session)');
        const _pending = pendingMsgRef.current;
        if(_pending){
          pendingMsgRef.current=null;
          replayQueuedMessage(_pending);
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>()=>{
    if(currentAudioRef.current){try{currentAudioRef.current.pause();}catch{}}
  },[]);

  // [WorkspaceVoice] Interrupção real do TTS — cancela áudio, fila e indicador "falando".
  const stopSpeech = useCallback((reason='unknown')=>{
    const wasSpeaking = !!currentAudioRef.current || voiceStateRef.current==='speaking';
    speechGenRef.current++; // invalida qualquer speakText() ainda em voo (ex: fetch de TTS pendente)
    ttsAbortRef.current?.abort();
    ttsAbortRef.current=null;
    if(currentAudioRef.current){
      try{currentAudioRef.current.pause();currentAudioRef.current.removeAttribute('src');currentAudioRef.current.load();}catch{}
      currentAudioRef.current=null;
    }
    speakingLockRef.current=false;
    speechQueueRef.current=[];
    if(voiceStateRef.current==='speaking') engineSetIdle();
    if(wasSpeaking && process.env.NODE_ENV!=='production') console.log(`[WorkspaceVoice] cancel reason=${reason}`);
  },[engineSetIdle]);

  const speakText = useCallback(async text=>{
    const clean=stripEmojis(text);
    const t=getToken();
    if(!clean||!t) return;
    // [WorkspaceVoice] guarda obrigatória: nunca iniciar TTS com a voz desligada
    if(!voiceModeRef.current) return;
    if(speakingLockRef.current){ speechQueueRef.current.push(clean); return; }
    speakingLockRef.current=true;
    const _mid = Date.now();
    if(process.env.NODE_ENV!=='production') console.log(`[WorkspaceVoice] speak_start message_id=${_mid}`);
    let nextText=clean;
    let playbackStarted=false;
    while(nextText && voiceModeRef.current){
      const myGen=++speechGenRef.current;
      const controller=new AbortController();
      ttsAbortRef.current=controller;
      const result=await requestAthenaTts({api:API_BASE,token:t,text:nextText,signal:controller.signal});
      if(speechGenRef.current!==myGen || !voiceModeRef.current) return;
      if(result?.audio){
        await playAthenaAudio(result.audio,{
          signal:controller.signal,
          onCreated:(audio)=>{currentAudioRef.current=audio;},
          onStart:()=>{ playbackStarted=true; startSpeaking(); },
        });
      } else if(process.env.NODE_ENV!=='production') {
        console.warn('[WorkspaceVoice] remote TTS unavailable; response remains text-only');
      }
      if(speechGenRef.current!==myGen) return;
      currentAudioRef.current=null;
      ttsAbortRef.current=null;
      nextText=speechQueueRef.current.shift()||null;
    }
    speakingLockRef.current=false;
    if(playbackStarted) doneSpeaking(); // engine decide: startActive (continuousMode) ou IDLE
    else finishResponseWithoutSpeech();
    if(process.env.NODE_ENV!=='production') console.log(`[WorkspaceVoice] speak_end message_id=${_mid}`);
  },[startSpeaking, doneSpeaking, finishResponseWithoutSpeech]);
  useEffect(()=>{ speakTextRef.current = speakText; },[speakText]);

  const sendMessage = useCallback(async (text, attachments=[], isVoice=false, sourceContext=null)=>{
    if(!text?.trim() && attachments.length===0) return { accepted:false, reason:'empty' };
    const fromQuickOverlay = sourceContext?.source === 'floating_overlay';
    if(processingLockRef.current) return { accepted:false, reason:'processing' };
    const _perf = process.env.NODE_ENV!=='production' ? performance.now() : 0;
    const msgText = text?.trim()||'';
    const now=Date.now();
    if(msgText && lastSentRef.current.text===msgText&&now-lastSentRef.current.ts<2500) {
      return { accepted:false, reason:'duplicate' };
    }
    // [SessionLifecycle] Block during active restoration — queue and auto-send when ready
    if(sessionRestoringRef.current){
      // The native card cannot receive the result of a delayed replay. Leave
      // this text unsent so a retry after restoration cannot duplicate it.
      if(fromQuickOverlay) return {
        accepted:false,
        reason:'restoring',
        response:'A Athena ainda está carregando sua conversa. Esta mensagem não foi enviada; aguarde alguns instantes e envie novamente.',
      };
      if(pendingMsgRef.current) return { accepted:false, reason:'restoring' };
      // Do not mark lastSent yet: this message has not reached the chat
      // request. The restoration callback will replay this complete payload.
      pendingMsgRef.current={text:msgText||text,attachments,isVoice,sourceContext};
      return { accepted:true, queued:true };
    }
    if(msgText) lastSentRef.current={text:msgText,ts:now};
    processingLockRef.current=true;
    lastActivityRef.current=Date.now();
    if(cogMode==='foco_total' && focusProfileRef.current?.id) trackMessageSignals(focusProfileRef.current.id, msgText);
    stopSpeech();
    const msgAttachments = attachments.length ? attachments.map(a=>{
      const mimeType = a.type || a.mimeType || 'application/octet-stream';
      return {
        name: a.name || 'Arquivo',
        type: mimeType,
        size: Number(a.size || 0) || 0,
        file_id: a.file_id || null,
        attachment_id: a.attachment_id || null,
        // Only legacy web files carry a preview. Private Android bytes never
        // pass through the WebView just to render a thumbnail.
        preview: a.data && mimeType.startsWith('image/') ? `data:${mimeType};base64,${a.data}` : null,
      };
    }) : undefined;
    setMessages(prev=>[...prev,{role:'user', content:msgText, attachments:msgAttachments, isVoice}]);
    setThinkingType(detectThinkingType(msgText, attachments, cogMode));
    // Detect focus mission from user message (only in Foco Total mode)
    let _activeMission = focusMissionRef.current;
    if(cogMode==='foco_total' && !_activeMission && msgText.length > 12){
      const _det = detectFocusMission(msgText);
      if(_det){ _activeMission=_det; setFocusMission(_det); saveMission(_det); focusMissionRef.current=_det; }
    }
    setChatBgState('thinking');
    engineSetProcessing();
    const t=getToken();
    if(!t){
      processingLockRef.current=false;
      setThinkingType(null);
      setChatBgState('idle');
      engineSetIdle();
      navigate('/');
      return {
        accepted:false,
        reason:'not_authenticated',
        response:'Abra a Athena e entre na sua conta antes de usar a janela rápida.',
      };
    }
    try {
    // The exact same action boundary serves Home, Workspace and Quick Athena.
    // It executes only after a real provider result and never trusts model
    // prose as evidence that something was created.
    if (attachments.length === 0) {
      const directIntent = detectAthenaOperationalIntent(msgText, { conversation: messages });
      const actionInput = (!directIntent && pendingCalendarRequestRef.current)
        ? `${pendingCalendarRequestRef.current} às ${msgText}`
        : (!directIntent && pendingTaskRequestRef.current ? `${pendingTaskRequestRef.current} ${msgText}` : msgText);
      const actionResult = await executeAthenaOperationalAction({
        message: actionInput,
        apiBase: API_BASE,
        token: t,
        sessionId: sessionIdRef.current,
        projectId: workspaceContext.project_id || null,
        conversation: messages,
      });
      if (actionResult) {
        if (actionResult.type === 'CREATE_CALENDAR_EVENT') {
          pendingCalendarRequestRef.current = actionResult.status === 'needs_input' ? actionInput : '';
        }
        if (actionResult.type === 'CREATE_TASK') {
          pendingTaskRequestRef.current = actionResult.status === 'needs_input' ? actionInput : '';
        }
        const answer = actionResult.userMessage;
        const actionMessages = [{
          role:'assistant', content:answer,
          response_presentation: actionResult.responsePresentation || null,
          response_message_id: actionResult.responseMessageId || null,
          card_id: actionResult.responseMessageId || null,
          session_id: actionResult.sessionId || sessionIdRef.current || null,
          action_result: actionResult,
          attachments: actionResult.pendingAttachments || undefined,
        }];
        if (actionResult.success && actionResult.imageUrl) {
          actionMessages.push({
            role: 'assistant', type: 'visual_aid', isLoading: false,
            imageUrl: actionResult.imageUrl,
            prompt: actionResult.promptUsed || actionInput,
            provider: actionResult.provider,
            image: actionResult.image || null,
          });
        }
        setMessages(prev=>[...prev,...actionMessages]);
        if (actionResult.sessionId) { setSessionId(actionResult.sessionId); sessionIdRef.current=actionResult.sessionId; localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY,actionResult.sessionId); }
        processingLockRef.current=false; setThinkingType(null); setChatBgState('idle'); engineSetIdle();
        if (voiceModeRef.current) speakText(answer);
        return { accepted:true, handled:'action', response: answer };
      }
    }
    // Profile switch detection ("não é o Vitor, é a Juliana")
    if(msgText.length < 120){
      const _sw = detectProfileSwitch(msgText);
      if(_sw.detected) setActiveProfile(_sw.profileId);
    }
      const _fp = focusProfileRef.current;
      const _cogPayload = _fp?.cognitive
        ? {cognitive_profile:_fp.cognitive, active_profile_name:_fp.name}
        : {};
      // Fase 5: response profile + cognitive directive
      const _rp = (cogMode==='foco_total' && _fp?.cognitive) ? buildResponseProfile(_fp.cognitive) : null;
      const _overrides = (cogMode==='foco_total' && _fp?.id) ? getPreferenceOverrides(_fp.id) : {};
      const _directive = (cogMode==='foco_total' && _fp?.cognitive) ? buildCognitivePrompt(_fp.cognitive, _fp.id) : '';
      const _adaptPayload = {
        ...(_rp       ? {response_profile:    _rp}       : {}),
        ...(_directive ? {cognitive_directive: _directive} : {}),
      };
      // Ações rápidas (Explicar Melhor, Resumir, Organizar, Em Passos, Gerar Imagem):
      // ancora a mensagem na resposta clicada, não na última mensagem global.
      const _sourcePayload = sourceContext?.sourceResponseText ? {
        action_type: sourceContext.actionType,
        source_message_id: sourceContext.sourceMessageId,
        source_response_text: sourceContext.sourceResponseText,
        source_user_message: sourceContext.sourceUserMessage,
      } : {};
      if(sourceContext?.sourceResponseText && process.env.NODE_ENV!=='production'){
        console.log('[Athena QuickAction] payload:', {message:msgText, session_id:sessionIdRef.current, ..._sourcePayload});
      }
      const _clientInstant = new Date();
      const _clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Fortaleza';
      const _clientTimeDisplay = new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'full', timeStyle: 'medium', timeZone: _clientTimezone,
      }).format(_clientInstant);
      const attachmentRefs = [...new Set(attachments.map((attachment) => attachment.attachment_id).filter(Boolean))];
      const fileIds = [...new Set(attachments.map((attachment) => attachment.file_id).filter(Boolean))];
      const legacyAttachments = attachments.filter((attachment) => !attachment.file_id && attachment.data);
      // This runs only after the user explicitly submits a message containing
      // a clear battery/network/audio-route request. It returns a proof only;
      // raw device state never joins the chat payload.
      let deviceContext = null;
      try { deviceContext = await prepareChatContext(msgText); } catch { /* Context is optional and cannot block chat. */ }
      const {data}=await axios.post(`${API_BASE}/chat/message`,
        {message:msgText,session_id:sessionIdRef.current,mode:cogMode,local_hour:getCurrentGreeting().hour,client_time:_clientInstant.toISOString(),client_timezone:_clientTimezone,client_time_display:_clientTimeDisplay,attachment_refs:attachmentRefs.length?attachmentRefs:undefined,file_ids:fileIds.length?fileIds:undefined,attachments:legacyAttachments.length?legacyAttachments:undefined,social_prefs:loadSocialPrefs(),workspace_context:workspaceContext,..._cogPayload,..._adaptPayload,..._sourcePayload,...(deviceContext?{device_context:deviceContext}:{})},
        {headers:{Authorization:`Bearer ${t}`},withCredentials:true,timeout:30_000});
      if(data?.is_error || !String(data?.response||'').trim()){
        const responseError=new Error(data?.error_code||'ATHENA_EMPTY_RESPONSE');
        responseError.response={status:data?.is_error?503:502,data};
        throw responseError;
      }
      const rawResp=(data.response||'').replace(/<action>[\s\S]*?<\/action>/g,'').trim();
      // Correção 3 Camadas 1+2: layout engine metadata
      const _layoutType = data.layout_type      || 'simple_conversation';
      const _presType   = data.presentation_type || 'conversa_comum';
      const _presMeta   = data.presentation_meta || null;
      // Recipe Showcase: apresentação estruturada vinda do backend (JSON pronto)
      const _wsPresentation = data.workspace_presentation || null;
      // Novo contrato semântico. Ele é opcional: mensagens antigas e qualquer
      // estrutura inválida continuam no renderer Markdown legado.
      const _responsePresentation = data.response_presentation || null;
      const _responseMessageId = data.response_message_id || null;
      // Editar Prompt: id estável do card, compartilhado pela pergunta e pela resposta
      const _cardId = data.card_id || null;
      // Correção 3 Camada 6: texto limpo para TTS
      const _voiceText  = data.voice_text        || '';
      const { stepDone, cleanText: afterFocus } = parseFocusUpdate(rawResp);
      const { notifBody, cleanText } = extractNotificationCommand(afterFocus);
      // Tags emitted by a model are never an action. Real reminders go
      // through AthenaActionOrchestrator before the model is called.
      const resp = notifBody
        ? 'Para criar um lembrete real, diga o horário e o que devo lembrar — por exemplo: “me lembre às 15h de enviar o orçamento”.'
        : (cleanText.trim() || rawResp || '');
      if(stepDone && _activeMission){
        const _upd = advanceMission(_activeMission);
        setFocusMission(_upd); saveMission(_upd); focusMissionRef.current=_upd;
        const _win = _upd.wins[_upd.wins.length-1]?.text;
        if(_win) setTimeout(()=>setFocusWin(_win), 600);
      }
      if(data.session_id){
        setSessionId(data.session_id);
        sessionIdRef.current=data.session_id;
        localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY,data.session_id);
      }
      // Backfill do card_id na mensagem do usuário já renderizada (empurrada antes do POST)
      if(_cardId){
        setMessages(prev=>{
          const idx=[...prev].reverse().findIndex(m=>m.role==='user'&&!m.card_id);
          if(idx===-1) return prev;
          const realIdx=prev.length-1-idx;
          const m=[...prev];
          m[realIdx]={...m[realIdx],card_id:_cardId};
          return m;
        });
      }
      setChatBgState('responding');
      setMessages(prev=>[...prev,{role:'assistant',content:'',streaming:true}]);
      // Animate response streaming — ~16 chars per frame at 60fps
      let streamed='';
      const CHUNK=16,FRAME=16;
      function doStream(){
        streamed+=resp.slice(streamed.length,Math.min(streamed.length+CHUNK,resp.length));
        const done=streamed.length>=resp.length;
        setMessages(prev=>{
          if(!prev.length) return prev;
          const m=[...prev];
          m[m.length-1]={
            role:'assistant',content:streamed,streaming:!done,
            ...(done ? {layout_type:_layoutType,presentation_type:_presType,presentation_meta:_presMeta,workspace_presentation:_wsPresentation,response_presentation:_responsePresentation,response_message_id:_responseMessageId,card_id:_cardId,session_id:sessionIdRef.current} : {}),
          };
          return m;
        });
        if(!done){setTimeout(doStream,FRAME);return;}
        if(process.env.NODE_ENV!=='production'){
          const _ms=Math.round(performance.now()-_perf);
          console.log(`[Athena Perf] mode=${cogMode} total=${_ms}ms resp=${resp.length}ch`);
        }
        processingLockRef.current=false;
        setThinkingType(null);
        setTimeout(()=>setChatBgState('idle'),2200);
        lastActivityRef.current=Date.now();
        // [WorkspaceVoice] usa a ref (não o state fechado no closure) para refletir
        // o valor mais recente do toggle, mesmo se o usuário mudou durante o streaming.
        const _shouldSpeak = voiceModeRef.current;
        if(process.env.NODE_ENV!=='production') console.log(`[WorkspaceVoice] response_ready shouldSpeak=${_shouldSpeak}`);
        if(_shouldSpeak && !fromQuickOverlay) speakText(_voiceText || stripMarkdown(resp));
        else engineSetIdle();
        if(data.follow_up){
          const fu=data.follow_up;
          setTimeout(()=>setMessages(prev=>[...prev,{role:'assistant',content:fu}]),3000);
        }
        // Image generation is an explicit paid/external action. It is handled
        // before the model call by AthenaActionOrchestrator, which receives a
        // verified provider result. Do not auto-spend quota from a focus mode
        // or a model response here.
        // Fase 5: preference learning — post-response, non-blocking
        if(cogMode==='foco_total' && focusProfileRef.current?.id){
          const _pid=focusProfileRef.current.id;
          trackPreferenceSignal(_pid, msgText);
          const _ins=checkInsightSuggestion(_pid);
          if(_ins && !isInsightDismissed(_pid,_ins.signal)) setInsightSuggestion(_ins);
        }
      }
      setTimeout(doStream,FRAME);
      return { accepted:true, response: resp };
    } catch(err) {
      const failure=normalizeAthenaApiFailure(err);
      processingLockRef.current=false;
      if(lastSentRef.current.text===msgText) lastSentRef.current={text:'',ts:0};
      setThinkingType(null);
      setChatBgState('idle');
      engineSetIdle();
      if(isAthenaSessionFailure(failure)){
        signalAthenaAuthExpired({token:t});
        navigate('/login',{replace:true});
        return {
          accepted:false,
          reason:'not_authenticated',
          response:'Sua sessão expirou. Abra a Athena para entrar novamente.',
        };
      }
      console.error('[Athena] sendMessage error:', failure.code);
      setMessages(prev=>[...prev,{role:'assistant',content:failure.userMessage}]);
      return {
        accepted:false,
        reason:failure.code||'request_failed',
        response:failure.userMessage,
      };
    }
  },[speakText, stopSpeech, navigate, cogMode, workspaceContext, prepareChatContext, messages, engineSetProcessing, engineSetIdle]);

  // Editar Prompt — regenera o mesmo card (mesmo card_id) com um prompt novo.
  // Não cria mensagem nova: atualiza o balão do usuário e substitui o conteúdo
  // do card assistente in-place. Memória/sessão/histórico não são tocados.
  const regenerateCard = useCallback(async (cardId, newPrompt)=>{
    const t = getToken();
    if(!t){ navigate('/'); throw new Error('not authenticated'); }
    setMessages(prev=>prev.map(m=>{
      if(m.card_id!==cardId) return m;
      if(m.role==='user') return {...m, content:newPrompt};
      if(m.role==='assistant') return {...m, regenerating:true};
      return m;
    }));
    try {
      const {data} = await axios.post(`${API_BASE}/chat/regenerate-card`,
        {session_id:sessionIdRef.current, card_id:cardId, new_prompt:newPrompt},
        {headers:{Authorization:`Bearer ${t}`},withCredentials:true});
      setMessages(prev=>prev.map(m=>{
        if(m.card_id!==cardId || m.role!=='assistant') return m;
        return {
          ...m, regenerating:false, content:data.response||'',
          layout_type:data.layout_type||'simple_conversation',
          presentation_type:data.presentation_type||'conversa_comum',
          presentation_meta:data.presentation_meta||null,
          workspace_presentation:data.workspace_presentation||null,
          response_presentation:data.response_presentation||null,
          response_message_id:data.response_message_id||null,
          _regenVersion:(m._regenVersion||0)+1,
        };
      }));
    } catch(err) {
      setMessages(prev=>prev.map(m=>(m.card_id===cardId && m.role==='assistant') ? {...m, regenerating:false} : m));
      throw err;
    }
  },[navigate]);

  const toggleListening = useCallback(()=>{
    if(voiceStateRef.current==='processing'||voiceStateRef.current==='speaking') return;
    // Sem modo contínuo, o mesmo botão precisa concluir a captura manual e
    // enviar o texto — nunca abortar e descartar a frase do usuário.
    if(!continuousVoice) { toggleManual(); return; }
    if(continuousVoice && voiceStateRef.current==='listening') { finishActive(); return; }
    stopSpeech();
    savedInputRef.current = inputValueRef.current || '';
    if(continuousVoice) startActive();
    else startManual();
  },[continuousVoice, stopSpeech, toggleManual, finishActive, startActive]);

  const newConversation = useCallback(async sid=>{
    engineStop();stopSpeech();
    restoreGenRef.current++;
    sessionIdRef.current = sid||null;
    setSessionId(sid||null);
    if(sid) localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY,sid);
    else localStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
    sessionRestoringRef.current = false;
    setMessages([]);
  },[engineStop,stopSpeech]);

  const loadConversation = useCallback(async sid=>{
    // Only call API for real session IDs (length ≥ 10); skip demo IDs like 'p1', 'p4'
    if(!sid || sid.length < 10) return;
    const t=getToken();
    if(!t){ navigate('/'); return; }
    // [WorkspaceVoice] trocar de conversa cancela qualquer fala em andamento
    engineStop();stopSpeech('session_switch');
    try {
      const _gen=++restoreGenRef.current;
      const {data}=await axios.get(`${API_BASE}/memory/sessions/${sid}`,
        {headers:{Authorization:`Bearer ${t}`},withCredentials:true});
      if(_gen!==restoreGenRef.current) return;
      sessionIdRef.current = sid;
      setSessionId(sid);
      localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY,sid);
      sessionRestoringRef.current = false;
      const msgs=data?.messages||[];
      setMessages(msgs);
      lastActivityRef.current=Date.now();
      // C — Re-entry: contextual message when loading existing conversation
      if(msgs.length>=2){
        axios.post(`${API_BASE}/chat/reentry`,{session_id:sid},{headers:{Authorization:`Bearer ${t}`},withCredentials:true})
          .then(({data:rd})=>{
            if(_gen!==restoreGenRef.current || sessionIdRef.current!==sid) return;
            if(rd.message) setMessages(prev=>[...prev,{role:'assistant',content:rd.message}]);
          }).catch(()=>{});
      }
    } catch(err) {
      if(err?.response?.status===401) navigate('/');
    }
  },[navigate, engineStop, stopSpeech]);

  const newProject = useCallback(p=>{
    if(!p?.session_id) return;
    setProjects(prev=>[p,...prev.filter(existing=>existing.project_id!==p.project_id)]);
    setSessionList(prev=>[
      {session_id:p.session_id, preview:p.name||'Projeto', updatedAt:'agora'},
      ...prev.filter(existing=>existing.session_id!==p.session_id),
    ]);
    restoreGenRef.current++;
    sessionIdRef.current=p.session_id;
    setSessionId(p.session_id);
    localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY,p.session_id);
    sessionRestoringRef.current=false;
    setMessages([]);
  },[]);

  const saveMemory = useCallback(async content=>{
    const t=getToken();
    if(!t) return;
    try {
      await axios.post(`${API_BASE}/memory/save`,{content},
        {headers:{Authorization:`Bearer ${t}`},withCredentials:true});
    } catch {}
  },[]);

  // [WorkspaceVoice] Ação do botão: liga/desliga a fonte única de verdade do TTS.
  // Desligar interrompe imediatamente qualquer fala em andamento (não só a próxima).
  const toggleVoiceMode = useCallback(()=>{
    setVoiceMode(v=>{
      const next = !v;
      if(!next) stopSpeech('user_disabled');
      return next;
    });
  },[stopSpeech]);

  const toggleContinuousVoice = useCallback(()=>{
    const next = !continuousVoice;
    setEngineContMode(next);
    if(next){
      setVoiceMode(true); // garante TTS ativo no modo contínuo
      startActive();
    } else {
      engineStop();
    }
  },[continuousVoice, setEngineContMode, startActive, engineStop]);

  useEffect(()=>{ sendMessageRef.current = sendMessage; },[sendMessage]);
  // The root NativeAppShell receives the native event; Workspace registers the
  // same real chat/action pipeline so Quick Athena keeps working on this route.
  useEffect(()=>registerAthenaQuickOverlayMessageHandler(async ({ text })=>{
    const send=sendMessageRef.current;
    if(typeof send!=='function'){
      return {accepted:false,response:'Abra a Athena e tente novamente. A conversa ainda não está pronta.'};
    }
    const result=await send(text,[],false,{source:'floating_overlay'});
    if(result?.queued===true) return {accepted:false,queued:true,response:''};
    return result;
  }),[]);
  useEffect(()=>{ focusProfileRef.current = focusProfile; },[focusProfile]);

  // Clear panel + toasts when leaving foco_total
  useEffect(()=>{ if(cogMode!=='foco_total'){ setShowProfilePanel(false); setDriftSuggestion(null); setInsightSuggestion(null); } },[cogMode]);

  // Check cognitive drift when profile is active in foco_total
  useEffect(()=>{
    if(!focusProfile?.cognitive){ setDriftSuggestion(null); return; }
    const s = checkDriftSuggestion(focusProfile);
    if(!s) return;
    const shown = sessionStorage.getItem(`drift_${focusProfile.id}`);
    if(!shown) setDriftSuggestion(s);
  },[focusProfile]);

  // Track image opens for behavioral learning
  useEffect(()=>{
    const handler = () => {
      if(focusProfileRef.current?.id && focusProfileRef.current?.cognitive)
        trackImageOpened(focusProfileRef.current.id);
    };
    window.addEventListener('athena-image-opened', handler);
    return ()=>window.removeEventListener('athena-image-opened', handler);
  },[]);

  const goHome = useCallback(()=>{
    engineStop();stopSpeech();
    if(onNavigateHome) onNavigateHome(); else navigate('/');
  },[onNavigateHome,navigate,engineStop,stopSpeech]);

  const handleAcceptDrift = useCallback(()=>{
    if(!driftSuggestion||!focusProfile) return;
    const updated = applyDriftUpdate(focusProfile, driftSuggestion.trait, driftSuggestion.delta);
    try {
      const _all=JSON.parse(localStorage.getItem('athena_focus_profiles')||'[]');
      const _i=_all.findIndex(p=>p.id===updated.id);
      if(_i>=0){_all[_i]=updated;localStorage.setItem('athena_focus_profiles',JSON.stringify(_all));}
    } catch {}
    setFocusProfile(updated);setDriftSuggestion(null);
    if(focusProfile.id) sessionStorage.setItem(`drift_${focusProfile.id}`,'1');
  },[driftSuggestion,focusProfile]);

  const handleDismissDrift = useCallback(()=>{
    if(focusProfile?.id) sessionStorage.setItem(`drift_${focusProfile.id}`,'1');
    setDriftSuggestion(null);
  },[focusProfile]);

  const handleAcceptInsight = useCallback(()=>{
    if(!insightSuggestion||!focusProfile?.id) return;
    setPreferenceOverride(focusProfile.id, insightSuggestion.signal, true);
    dismissInsight(focusProfile.id, insightSuggestion.signal);
    setInsightSuggestion(null);
  },[insightSuggestion,focusProfile]);

  const handleDismissInsight = useCallback(()=>{
    if(focusProfile?.id && insightSuggestion) dismissInsight(focusProfile.id, insightSuggestion.signal);
    setInsightSuggestion(null);
  },[focusProfile,insightSuggestion]);

  // Show profile-selection landing when entering Foco Total without a profile
  if (cogMode === 'foco_total' && !focusProfile) {
    return (
      <FocusTotalLanding
        onSelectProfile={p => {
          setFocusProfile(p);
          const saved = loadMission();
          if (saved && saved.progress < 100) {
            setFocusMission(saved);
            setShowFocusResume(true);
          }
        }}
        onBack={() => setCogMode('athena2')}
      />
    );
  }

  const _isFocoActive = cogMode==='foco_total' && !!focusProfile;
  const _panelMob = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <LifeOSProvider cacheOwnerId={user?.id} surface="workspace" key={user?.id || 'workspace-anonymous'}>
      <>
      <ModoDiscreto
          user={user}
          messages={messages}
          onSend={sendMessage}
          onRegenerateCard={regenerateCard}
          onExit={goHome}
          voiceState={voiceState}
          onMicToggle={toggleListening}
          voiceMode={voiceMode}
          onToggleVoiceMode={toggleVoiceMode}
          continuousVoice={continuousVoice}
          onToggleContinuousVoice={toggleContinuousVoice}
          sessionList={sessionList}
          onNewConversation={newConversation}
          onLoadConversation={loadConversation}
          projects={projects}
          onNewProject={newProject}
          onSaveMemory={saveMemory}
          token={token}
          sessionId={sessionId}
          cogMode={cogMode}
          setCogMode={setCogMode}
          setInputRef={setInputRef}
          inputValueRef={inputValueRef}
          thinkingType={thinkingType}
          chatBgState={chatBgState}
          focusMission={focusMission}
          setFocusMission={setFocusMission}
          focusWin={focusWin}
          setFocusWin={setFocusWin}
          showFocusResume={showFocusResume}
          setShowFocusResume={setShowFocusResume}
          cognitiveProfile={focusProfile?.cognitive}
        />
      {_isFocoActive && (
        <>
          <CognitiveProfileButton
            onClick={()=>setShowProfilePanel(v=>!v)}
            accentColor={workspaceProfile?.accentColor}
            isMobile={_panelMob}
          />
          {showProfilePanel && (
            <CognitiveProfilePanel
              profile={focusProfile}
              workspaceProfile={workspaceProfile}
              onClose={()=>setShowProfilePanel(false)}
              isMobile={_panelMob}
            />
          )}
          <DriftSuggestionToast
            suggestion={driftSuggestion}
            accentColor={workspaceProfile?.accentColor}
            onAccept={handleAcceptDrift}
            onDismiss={handleDismissDrift}
          />
          <AdaptiveInsightToast
            insight={insightSuggestion}
            accentColor={workspaceProfile?.accentColor}
            onAccept={handleAcceptInsight}
            onDismiss={handleDismissInsight}
          />
        </>
      )}
      </>
    </LifeOSProvider>
  );
}
